import { defineMessages, FormattedMessage, useIntl, IntlProvider } from 'react-intl';
import isArray from 'lodash/isArray';
import React, { useState, useEffect, useRef, useMemo, useCallback, useReducer, useContext } from 'react';
import styled, { css, withTheme, keyframes, ThemeContext } from 'styled-components';
import defer from 'lodash/defer';
import debounce from 'lodash/debounce';
import delay from 'lodash/delay';
import isNumber from 'lodash/isNumber';
import { Button, IconButton, Toast, Dialog, Checkbox, TextField } from '@vitalsource/vstui';
import { cloud, green, red, frost, ninja, ocean } from '@vitalsource/vstui/colors';
import { XSmall, Checkmark, CaretLeftLarge, CaretRightLarge, QuestionCircle, ICircleSmall } from '@vitalsource/vstui-icons';
import range from 'lodash/range';
import shuffle from 'lodash/shuffle';
import startsWith from 'lodash/startsWith';
import { ExclamationCircle } from '@vitalsource/vstui/icons';
import get from 'lodash/get';
import HTMLReactParser from 'html-react-parser';
import isEqual from 'lodash/isEqual';
import { Popover, List } from '@vitalsource/vstui/labs';
import { v4 } from 'uuid';
import { createTheme, ThemeProvider } from '@vitalsource/vstui/themes';
import Tooltip from '@vitalsource/vstui/Tooltip';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var AssessmentCreateFlag;

(function (AssessmentCreateFlag) {
  AssessmentCreateFlag[AssessmentCreateFlag["AssessmentDontCreate"] = 0] = "AssessmentDontCreate";
  AssessmentCreateFlag[AssessmentCreateFlag["AssessmentCreateIfDoesNotExist"] = 1] = "AssessmentCreateIfDoesNotExist";
})(AssessmentCreateFlag || (AssessmentCreateFlag = {}));

var NOT_STARTED = -1; // answers can be true, false or undetermined (not evaluated or answered yet)

var undetermined = null;
var unanswered = null;
var QuestionStatus;

(function (QuestionStatus) {
  QuestionStatus["Incomplete"] = "Incomplete";
  QuestionStatus["Complete"] = "Complete";
  QuestionStatus["Correct"] = "Correct";
  QuestionStatus["Incorrect"] = "Incorrect";
})(QuestionStatus || (QuestionStatus = {}));

var AssessmentType$1;

(function (AssessmentType) {
  AssessmentType["Formative"] = "Formative";
  AssessmentType["Summative"] = "Summative";
})(AssessmentType$1 || (AssessmentType$1 = {}));

var QuestionMode;

(function (QuestionMode) {
  /** question is standalone, i.e has no outer container **/
  QuestionMode["Standalone"] = "Standalone";
  /** question operates standalone, but is within an outer group like group of practice questions **/

  QuestionMode["StandaloneInGroup"] = "StandaloneInGroup";
  /** question is first question in a tutorial **/

  QuestionMode["Tutorial"] = "Tutorial";
  /** question is a child of a tutorial **/

  QuestionMode["TutorialFollowup"] = "TutorialFollowup";
  /** question forms part of a combined whole, e.g. assessment */

  /** primarily, it has no integrated submission controls of its own  */

  QuestionMode["Child"] = "Child";
})(QuestionMode || (QuestionMode = {}));

var QuestionBorder;

(function (QuestionBorder) {
  /** question has no border, e.g it sits in some kind of outer container **/
  QuestionBorder["None"] = "None";
  /** question has a shadow border, e.g. when totally standalone **/

  QuestionBorder["HaloShadow"] = "HaloShadow";
})(QuestionBorder || (QuestionBorder = {}));

var QuestionImprovementFlag;

(function (QuestionImprovementFlag) {
  QuestionImprovementFlag[QuestionImprovementFlag["FlaggedBad"] = 0] = "FlaggedBad";
  QuestionImprovementFlag[QuestionImprovementFlag["FlaggedGood"] = 1] = "FlaggedGood";
})(QuestionImprovementFlag || (QuestionImprovementFlag = {}));

var QuestionImprovementIssueType;

(function (QuestionImprovementIssueType) {
  QuestionImprovementIssueType["CorrectAnswerWrong"] = "CorrectAnswerWrong";
  QuestionImprovementIssueType["UserAnswerAlsoCorrect"] = "UserAnswerAlsoCorrect";
  QuestionImprovementIssueType["QuestionUnclear"] = "QuestionUnclear";
  QuestionImprovementIssueType["QuestionNotRelevant"] = "QuestionNotRelevant";
})(QuestionImprovementIssueType || (QuestionImprovementIssueType = {}));

var QuestionSystemErrorType;

(function (QuestionSystemErrorType) {
  QuestionSystemErrorType["EvaluationError"] = "EvaluationError";
  QuestionSystemErrorType["RevealAnswerError"] = "RevealAnswerError";
  QuestionSystemErrorType["ImprovementFeedbackError"] = "ImprovementFeedbackError";
})(QuestionSystemErrorType || (QuestionSystemErrorType = {}));

var QuestionLockedReason;

(function (QuestionLockedReason) {
  QuestionLockedReason["Waiting"] = "Waiting";
})(QuestionLockedReason || (QuestionLockedReason = {}));

var getDefaultQuestionState = function () {
  return {
    id: "",
    started: true,
    loaded: false,
    submitRequested: false,
    numSubmitRequestedAttempts: 0,
    submitActive: false,
    completed: false,
    submitted: false,
    answerCorrect: undefined,
    revealAnswer: false,
    answerRevealed: false,
    startTime: NOT_STARTED,
    numAttempts: 0,
    validationErrorMsg: undefined,
    validationErrorCode: undefined,
    feedback: undefined,
    systemFeedback: undefined,
    locked: false,
    improvementStatus: undefined
  };
};

function loadScript(_a) {
  var src = _a.src,
      onLoad = _a.onLoad,
      onError = _a.onError;
  var script = document.createElement("script");
  script.src = src;
  script.type = "text/javascript";
  script.onload = onLoad;
  script.onerror = onError;
  var head = document.getElementsByTagName("head")[0];
  head.appendChild(script);
}
function loadScriptPromise(src) {
  return new Promise(function (resolve, reject) {
    var script = document.querySelector("script[src=\"" + src + "\"]");

    if (!script) {
      loadScript({
        src: src,
        onLoad: function () {
          return resolve();
        },
        onError: function () {
          return reject();
        }
      });
    } else {
      resolve();
    }
  });
}

/* eslint-disable */

var ConnectionStatus;

(function (ConnectionStatus) {
  ConnectionStatus[ConnectionStatus["Pending"] = 0] = "Pending";
  ConnectionStatus[ConnectionStatus["Open"] = 1] = "Open";
  ConnectionStatus[ConnectionStatus["Closed"] = 2] = "Closed";
})(ConnectionStatus || (ConnectionStatus = {}));

var TightropeInitializer =
/** @class */
function () {
  function TightropeInitializer() {
    this.connections = {};
  }

  TightropeInitializer.inject = function (url) {
    return loadScriptPromise(url);
  };

  Object.defineProperty(TightropeInitializer.prototype, "client", {
    get: function () {
      return this._client;
    },
    enumerable: false,
    configurable: true
  });

  TightropeInitializer.prototype.init = function (args) {
    var _this = this;

    var clientHostProtocol = "https";
    if (args.clientProtocol) clientHostProtocol = args.clientProtocol;
    var clientHostUrl = new URL(clientHostProtocol + "://" + args.clientHost);
    return TightropeInitializer.inject(clientHostUrl.protocol + "//" + clientHostUrl.host + "/static/assessment/tightrope.js").then(function () {
      var _a, _b, _c;

      if (_this._client && _this.currentConnection && _this.currentConnection.status === ConnectionStatus.Open) {
        try {
          _this._client.disconnect();
        } catch (e) {
          console.error("Unable to close tightrope connection");
        }
      }

      var initOptions = {
        vbid: args.vbid,
        tenantId: args.tenantId
      };

      if (args.serverHost) {
        // Use the default protocol if not specified
        var serverProtocol = args.serverProtocol;
        if (!serverProtocol) serverProtocol = clientHostProtocol;
        var socketProtocol = "ws";

        if (serverProtocol === "https") {
          socketProtocol += "s";
        }

        var serverHostUrl = new URL(serverProtocol + "://" + args.serverHost); // Used for debugging by overriding Tightrope client defaults, not for production

        initOptions = Object.assign({}, initOptions, {
          authUrl: serverHostUrl.protocol + "//" + serverHostUrl.host + "/common/v1/api/auth/token",
          wsUrl: socketProtocol + "://" + serverHostUrl.host + "/assessment/v1/ws"
        });
      } // Save the connection args for later


      _this.currentConnection = {
        status: ConnectionStatus.Pending,
        args: args
      };
      _this.connections[_this.argsToConnectionKey(args)] = _this.currentConnection; // @ts-ignore

      _this._client = new TightropeClient(args.sessionId, initOptions);
      (_a = _this._client) === null || _a === void 0 ? void 0 : _a.on("open", function (data) {
        _this.currentConnection.status = ConnectionStatus.Open;
      });
      (_b = _this._client) === null || _b === void 0 ? void 0 : _b.on("close", function (data) {
        _this.currentConnection.status = ConnectionStatus.Closed;
      });
      return (_c = _this._client) === null || _c === void 0 ? void 0 : _c.connect().then(function () {
        var _a;

        (_a = _this._client) === null || _a === void 0 ? void 0 : _a.log("connected!");
        return Promise.resolve();
      }).catch(function (e) {
        console.error(e);
        return Promise.reject(e);
      });
    });
  }; // Wait for a client API method to become available


  TightropeInitializer.prototype.waitForInit = function (clientMethod) {
    var _this = this;

    var args = [];

    for (var _i = 1; _i < arguments.length; _i++) {
      args[_i - 1] = arguments[_i];
    }

    if (this.currentConnection) {
      var status_1 = this.currentConnection.status;

      switch (status_1) {
        case ConnectionStatus.Pending:
          return new Promise(function (resolve, reject) {
            setTimeout(function () {
              _this.waitForInit.apply(_this, __spreadArray([clientMethod], args, false)).then(resolve).catch(reject);
            }, 500);
          });

        case ConnectionStatus.Open:
          // @ts-ignore
          return this[clientMethod](args);

        default:
          return Promise.reject(new Error("Tightrope service connection closed"));
      }
    } else {
      return Promise.reject(new Error("No initialized connections found for Tightrope service"));
    }
  };

  TightropeInitializer.prototype.hasClient = function () {
    return !!this._client;
  };

  TightropeInitializer.prototype.argsToConnectionKey = function (args) {
    return this.stringToHashcode("" + args.vbid);
  };

  TightropeInitializer.prototype.stringToHashcode = function (input) {
    var hash = 0;

    if (input.length == 0) {
      return hash;
    }

    for (var i = 0; i < input.length; i++) {
      var char = input.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return hash;
  };

  return TightropeInitializer;
}();

var initializer = new TightropeInitializer();
var connect = function (args) {
  return initializer.init(args);
}; // Return a promise and try to connect, even if the service isn't yet ready

var handler = {
  //@ts-ignore
  //@ts-ignore
  get: function (target, prop, receiver) {
    //@ts-ignore
    if (initializer.hasClient()) {
      // @ts-ignore
      return initializer.client[prop];
    } else {
      return initializer.waitForInit.bind(initializer, prop);
    }
  }
};
var Client = new Proxy({}, handler);

// c.f. https://effectivetypescript.com/2020/06/16/typed-identity-functions/

var withValueTypesFrom = function () {
  return function (x) {
    return x;
  };
}; // todo if there are a lot of messages break them out into functional directories when needed


var messagesBase = defineMessages(withValueTypesFrom()({
  ok: {
    id: "coachme.base.ok"
  },
  cancel: {
    id: "coachme.base.cancel"
  },
  continue: {
    id: "coachme.base.continue"
  },
  submit: {
    id: "coachme.base.submit"
  },
  loading: {
    id: "coachme.base.loading"
  },
  close: {
    id: "coachme.base.close"
  },
  back: {
    id: "coachme.base.back"
  },
  moreInfo: {
    id: "coachme.base.moreInfo"
  },
  taglinePoweredBy: {
    id: "coachme.base.taglinePoweredBy"
  },
  evaluating: {
    id: "coachme.base.evaluating"
  },
  questions: {
    id: "coachme.base.questions"
  },
  coachme: {
    id: "coachme.base.coachme"
  }
}));
var messagesQuestion = defineMessages(withValueTypesFrom()({
  status: {
    id: "coachme.question.status"
  },
  pip: {
    id: "coachme.question.pip"
  },
  typeMultipleChoice: {
    id: "coachme.question.type.multipleChoice"
  },
  typeGapFill: {
    id: "coachme.question.type.gapFill"
  },
  typeSubmitCompare: {
    id: "coachme.question.type.submitCompare"
  },
  notificationAttention: {
    id: "coachme.question.notification.attention"
  },
  feedbackAnswerCorrect: {
    id: "coachme.question.feedback.answerCorrect"
  },
  feedbackAnswerIncorrect: {
    id: "coachme.question.feedback.answerIncorrect"
  },
  feedbackSubmissionThanks: {
    id: "coachme.question.feedback.submissionThanks"
  },
  feedbackWrongAnswersSpecific: {
    id: "coachme.question.feedback.wrongAnswersSpecific"
  },
  feedbackWrongAnswersGeneric: {
    id: "coachme.question.feedback.wrongAnswersGeneric"
  },
  feedbackWrongAnswerPrompt: {
    id: "coachme.question.feedback.wrongAnswersPrompt"
  },
  feedbackWrongAnswerPromptNotIndicated: {
    id: "coachme.question.feedback.wrongAnswersPromptNotIndicated"
  },
  feedbackItemCorrectAnswer: {
    id: "coachme.question.feedback.itemCorrectAnswer"
  },
  feedbackItemIncorrectAnswer: {
    id: "coachme.question.feedback.itemIncorrectAnswer"
  },
  feedbackItemRevealedCorrectAnswer: {
    id: "coachme.question.feedback.itemRevealedCorrectAnswer"
  },
  feedbackCorrectAnswerIs: {
    id: "coachme.question.feedback.correctAnswerIs"
  },
  feedbackRevealAnswerAnnouncement: {
    id: "coachme.question.feedback.revealAnnouncement"
  },
  feedbackResetAnnouncement: {
    id: "coachme.question.feedback.resetAnnouncement"
  },
  feedbackTightropeResponse: {
    id: "coachme.question.feedback.tightropeResponse"
  },
  controlsCheckAnswer: {
    id: "coachme.question.controls.checkAnswer"
  },
  controlsRevealAnswer: {
    id: "coachme.question.controls.revealAnswer"
  },
  controlsSubmit: {
    id: "coachme.question.controls.submit"
  },
  controlsNextQuestion: {
    id: "coachme.question.controls.nextQuestion"
  },
  controlsDone: {
    id: "coachme.question.controls.done"
  },
  controlsRetry: {
    id: "coachme.question.controls.retry"
  },
  controlsTypedPlaceHolder: {
    id: "coachme.question.controls.typedPlaceHolder"
  },
  controlsSelectPlaceHolder: {
    id: "coachme.question.controls.selectPlaceHolder"
  },
  validationPleaseSelectAnswer: {
    id: "coachme.question.validation.pleaseSelectAnswer"
  },
  validationTypeAnAnswer: {
    id: "coachme.question.validation.typeAnAnswer"
  },
  statusComplete: {
    id: "coachme.question.status.complete"
  },
  statusIncomplete: {
    id: "coachme.question.status.incomplete"
  },
  statusCorrect: {
    id: "coachme.question.status.correct"
  },
  statusIncorrect: {
    id: "coachme.question.status.incorrect"
  },
  suggestionsPrompt: {
    id: "coachme.question.suggestions.prompt"
  },
  suggestionsClose: {
    id: "coachme.question.suggestions.close"
  }
}));
var messagesSubmitCompare = defineMessages(withValueTypesFrom()({
  inputFieldLabel: {
    id: "coachme.submitCompare.inputFieldLabel"
  },
  answerSaved: {
    id: "coachme.submitCompare.answerSaved"
  },
  compareWithExpert: {
    id: "coachme.submitCompare.compareWithExpert"
  },
  selfAssessmentAnnouncement: {
    id: "coachme.submitCompare.selfAssessmentAnnouncement"
  }
}));
var messagesGapFill = defineMessages(withValueTypesFrom()({
  gapNumberLabel: {
    id: "coachme.gapFill.gapNumberLabel"
  },
  fillInTheBlankPrompt: {
    id: "coachme.gapFill.fillInTheBlankPrompt"
  },
  validationNotFilledIn: {
    id: "coachme.gapFill.validation.notFilledIn"
  },
  validationSameAnswerRepeated: {
    id: "coachme.gapFill.validation.sameAnswerRepeated"
  },
  wordBankHeaderLabel: {
    id: "coachme.gapFill.wordBank.headerLabel"
  },
  wordBankWordSpent: {
    id: "coachme.gapFill.wordBank.wordSpent"
  },
  wordBankFooterLabel: {
    id: "coachme.gapFill.wordBank.footerLabel"
  }
}));
var messagesWidgets = defineMessages(withValueTypesFrom()({
  addToFlashcard: {
    id: "coachme.widgets.addToFlashcard"
  },
  createANote: {
    id: "coachme.widgets.createANote"
  },
  noteCreated: {
    id: "coachme.widgets.noteCreated"
  },
  createAFlashcard: {
    id: "coachme.widgets.createAFlashcard"
  },
  flashcardCreated: {
    id: "coachme.widgets.flashcardCreated"
  },
  reviewFlashcard: {
    id: "coachme.widgets.reviewFlashcard"
  }
}));
var messagesQuestionGroup = defineMessages(withValueTypesFrom()({
  previousQuestion: {
    id: "coachme.questionGroup.previousQuestion"
  },
  nextQuestion: {
    id: "coachme.questionGroup.nextQuestion"
  },
  assessmentStart: {
    id: "coachme.questionGroup.assessmentStart"
  },
  assessmentResetA11y: {
    id: "coachme.questionGroup.assessmentResetA11y"
  },
  assessmentAnswersRevealedA11y: {
    id: "coachme.questionGroup.assessmentAnswersRevealedA11y"
  },
  checkAllAnswers: {
    id: "coachme.questionGroup.checkAllAnswers"
  },
  retryQuestions: {
    id: "coachme.questionGroup.retryQuestions"
  },
  revealAnswers: {
    id: "coachme.questionGroup.revealAnswers"
  }
}));
var messagesAssessment = defineMessages(withValueTypesFrom()({
  formativeTitle: {
    id: "coachme.assessment.formativeTitle"
  },
  formativeA11yTitle: {
    id: "coachme.assessment.formativeA11yTitle"
  },
  summativeTitle: {
    id: "coachme.assessment.summativeTitle"
  },
  summativeA11yTitle: {
    id: "coachme.assessment.summativeA11yTitle"
  },
  questionCount: {
    id: "coachme.assessment.questionCount"
  },
  scoreCorrectPercent: {
    id: "coachme.assessment.scoreCorrectPercent"
  },
  scoreCorrectCount: {
    id: "coachme.assessment.scoreCorrectCount"
  },
  scoreIncorrectCount: {
    id: "coachme.assessment.scoreIncorrectCount"
  },
  scoreIncompleteCount: {
    id: "coachme.assessment.scoreIncompleteCount"
  },
  resultsTitle: {
    id: "coachme.assessment.resultsTitle"
  },
  resultsA11yLineItem: {
    id: "coachme.assessment.resultsA11yLineItem"
  },
  resultsA11y: {
    id: "coachme.assessment.resultsA11y"
  },
  resultsFocusA11y: {
    id: "coachme.assessment.resultsFocusA11y"
  }
}));
var messagesQuestionImprovement = defineMessages(withValueTypesFrom()({
  prompt: {
    id: "coachme.questionImprovement.prompt"
  },
  thumbsUp: {
    id: "coachme.questionImprovement.thumbsUp"
  },
  thumbsDown: {
    id: "coachme.questionImprovement.thumbsDown"
  }
}));
var messagesQuestionImprovementDialog = defineMessages(withValueTypesFrom()({
  title: {
    id: "coachme.questionImprovement.dialog.title"
  },
  information: {
    id: "coachme.questionImprovement.dialog.information"
  },
  issueWrongAnswer: {
    id: "coachme.questionImprovement.dialog.issue.wrongAnswer"
  },
  issueMineAlsoCorrect: {
    id: "coachme.questionImprovement.dialog.issue.mineAlsoCorrect"
  },
  issueQuestionUnclear: {
    id: "coachme.questionImprovement.dialog.issue.questionUnclear"
  },
  issueQuestionNotRelevant: {
    id: "coachme.questionImprovement.dialog.issue.questionNotRelevant"
  },
  otherFeedback: {
    id: "coachme.questionImprovement.dialog.otherFeedback"
  },
  feedbackPlaceholder: {
    id: "coachme.questionImprovement.dialog.feedbackPlaceholder"
  }
}));
var messagesAssessmentConfirmIncompleteDialog = defineMessages(withValueTypesFrom()({
  title: {
    id: "coachme.assessment.confirmIncompleteDialog.title"
  },
  message: {
    id: "coachme.assessment.confirmIncompleteDialog.message"
  }
}));
var messagesErrors = defineMessages(withValueTypesFrom()({
  evaluateErrorTitle: {
    id: "coachme.errors.evaluateErrorTitle"
  },
  evaluateErrorDescription: {
    id: "coachme.errors.evaluateErrorDescription"
  },
  revealAnswerErrorTitle: {
    id: "coachme.errors.revealAnswerErrorTitle"
  },
  revealAnswerErrorDescription: {
    id: "coachme.errors.revealAnswerErrorDescription"
  },
  submitAssessmentErrorTitle: {
    id: "coachme.errors.submitAssessmentErrorTitle"
  },
  submitAssessmentErrorDescription: {
    id: "coachme.errors.submitAssessmentErrorDescription"
  },
  improvementFeedbackErrorTitle: {
    id: "coachme.errors.improvementFeedbackErrorTitle"
  },
  improvementFeedbackErrorDescription: {
    id: "coachme.errors.improvementFeedbackErrorDescription"
  },
  enrichmentLoadErrorTitle: {
    id: "coachme.errors.enrichmentLoadErrorTitle"
  },
  enrichmentLoadErrorDescription: {
    id: "coachme.errors.enrichmentLoadErrorDescription"
  },
  progressLoadErrorTitle: {
    id: "coachme.errors.progressLoadErrorTitle"
  },
  progressLoadErrorDescription: {
    id: "coachme.errors.progressLoadErrorDescription"
  }
}));
var messagesProgress = defineMessages(withValueTypesFrom()({
  title: {
    id: "coachme.progress.title"
  },
  countComplete: {
    id: "coachme.progress.countComplete"
  },
  countIncomplete: {
    id: "coachme.progress.countIncomplete"
  },
  indicatorA11y: {
    id: "coachme.progress.indicatorA11y"
  },
  countBreakdownA11y: {
    id: "coachme.progress.countBreakdownA11y"
  },
  questionStatusA11y: {
    id: "coachme.progress.questionStatusA11y"
  },
  detailListItemA11y: {
    id: "coachme.progress.detailListItemA11y"
  },
  backToAll: {
    id: "coachme.progress.backToAll"
  }
}));

var TightropeEvaluatorService =
/** @class */
function () {
  function TightropeEvaluatorService() {
    this.getSocket = function () {
      return Client;
    };
  }

  TightropeEvaluatorService.prototype.init = function (args) {
    return connect(args);
  };

  TightropeEvaluatorService.prototype.evaluateResponse = function (enrichmentId, questionId, userAnswer, tutorialId, tutorialStep, options) {
    return Client.evaluateResponse(__assign(__assign(__assign({
      enrichment_id: enrichmentId,
      question_id: questionId,
      student_answers: userAnswer
    }, tutorialId && {
      tutorial_id: tutorialId
    }), tutorialStep && {
      step_id: tutorialStep
    }), (options === null || options === void 0 ? void 0 : options.skipSuggestions) !== undefined && {
      skip_suggestions: options.skipSuggestions
    })).then(function (response) {
      // return response.result;
      return __assign(__assign({}, response.result), {
        feedback: {
          descriptor: messagesQuestion.feedbackTightropeResponse,
          values: {
            feedback: response.result.feedback
          }
        }
      });
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.revealAnswer = function (enrichmentId, questionId) {
    return Client.revealAnswer({
      enrichment_id: enrichmentId,
      question_id: questionId
    }).then(function (response) {
      return {
        correct_answers: response.result.correct_answer
      };
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.restoreEnrichment = function (enrichmentId, type, createFlag) {
    return Client.restoreAssessment(__assign({
      enrichment_id: enrichmentId
    }, createFlag === AssessmentCreateFlag.AssessmentCreateIfDoesNotExist && {
      create_if_does_not_exist: true
    })).then(function (response) {
      var questions = response.result.questions;
      var restored = Object.keys(questions).reduce(function (result, questionId) {
        var question = questions[questionId];
        result.questions[questionId] = __assign(__assign({}, question), {
          feedback: {
            descriptor: messagesQuestion.feedbackTightropeResponse,
            values: {
              feedback: question.feedback
            }
          },

          /**
           * backend sends back flagged or not flagged but ui operates on a QuestionImprovementStatus
           * object. As the actual content of the flagging is not re-shown an empty object will
           * suffice as that will mean the improvement feedback control will not show
           * */
          improvementStatus: question.flagged_question ? {} : undefined
        });
        return result;
      }, {
        questions: {}
      });
      restored.assessment = response.result.assessment;
      return restored;
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.startAssessment = function (enrichmentId) {
    return Client.startAssessment({
      enrichment_id: enrichmentId
    }).then(function () {
      return Promise.resolve();
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.submitAssessment = function (enrichmentId, userAnswers) {
    return Promise.all(Object.keys(userAnswers).map(function (questionId) {
      // note tightrope doesn't like empty answers only send ones that are filled in
      return Object.keys(userAnswers[questionId]).length ? Client.storeAnswers({
        enrichment_id: enrichmentId,
        question_id: questionId,
        student_answers: userAnswers[questionId]
      }) : Promise.resolve();
    })).then(function () {
      return Client.submitAssessment({
        enrichment_id: enrichmentId
      });
    }).then(function (response) {
      return __assign(__assign({}, response.result), {
        questions: Object.keys(userAnswers).reduce(function (acc, questionId) {
          // if a question isn't answered the backend does not return it in the response
          // so we need to fill in those unanswered questions for uniformity
          if (!Object.keys(userAnswers[questionId]).length) {
            acc[questionId] = {
              correct: false,
              feedback: null,
              input_correctness: {},
              first_attempt_correct: false
            };
          } else {
            acc[questionId] = response.result.questions[questionId];
            acc[questionId].feedback = {
              descriptor: messagesQuestion.feedbackTightropeResponse,
              values: {
                feedback: acc[questionId].feedback
              }
            };
          }

          return acc;
        }, {})
      });
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.storeAnswers = function (enrichmentId, questionId, userAnswer) {
    return Client.storeAnswers({
      enrichment_id: enrichmentId,
      question_id: questionId,
      student_answers: userAnswer
    }).then(function () {
      return Promise.resolve();
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.saveParameters = function (enrichmentId, questionId, parameters) {
    return Client.saveParameters({
      enrichment_id: enrichmentId,
      question_id: questionId,
      parameters: parameters
    }).then(function () {
      return Promise.resolve();
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.submitQuestionImprovementStatus = function (enrichmentId, questionId, status, tutorialId, tutorialStep) {
    var _a, _b, _c, _d;

    return Client.flagQuestion(__assign(__assign(__assign({
      enrichment_id: enrichmentId,
      question_id: questionId,
      rating: status.flag === QuestionImprovementFlag.FlaggedBad ? "thumbs_down" : "thumbs_up"
    }, tutorialId && {
      tutorial_id: tutorialId
    }), tutorialStep && {
      step_id: tutorialStep
    }), {
      comments: (_b = (_a = status.feedback) === null || _a === void 0 ? void 0 : _a.otherFeedback) !== null && _b !== void 0 ? _b : null,
      reasons: (_d = (_c = status.feedback) === null || _c === void 0 ? void 0 : _c.issues) !== null && _d !== void 0 ? _d : null
    })).then(function () {
      return Promise.resolve();
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.practiceCompleted = function (bookLocation) {
    // todo using low level here as tightrope client was not working
    // return TightropeClient.practiceCompleted({ book_location: bookLocation })
    return Client.send(__assign({}, bookLocation && {
      book_location: bookLocation
    }), {
      action: "practice_completed"
    }).then(function (response) {
      return response.result.practice_completed.map(function (item, index) {
        var _a;

        return {
          id: index.toString(),
          detailId: item.book_location,
          title: item.book_location,
          completionTotal: item.total_questions,
          completionCount: item.questions_attempted,
          percentComplete: (_a = item.percent_score) !== null && _a !== void 0 ? _a : 0,
          firstCfiLocation: item.first_cfi_location
        };
      });
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.questionStatus = function (scope) {
    // todo using low level here as tightrope client was not working
    // return TightropeClient.questionStatus({ book_location: bookLocation })
    return Client.send(__assign(__assign({}, isArray(scope) && {
      enrichment_ids: scope
    }), !isArray(scope) && {
      book_location: scope
    }), {
      action: "question_status"
    }).then(function (response) {
      var activities = response.result.status.map(function (item) {
        return {
          id: item.enrichment_id,
          cfi: item.cfi_location,
          page: "",
          questions: Object.values(item.question_status).sort(function (a, b) {
            return a.ordinal - b.ordinal;
          }).map(function (question) {
            return {
              id: question.component_id,
              status: TightropeEvaluatorService.convertQuestionStatus(question.status)
            };
          })
        };
      });
      return {
        activities: activities
      };
    }).catch(function (e) {
      return Promise.reject(TightropeEvaluatorService.parseError(e));
    });
  };

  TightropeEvaluatorService.prototype.getEnrichmentService = function (enrichmentId) {
    return {
      revealAnswer: this.revealAnswer.bind(this, enrichmentId),
      evaluateResponse: this.evaluateResponse.bind(this, enrichmentId),
      startAssessment: this.startAssessment.bind(this, enrichmentId),
      submitAssessment: this.submitAssessment.bind(this, enrichmentId),
      storeAnswers: this.storeAnswers.bind(this, enrichmentId),
      saveParameters: this.saveParameters.bind(this, enrichmentId),
      submitQuestionImprovementStatus: this.submitQuestionImprovementStatus.bind(this, enrichmentId)
    };
  };

  TightropeEvaluatorService.parseError = function (e) {
    if (e instanceof Error) {
      return e;
    } else if ("errors" in e) {
      return new Error(TightropeEvaluatorService.findNestedErrorStrings(e.errors)[0]);
    } else if (e.message) {
      return new Error(e.message);
    } else {
      return new Error("Unknown assessment error: " + e);
    }
  }; // todo work out type of errors
  // eslint-disable-next-line @typescript-eslint/no-explicit-any


  TightropeEvaluatorService.findNestedErrorStrings = function (errors, results) {
    if (results === void 0) {
      results = [];
    } // sometimes error messages are nested in "non_field_errors" etc
    // this makes the error readable


    var errorKey = "error";
    Object.keys(errors).forEach(function (key) {
      var value = errors[key];

      if (key.indexOf(errorKey) !== -1 && typeof value === "string") {
        results.push(value);
      } else if (typeof value === "object") {
        TightropeEvaluatorService.findNestedErrorStrings(value, results);
      }
    });
    return results;
  };

  TightropeEvaluatorService.convertQuestionStatus = function (status) {
    if (status === "correct") {
      return QuestionStatus.Correct;
    } else if (status === "incorrect") {
      return QuestionStatus.Incorrect;
    } else if (status === "complete") {
      return QuestionStatus.Complete;
    } else {
      return QuestionStatus.Incomplete;
    }
  };

  return TightropeEvaluatorService;
}();

var AssessmentInstance = new TightropeEvaluatorService();

function createCommonjsModule(fn) {
  var module = { exports: {} };
	return fn(module, module.exports), module.exports;
}

// Copyright Joyent, Inc. and other Node contributors.

// If obj.hasOwnProperty has been overridden, then calling
// obj.hasOwnProperty(prop) will break.
// See: https://github.com/joyent/node/issues/1707
function hasOwnProperty(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

var decode = function(qs, sep, eq, options) {
  sep = sep || '&';
  eq = eq || '=';
  var obj = {};

  if (typeof qs !== 'string' || qs.length === 0) {
    return obj;
  }

  var regexp = /\+/g;
  qs = qs.split(sep);

  var maxKeys = 1000;
  if (options && typeof options.maxKeys === 'number') {
    maxKeys = options.maxKeys;
  }

  var len = qs.length;
  // maxKeys <= 0 means that we should not limit keys count
  if (maxKeys > 0 && len > maxKeys) {
    len = maxKeys;
  }

  for (var i = 0; i < len; ++i) {
    var x = qs[i].replace(regexp, '%20'),
        idx = x.indexOf(eq),
        kstr, vstr, k, v;

    if (idx >= 0) {
      kstr = x.substr(0, idx);
      vstr = x.substr(idx + 1);
    } else {
      kstr = x;
      vstr = '';
    }

    k = decodeURIComponent(kstr);
    v = decodeURIComponent(vstr);

    if (!hasOwnProperty(obj, k)) {
      obj[k] = v;
    } else if (Array.isArray(obj[k])) {
      obj[k].push(v);
    } else {
      obj[k] = [obj[k], v];
    }
  }

  return obj;
};

// Copyright Joyent, Inc. and other Node contributors.

var stringifyPrimitive = function(v) {
  switch (typeof v) {
    case 'string':
      return v;

    case 'boolean':
      return v ? 'true' : 'false';

    case 'number':
      return isFinite(v) ? v : '';

    default:
      return '';
  }
};

var encode = function(obj, sep, eq, name) {
  sep = sep || '&';
  eq = eq || '=';
  if (obj === null) {
    obj = undefined;
  }

  if (typeof obj === 'object') {
    return Object.keys(obj).map(function(k) {
      var ks = encodeURIComponent(stringifyPrimitive(k)) + eq;
      if (Array.isArray(obj[k])) {
        return obj[k].map(function(v) {
          return ks + encodeURIComponent(stringifyPrimitive(v));
        }).join(sep);
      } else {
        return ks + encodeURIComponent(stringifyPrimitive(obj[k]));
      }
    }).join(sep);

  }

  if (!name) return '';
  return encodeURIComponent(stringifyPrimitive(name)) + eq +
         encodeURIComponent(stringifyPrimitive(obj));
};

var querystring = createCommonjsModule(function (module, exports) {

exports.decode = exports.parse = decode;
exports.encode = exports.stringify = encode;
});

/* eslint-disable */

var DEFAULT_PROTOCOL = "https";
var DEFAULT_HOST = "jigsaw.vitalsource.com";

var apiUrl = function (host) {
  return host + "/smartauthor/enrichments_service";
};

var manifestsUrl = function (host) {
  return apiUrl(host) + "/manifests";
};

var enrichmentsUrl = function (host) {
  return apiUrl(host) + "/enrichments";
};

var EnrichmentsService =
/** @class */
function () {
  function EnrichmentsService() {
    this.serviceHost = DEFAULT_PROTOCOL + "://" + DEFAULT_HOST;
  }

  EnrichmentsService.prototype.init = function (args) {
    var serviceProtocol = DEFAULT_PROTOCOL;

    if (args.protocol) {
      serviceProtocol = args.protocol;
    }

    var serviceHostUrl = new URL(serviceProtocol + "://" + args.host);
    this.serviceHost = serviceHostUrl.protocol + "//" + serviceHostUrl.host;
    return Promise.resolve();
  };

  EnrichmentsService.prototype.getManifest = function (vbid) {
    return this.getJson(this.manifestUrl(vbid), {});
  };

  EnrichmentsService.prototype.getEnrichment = function (id) {
    return this.getJson(this.enrichmentUrl(id), {}).then(function (body) {
      return EnrichmentsService.transformJSONAPI(body);
    });
  };

  EnrichmentsService.prototype.manifestUrl = function (vbid) {
    return manifestsUrl(this.serviceHost) + "/" + vbid;
  };

  EnrichmentsService.prototype.enrichmentUrl = function (id) {
    return enrichmentsUrl(this.serviceHost) + "/" + id;
  };

  EnrichmentsService.prototype.getJson = function (route, params) {
    if (params) {
      route = this.applyParamsToUrl(route, params);
    }

    return fetch(route, {
      credentials: "include"
    }).then(this.handleResponse);
  };

  EnrichmentsService.prototype.handleResponse = function (res) {
    return res.json().then(function (body) {
      if (!res.ok) {
        var error = Object.assign({}, body, {
          status: res.status,
          statusText: res.statusText
        });
        return Promise.reject(error);
      } else if (body.errors) {
        return Promise.reject({
          status: body.errors[0].code,
          statusText: body.errors[0].detail || body.errors[0].title
        });
      }

      return body;
    });
  };

  EnrichmentsService.prototype.applyParamsToUrl = function (route, params) {
    if (!params || !Object.keys(params).length) {
      return route;
    } else {
      var qParams = querystring.stringify(params);
      return route.concat("?" + qParams);
    }
  };

  EnrichmentsService.processItem = function (item, included) {
    return __assign(__assign({}, item), {
      relationships: this.processRelationships(item.relationships ? item.relationships : {}, included)
    });
  };

  EnrichmentsService.processRelationships = function (rels, included) {
    var _this = this;

    return Object.entries(rels).reduce(function (result, data) {
      var relName = data[0],
          rel = data[1];

      if (rel && rel.data) {
        var data_1 = rel.data; // Check if data is array or obj

        if (Array.isArray(data_1)) {
          var foundRelations = data_1.reduce(function (result, rel) {
            var foundRelation = included.find(function (i) {
              return i.id === rel.id;
            });

            if (foundRelation) {
              result.push(_this.processItem(foundRelation, included));
            }

            return result;
          }, []);

          if (foundRelations.length > 0) {
            result[relName] = {
              data: foundRelations
            };
          }
        } else {
          var foundRelation = included.find(function (i) {
            return i.id === data_1.id;
          });

          if (foundRelation) {
            result[relName] = {
              data: [_this.processItem(foundRelation, included)]
            };
          }
        }
      }

      return result;
    }, {});
  };

  EnrichmentsService.transformJSONAPI = function (response) {
    return this.processItem(response.data, response.included);
  };

  EnrichmentsService.rawEnrichmentToAssessment = function (rawEnrichment) {
    var _a; // can be useful for debugging live data against local eval in storybook test


    var enrichment = EnrichmentsService.transformJSONAPI(rawEnrichment);
    return (_a = enrichment.relationships) === null || _a === void 0 ? void 0 : _a.contents.data[0];
  };

  return EnrichmentsService;
}();
var EnrichmentsInstance = new EnrichmentsService();

var Services =
/** @class */
function () {
  function Services() {}

  Services.prototype.initEvaluatorService = function (serviceOpts) {
    return AssessmentInstance.init(serviceOpts);
  };

  Services.prototype.initEnrichmentsService = function (serviceOpts) {
    return EnrichmentsInstance.init(serviceOpts);
  };

  Services.prototype.getManifest = function (vbid) {
    return EnrichmentsInstance.getManifest(vbid);
  };

  Services.prototype.getTightropeClient = function () {
    return AssessmentInstance.getSocket();
  };

  return Services;
}();

var ServicesInstance = new Services();

/**
 * These types were our best guess as to what things may look like from SA API
 * Probably should be pulling these in from the API or CIA
 */
var EnrichmentType;

(function (EnrichmentType) {
  EnrichmentType["ASSESSMENTS"] = "assessments";
})(EnrichmentType || (EnrichmentType = {}));

var AssessmentType;

(function (AssessmentType) {
  AssessmentType["FORMATIVE"] = "formative";
  AssessmentType["SUMMATIVE"] = "summative";
})(AssessmentType || (AssessmentType = {}));

var EnrichmentErrorType;

(function (EnrichmentErrorType) {
  EnrichmentErrorType["None"] = "None";
  EnrichmentErrorType["RetrieveEnrichment"] = "RetrieveEnrichment";
  EnrichmentErrorType["RestoreEnrichment"] = "RestoreEnrichment";
  EnrichmentErrorType["UnknownEnrichmentType"] = "UnknownEnrichmentType";
})(EnrichmentErrorType || (EnrichmentErrorType = {}));

var useEnrichment = function (enrichmentId) {
  var _a = useState(undefined),
      data = _a[0],
      setData = _a[1];

  var _b = useState(undefined),
      restoredData = _b[0],
      setRestoredData = _b[1];

  var _c = useState(undefined),
      error = _c[0],
      setError = _c[1]; // need to identify summative to know whether to pass "create_if_does_not_exist" flag
  // this will start an attempt if one isn't already underway
  // this is why data is loaded before restored data and the need to keep the two synced
  // the parent would know this from manifest but it is better for the parent
  // to hand off just an enrichmentID and let the components do the rest


  useEffect(function () {
    // to avoid stale restored data alongside changed enrichment data
    setData(undefined);
    setRestoredData(undefined); // get the enrichment data we will then know assessment type

    EnrichmentsInstance.getEnrichment(enrichmentId).then(function (enrichment) {
      setData(enrichment);
    }).catch(function (e) {
      setError({
        type: EnrichmentErrorType.RetrieveEnrichment,
        detail: e.toString()
      });
    });
  }, [enrichmentId]);
  useEffect(function () {
    var _a;

    if (data === undefined) {
      // want to load enrichment first so we know to pass "create_if_does_not_exist"
      return;
    }

    var enrichmentType = (_a = data.relationships) === null || _a === void 0 ? void 0 : _a.content.data[0].attributes.assessment_type;
    AssessmentInstance.restoreEnrichment(enrichmentId, enrichmentType, enrichmentType === AssessmentType.SUMMATIVE ? AssessmentCreateFlag.AssessmentCreateIfDoesNotExist : AssessmentCreateFlag.AssessmentDontCreate).then(function (data) {
      setRestoredData(data);
    }).catch(function (e) {
      setError({
        type: EnrichmentErrorType.RestoreEnrichment,
        detail: e.toString()
      });
    });
  }, [data, enrichmentId]); // need to provide both assets together otherwise there could be a flash of unrestored work

  return restoredData && data ? {
    data: data,
    restoredData: restoredData,
    error: error
  } : {
    data: undefined,
    restoredData: undefined,
    error: error
  };
};

var Styled$z = {
  Container: styled.div.withConfig({
    displayName: "QuestionGroupControls__Container"
  })(templateObject_1$N || (templateObject_1$N = __makeTemplateObject(["\n    font-size: 1.6rem;\n    padding: 16px;\n    position: relative;\n    &:before {\n      content: \"\";\n      display: block;\n      box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.2);\n      height: 1px;\n      top: 0;\n      left: 0;\n      width: 100%;\n      position: absolute;\n    }\n  "], ["\n    font-size: 1.6rem;\n    padding: 16px;\n    position: relative;\n    &:before {\n      content: \"\";\n      display: block;\n      box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.2);\n      height: 1px;\n      top: 0;\n      left: 0;\n      width: 100%;\n      position: absolute;\n    }\n  "]))),
  ButtonStrip: styled.div.withConfig({
    displayName: "QuestionGroupControls__ButtonStrip"
  })(templateObject_2$A || (templateObject_2$A = __makeTemplateObject(["\n    margin-bottom: 16px;\n    display: flex;\n    button {\n      // todo need option in vstui for wrapping button\n      height: auto;\n      div:first-child {\n        white-space: normal;\n        margin: 2px;\n        text-align: center;\n      }\n      flex: 1 1 0;\n      &:not(:last-of-type) {\n        margin-right: 12px;\n      }\n    }\n  "], ["\n    margin-bottom: 16px;\n    display: flex;\n    button {\n      // todo need option in vstui for wrapping button\n      height: auto;\n      div:first-child {\n        white-space: normal;\n        margin: 2px;\n        text-align: center;\n      }\n      flex: 1 1 0;\n      &:not(:last-of-type) {\n        margin-right: 12px;\n      }\n    }\n  "])))
};

var QuestionGroupControls = function (_a) {
  var submitted = _a.submitted,
      onSubmit = _a.onSubmit,
      onReveal = _a.onReveal,
      onReset = _a.onReset,
      onDone = _a.onDone;
  return /*#__PURE__*/React.createElement(Styled$z.Container, null, !submitted ? /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    onClick: function () {
      onSubmit();
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestionGroup.checkAllAnswers))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styled$z.ButtonStrip, null, /*#__PURE__*/React.createElement(Button, {
    outline: true,
    onClick: function () {
      onReset();
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestionGroup.retryQuestions))), /*#__PURE__*/React.createElement(Button, {
    outline: true,
    onClick: function () {
      onReveal();
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestionGroup.revealAnswers)))), onDone && /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    onClick: function () {
      onDone();
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsDone)))));
};
var templateObject_1$N, templateObject_2$A;

var primaryShade = function (index) {
  return css(templateObject_1$M || (templateObject_1$M = __makeTemplateObject(["\n    ", "\n  "], ["\n    ", "\n  "])), function (_a) {
    var theme = _a.theme;
    return theme.vstui.color.primary.getShade(index);
  });
};
var themePrimaryShade = function (index, theme) {
  return theme.vstui.color.primary.getShade(index);
}; // mixins

var boxStyle = css(templateObject_3$q || (templateObject_3$q = __makeTemplateObject(["\n  border-radius: 4px;\n  background-color: #ffffff;\n  border: 1px solid ", ";\n"], ["\n  border-radius: 4px;\n  background-color: #ffffff;\n  border: 1px solid ", ";\n"])), cloud.getShade(3));
var correctBoxStyle = css(templateObject_4$l || (templateObject_4$l = __makeTemplateObject(["\n  ", ";\n  background-color: ", ";\n  border: 1px solid ", ";\n"], ["\n  ", ";\n  background-color: ", ";\n  border: 1px solid ", ";\n"])), boxStyle, green.getShade(1), green.getShade(6));
var incorrectBoxStyle = css(templateObject_5$d || (templateObject_5$d = __makeTemplateObject(["\n  ", ";\n  background-color: ", ";\n  border: 1px solid ", ";\n"], ["\n  ", ";\n  background-color: ", ";\n  border: 1px solid ", ";\n"])), boxStyle, red.getShade(1), red.getShade(6));
var revealedBoxStyle = css(templateObject_6$b || (templateObject_6$b = __makeTemplateObject(["\n  ", ";\n  background-color: ", ";\n  border: 1px solid ", ";\n"], ["\n  ", ";\n  background-color: ", ";\n  border: 1px solid ", ";\n"])), boxStyle, cloud.getShade(1), cloud.getShade(4));
var surveyBoxStyle = css(templateObject_7$7 || (templateObject_7$7 = __makeTemplateObject(["\n  ", ";\n  background-color: ", ";\n  border: 1px solid ", ";\n  border-radius: 4px;\n"], ["\n  ", ";\n  background-color: ", ";\n  border: 1px solid ", ";\n  border-radius: 4px;\n"])), boxStyle, primaryShade(1), primaryShade(6));
var markupHost = css(templateObject_8$4 || (templateObject_8$4 = __makeTemplateObject(["\n  .callout {\n    padding: 1rem;\n    background-color: ", ";\n    border: 1px solid ", ";\n    border-radius: 4px;\n    font-weight: 400;\n  }\n  .answer {\n    font-weight: 900;\n  }\n  .term,\n  .definition {\n    font-style: italic;\n  }\n"], ["\n  .callout {\n    padding: 1rem;\n    background-color: ", ";\n    border: 1px solid ", ";\n    border-radius: 4px;\n    font-weight: 400;\n  }\n  .answer {\n    font-weight: 900;\n  }\n  .term,\n  .definition {\n    font-style: italic;\n  }\n"])), cloud.getShade(1), cloud.getShade(4));
var visuallyHidden = css(templateObject_9$2 || (templateObject_9$2 = __makeTemplateObject(["\n  position: absolute;\n  clip: rect(1px 1px 1px 1px);\n  /* for Internet Explorer */\n  clip: rect(1px, 1px, 1px, 1px);\n  padding: 0;\n  border: 0;\n  height: 1px;\n  width: 1px;\n  white-space: nowrap;\n  overflow: hidden;\n"], ["\n  position: absolute;\n  clip: rect(1px 1px 1px 1px);\n  /* for Internet Explorer */\n  clip: rect(1px, 1px, 1px, 1px);\n  padding: 0;\n  border: 0;\n  height: 1px;\n  width: 1px;\n  white-space: nowrap;\n  overflow: hidden;\n"])));
var focusRect = css(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n  border-radius: 4px;\n  outline: none;\n  box-shadow: 0 0 0 4px white, 0 0 0 6px ", ";\n  scroll-margin: 12px;\n"], ["\n  border-radius: 4px;\n  outline: none;\n  box-shadow: 0 0 0 4px white, 0 0 0 6px ", ";\n  scroll-margin: 12px;\n"])), primaryShade(6));
var templateObject_1$M, templateObject_3$q, templateObject_4$l, templateObject_5$d, templateObject_6$b, templateObject_7$7, templateObject_8$4, templateObject_9$2, templateObject_10;

var useTakeFocus = function (ref, _a) {
  var _b = _a.smoothScrollIntoView,
      smoothScrollIntoView = _b === void 0 ? true : _b,
      _c = _a.takeFocus,
      takeFocus = _c === void 0 ? true : _c,
      _d = _a.temporary,
      temporary = _d === void 0 ? false : _d,
      _e = _a.onceOnly,
      onceOnly = _e === void 0 ? false : _e;
  var focusTaken = useRef(false);
  useEffect(function () {
    if (takeFocus && ref.current && !focusTaken.current) {
      if (temporary) {
        ref.current.setAttribute("tabindex", "-1");
      }

      if (smoothScrollIntoView) {
        ref.current.focus({
          preventScroll: true
        });
        ref.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      } else {
        ref.current.focus();
      }

      focusTaken.current = true;
      ref.current.addEventListener("blur", function () {
        var _a;

        if (temporary) {
          (_a = ref.current) === null || _a === void 0 ? void 0 : _a.removeAttribute("tabindex");
        }

        if (!onceOnly) {
          focusTaken.current = false;
        }
      }, {
        once: true
      });
    } else {
      focusTaken.current = false;
    }
  }, [onceOnly, ref, smoothScrollIntoView, takeFocus, temporary]);
};

var A11yHiddenSpan = styled.span.withConfig({
  displayName: "A11y__A11yHiddenSpan"
})(templateObject_1$L || (templateObject_1$L = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), visuallyHidden); // visually hidden content just for the screen reader

var A11y = function (_a) {
  var children = _a.children,
      _b = _a.focused,
      focused = _b === void 0 ? false : _b,
      props = __rest(_a, ["children", "focused"]);

  var ref = useRef(null);
  useTakeFocus(ref, {
    takeFocus: focused,
    temporary: true
  });
  return /*#__PURE__*/React.createElement(A11yHiddenSpan, __assign({
    ref: ref
  }, props), children);
};
var templateObject_1$L;

var pipSize = 22;
var pipFocusClass = "pipFocus";
var Styled$y = {
  ContainerBtn: styled.button.withConfig({
    displayName: "QuestionNavBarPip__ContainerBtn"
  })(templateObject_1$K || (templateObject_1$K = __makeTemplateObject(["\n    cursor: pointer;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    width: ", "px;\n    height: ", "px;\n    margin: 0 4px;\n    position: relative;\n    border: none;\n    padding: 0;\n    background-color: transparent;\n    outline: none;\n    .", " {\n      display: none;\n    }\n    :focus {\n      .", " {\n        display: block;\n      }\n    }\n  "], ["\n    cursor: pointer;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    width: ", "px;\n    height: ", "px;\n    margin: 0 4px;\n    position: relative;\n    border: none;\n    padding: 0;\n    background-color: transparent;\n    outline: none;\n    .", " {\n      display: none;\n    }\n    :focus {\n      .", " {\n        display: block;\n      }\n    }\n  "])), pipSize, pipSize, pipFocusClass, pipFocusClass),
  SvgWrapper: styled.div.withConfig({
    displayName: "QuestionNavBarPip__SvgWrapper"
  })(templateObject_2$z || (templateObject_2$z = __makeTemplateObject(["\n    position: absolute;\n    top: 0;\n    left: 0;\n    height: ", "px;\n    svg {\n      overflow: visible;\n    }\n  "], ["\n    position: absolute;\n    top: 0;\n    left: 0;\n    height: ", "px;\n    svg {\n      overflow: visible;\n    }\n  "])), pipSize),
  QuestionNumber: styled.span.withConfig({
    displayName: "QuestionNavBarPip__QuestionNumber"
  })(templateObject_3$p || (templateObject_3$p = __makeTemplateObject(["\n    font-size: 1.1rem;\n    position: relative;\n  "], ["\n    font-size: 1.1rem;\n    position: relative;\n  "]))),
  IconHolder: styled.span.withConfig({
    displayName: "QuestionNavBarPip__IconHolder"
  })(templateObject_4$k || (templateObject_4$k = __makeTemplateObject(["\n    position: relative;\n    svg {\n      transform: scale(0.9);\n      vertical-align: middle;\n      margin-top: -2px;\n    }\n  "], ["\n    position: relative;\n    svg {\n      transform: scale(0.9);\n      vertical-align: middle;\n      margin-top: -2px;\n    }\n  "])))
};
var StatusColorAspect;

(function (StatusColorAspect) {
  StatusColorAspect[StatusColorAspect["Stroke"] = 0] = "Stroke";
  StatusColorAspect[StatusColorAspect["Fill"] = 1] = "Fill";
})(StatusColorAspect || (StatusColorAspect = {}));

var statusToColor = function (status, aspect, theme) {
  if (status === QuestionStatus.Complete) {
    return aspect === StatusColorAspect.Stroke ? themePrimaryShade(6, theme) : themePrimaryShade(1, theme);
  } else if (status === QuestionStatus.Correct) {
    return aspect === StatusColorAspect.Stroke ? green.getShade(6) : green.getShade(1);
  } else if (status === QuestionStatus.Incorrect) {
    return aspect === StatusColorAspect.Stroke ? red.getShade(6) : red.getShade(1);
  } else {
    return aspect === StatusColorAspect.Stroke ? cloud.getShade(6) : cloud.getShade(1);
  }
};

var QuestionNavBarPip = function (_a) {
  var index = _a.index,
      status = _a.status,
      selected = _a.selected,
      onClick = _a.onClick,
      theme = _a.theme;
  var intl = useIntl();
  var strokeWidth = selected ? "4" : "1.5";
  return /*#__PURE__*/React.createElement(Styled$y.ContainerBtn, {
    "aria-current": selected,
    onClick: function () {
      return onClick();
    }
  }, /*#__PURE__*/React.createElement(Styled$y.SvgWrapper, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("svg", {
    width: pipSize,
    height: pipSize,
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("circle", {
    stroke: statusToColor(status, StatusColorAspect.Stroke, theme),
    strokeWidth: strokeWidth,
    fill: statusToColor(status, StatusColorAspect.Fill, theme),
    cx: pipSize / 2,
    cy: pipSize / 2,
    r: selected ? "11.25" : "10.25"
  })), /*#__PURE__*/React.createElement("g", {
    className: pipFocusClass,
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("circle", {
    stroke: themePrimaryShade(6, theme),
    strokeWidth: 2,
    cx: pipSize / 2,
    cy: pipSize / 2,
    r: selected ? 16 : 14
  })))), status === QuestionStatus.Incomplete ? /*#__PURE__*/React.createElement(Styled$y.QuestionNumber, {
    "aria-hidden": true
  }, index + 1) : status === QuestionStatus.Incorrect ? /*#__PURE__*/React.createElement(Styled$y.IconHolder, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(XSmall, {
    color: statusToColor(status, StatusColorAspect.Stroke, theme)
  })) : /*#__PURE__*/React.createElement(Styled$y.IconHolder, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Checkmark, {
    color: statusToColor(status, StatusColorAspect.Stroke, theme)
  })), /*#__PURE__*/React.createElement(A11y, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.pip, {
    values: {
      index: index + 1,
      status: intl.formatMessage(messagesQuestion.status, {
        status: status
      })
    }
  }))));
};

var QuestionNavBarPip$1 = withTheme(QuestionNavBarPip);
var templateObject_1$K, templateObject_2$z, templateObject_3$p, templateObject_4$k;

var Styled$x = {
  Container: styled.div.withConfig({
    displayName: "QuestionNavBar__Container"
  })(templateObject_1$J || (templateObject_1$J = __makeTemplateObject(["\n    text-align: center;\n    background-color: ", ";\n    box-shadow: 0 0.5px 1px 0 rgba(0, 0, 0, 0.2);\n    display: flex;\n  "], ["\n    text-align: center;\n    background-color: ", ";\n    box-shadow: 0 0.5px 1px 0 rgba(0, 0, 0, 0.2);\n    display: flex;\n  "])), frost.getShade(1)),
  BtnHolder: styled.div.withConfig({
    displayName: "QuestionNavBar__BtnHolder"
  })(templateObject_2$y || (templateObject_2$y = __makeTemplateObject(["\n    min-width: 40px;\n    padding: 6px 8px;\n    box-sizing: content-box;\n  "], ["\n    min-width: 40px;\n    padding: 6px 8px;\n    box-sizing: content-box;\n  "]))),
  PipsHolder: styled.div.withConfig({
    displayName: "QuestionNavBar__PipsHolder"
  })(templateObject_3$o || (templateObject_3$o = __makeTemplateObject(["\n    flex-grow: 1;\n    align-self: center;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n  "], ["\n    flex-grow: 1;\n    align-self: center;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n  "])))
};

var QuestionNavBar = function (_a) {
  var items = _a.items,
      itemIndex = _a.itemIndex,
      onChange = _a.onChange;
  var intl = useIntl();
  return /*#__PURE__*/React.createElement(Styled$x.Container, null, /*#__PURE__*/React.createElement(Styled$x.BtnHolder, null, itemIndex > 0 && /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": intl.formatMessage(messagesQuestionGroup.previousQuestion),
    icon: /*#__PURE__*/React.createElement(CaretLeftLarge, {
      color: frost.getShade(10)
    }),
    onClick: function () {
      onChange(itemIndex - 1);
    }
  })), /*#__PURE__*/React.createElement(Styled$x.PipsHolder, null, items.map(function (item, index) {
    return /*#__PURE__*/React.createElement(QuestionNavBarPip$1, {
      key: "question-" + index,
      index: index,
      status: item,
      selected: index === itemIndex,
      onClick: function () {
        if (index !== itemIndex) {
          onChange(index);
        }
      }
    });
  })), /*#__PURE__*/React.createElement(Styled$x.BtnHolder, null, itemIndex < items.length - 1 && /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": intl.formatMessage(messagesQuestionGroup.nextQuestion),
    icon: /*#__PURE__*/React.createElement(CaretRightLarge, {
      color: frost.getShade(10)
    }),
    onClick: function () {
      onChange(itemIndex + 1);
    }
  })));
};
var templateObject_1$J, templateObject_2$y, templateObject_3$o;

var AriaLiveMode;

(function (AriaLiveMode) {
  AriaLiveMode["Polite"] = "Polite";
  AriaLiveMode["Assertive"] = "Assertive";
})(AriaLiveMode || (AriaLiveMode = {}));

// action types
var QUESTION_ACTION_PREFIX = "QUESTION";
var QUESTION_STARTED = "QUESTION_STARTED";
var QUESTION_LOADED = "QUESTION_LOADED";
var QUESTION_RESET = "QUESTION_RESET";
var QUESTION_SUBMIT_REQUESTED = "QUESTION_SUBMIT_REQUESTED";
var QUESTION_VALIDATION_ERROR = "QUESTION_VALIDATION_ERROR";
var QUESTION_COMPLETED = "QUESTION_COMPLETED";
var QUESTION_SUBMITTED = "QUESTION_SUBMITTED";
var QUESTION_CLEAR_SUBMIT_ACTIVE = "QUESTION_CLEAR_SUBMIT_ACTIVE";
var QUESTION_ATTEMPT = "QUESTION_ATTEMPT";
var QUESTION_ASSESSED = "QUESTION_ASSESSED";
var QUESTION_REVEAL_ANSWER = "QUESTION_REVEAL_ANSWER";
var QUESTION_ANSWER_REVEALED = "QUESTION_ANSWER_REVEALED";
var QUESTION_TIME_STARTED = "QUESTION_TIME_STARTED";
var QUESTION_SET_FEEDBACK = "QUESTION_SET_FEEDBACK";
var QUESTION_ARIA_LIVE_MESSAGE = "QUESTION_ARIA_LIVE_MESSAGE";
var QUESTION_FOCUS_START = "QUESTION_FOCUS_START";
var QUESTION_EVALUATED = "QUESTION_EVALUATED";
var QUESTION_LOCK = "QUESTION_LOCK";
var QUESTION_UNLOCK = "QUESTION_UNLOCK";
var QUESTION_SYSTEM_ERROR = "QUESTION_SYSTEM_ERROR";
var QUESTION_CLEAR_SYSTEM_ERROR = "QUESTION_CLEAR_SYSTEM_ERROR";
var QUESTION_SET_IMPROVEMENT_STATUS = "QUESTION_SET_IMPROVEMENT_STATUS";
var QUESTION_CLEAR_IMPROVEMENT_STATUS = "QUESTION_CLEAR_IMPROVEMENT_STATUS";
var QUESTION_SET_ANSWER_SUGGESTIONS = "QUESTION_SET_ANSWER_SUGGESTIONS";
var QUESTION_DISMISS_ANSWER_SUGGESTION = "QUESTION_DISMISS_ANSWER_SUGGESTION"; // action creators

var createBasicAction = function (name, payload) {
  return {
    type: name,
    payload: payload
  };
};
var questionLoaded = function () {
  return {
    type: QUESTION_LOADED
  };
};
var questionReset = function (state) {
  return {
    type: QUESTION_RESET,
    state: state
  };
};
var questionEvaluated = function (state) {
  return createBasicAction(QUESTION_EVALUATED, state);
};
var questionSubmitRequested = function (submitRequested, byUser) {
  return {
    type: QUESTION_SUBMIT_REQUESTED,
    submitRequested: submitRequested,
    byUser: byUser
  };
};
var questionError = function (errorMsg, errorCode) {
  return {
    type: QUESTION_VALIDATION_ERROR,
    errorMsg: errorMsg,
    errorCode: errorCode
  };
};
var questionSubmitted = function (submitted) {
  return {
    type: QUESTION_SUBMITTED,
    submitted: submitted
  };
};
var questionClearSubmitActive = function () {
  return {
    type: QUESTION_CLEAR_SUBMIT_ACTIVE
  };
};
var questionAttempt = function () {
  return {
    type: QUESTION_ATTEMPT
  };
};
var questionAssessed = function (correct) {
  return {
    type: QUESTION_ASSESSED,
    correct: correct
  };
};
var questionRevealAnswer = function (revealAnswer) {
  return {
    type: QUESTION_REVEAL_ANSWER,
    revealAnswer: revealAnswer
  };
};
var questionAnswerRevealed = function (answerRevealed) {
  return {
    type: QUESTION_ANSWER_REVEALED,
    answerRevealed: answerRevealed
  };
};
var questionStartTime = function (startTime) {
  return {
    type: QUESTION_TIME_STARTED,
    startTime: startTime
  };
};
var questionSetFeedback = function (feedback, systemFeedback) {
  return {
    type: QUESTION_SET_FEEDBACK,
    feedback: feedback,
    systemFeedback: systemFeedback
  };
};
var questionAriaLiveMessage = function (message, mode) {
  if (mode === void 0) {
    mode = AriaLiveMode.Polite;
  }

  return {
    type: QUESTION_ARIA_LIVE_MESSAGE,
    message: message,
    mode: mode
  };
};
var questionFocusStart = function (focus) {
  if (focus === void 0) {
    focus = true;
  }

  return {
    type: QUESTION_FOCUS_START,
    focus: focus
  };
};
var questionLock = function (lockedReason, lockStartTime) {
  if (lockStartTime === void 0) {
    lockStartTime = Date.now();
  }

  return {
    type: QUESTION_LOCK,
    lockedReason: lockedReason,
    lockStartTime: lockStartTime
  };
};
var questionUnlock = function () {
  return {
    type: QUESTION_UNLOCK
  };
};
var questionSystemError = function (systemError) {
  return {
    type: QUESTION_SYSTEM_ERROR,
    systemError: systemError
  };
};
var questionClearSystemError = function () {
  return {
    type: QUESTION_CLEAR_SYSTEM_ERROR
  };
};
var questionSetImprovementStatus = function (status) {
  return {
    type: QUESTION_SET_IMPROVEMENT_STATUS,
    status: status
  };
};
var questionClearImprovementStatus = function () {
  return {
    type: QUESTION_CLEAR_IMPROVEMENT_STATUS
  };
};
var questionSetAnswerSuggestions = function (answerSuggestions) {
  return {
    type: QUESTION_SET_ANSWER_SUGGESTIONS,
    answerSuggestions: answerSuggestions
  };
};
var questionDismissAnswerSuggestion = function (answerId) {
  return {
    type: QUESTION_DISMISS_ANSWER_SUGGESTION,
    answerId: answerId
  };
};

// action types
var MULTIPLE_CHOICE_SET_RESPONSE = "MULTIPLE_CHOICE_SET_RESPONSE";
var MULTIPLE_CHOICE_SET_REVEALED_ANSWER = "MULTIPLE_CHOICE_SET_REVEALED_ANSWER"; // action creators

var multipleChoiceSetResponseAction = function (response) {
  return {
    type: MULTIPLE_CHOICE_SET_RESPONSE,
    response: response
  };
};
var multipleChoiceSetRevealedAnswerAction = function (revealedAnswer) {
  return {
    type: MULTIPLE_CHOICE_SET_REVEALED_ANSWER,
    revealedAnswer: revealedAnswer
  };
};

var multipleChoiceStateReducer = function (state, action) {
  switch (action.type) {
    case MULTIPLE_CHOICE_SET_RESPONSE:
      return __assign(__assign({}, state), {
        response: action.response
      });

    case MULTIPLE_CHOICE_SET_REVEALED_ANSWER:
      return __assign(__assign({}, state), {
        revealedAnswer: action.revealedAnswer
      });

    default:
      return state;
  }
};

var questionStateReducer = function (state, action) {
  var _a;

  var _b;

  if (!startsWith(action.type, QUESTION_ACTION_PREFIX)) {
    return state;
  }

  switch (action.type) {
    case QUESTION_STARTED:
      return __assign(__assign({}, state), {
        started: action.started
      });

    case QUESTION_LOADED:
      return state.loaded ? state : __assign(__assign({}, state), {
        loaded: true
      });

    case QUESTION_RESET:
      // note improvement status persists across reset
      return __assign(__assign({}, action.state), {
        improvementStatus: state.improvementStatus
      });

    case QUESTION_EVALUATED:
      return __assign(__assign({}, state), action.payload);

    case QUESTION_SUBMIT_REQUESTED:
      return __assign(__assign({}, state), {
        submitRequested: action.submitRequested,
        submitActive: !!action.byUser || state.submitActive,
        numSubmitRequestedAttempts: action.submitRequested ? state.numSubmitRequestedAttempts + 1 : state.numSubmitRequestedAttempts
      });

    case QUESTION_VALIDATION_ERROR:
      return __assign(__assign({}, state), {
        validationErrorMsg: action.errorMsg,
        validationErrorCode: action.errorCode,
        submitActive: false
      });

    case QUESTION_SUBMITTED:
      return __assign(__assign({}, state), {
        submitted: action.submitted
      });

    case QUESTION_CLEAR_SUBMIT_ACTIVE:
      return __assign(__assign({}, state), {
        submitActive: false
      });

    case QUESTION_ASSESSED:
      return __assign(__assign({}, state), {
        answerCorrect: action.correct
      });

    case QUESTION_COMPLETED:
      return __assign(__assign({}, state), {
        completed: action.completed
      });

    case QUESTION_REVEAL_ANSWER:
      return __assign(__assign({}, state), {
        revealAnswer: action.revealAnswer
      });

    case QUESTION_ANSWER_REVEALED:
      return __assign(__assign({}, state), {
        answerRevealed: action.answerRevealed
      });

    case QUESTION_TIME_STARTED:
      return __assign(__assign({}, state), {
        startTime: action.startTime
      });

    case QUESTION_ATTEMPT:
      return __assign(__assign({}, state), {
        numAttempts: state.numAttempts + 1
      });

    case QUESTION_SET_FEEDBACK:
      return __assign(__assign({}, state), {
        feedback: action.feedback,
        systemFeedback: action.systemFeedback
      });

    case QUESTION_ARIA_LIVE_MESSAGE:
      return __assign(__assign({}, state), {
        ariaLiveMessage: action.message,
        ariaLiveMode: action.mode
      });

    case QUESTION_FOCUS_START:
      return __assign(__assign({}, state), {
        focusQuestionStart: action.focus
      });

    case QUESTION_LOCK:
      if (!state.locked) {
        return __assign(__assign({}, state), {
          locked: true,
          lockedReason: action.lockedReason
        });
      } else {
        return state;
      }

    case QUESTION_UNLOCK:
      if (state.locked) {
        return __assign(__assign({}, state), {
          locked: false,
          lockedReason: undefined
        });
      } else {
        return state;
      }

    case QUESTION_SYSTEM_ERROR:
      return __assign(__assign({}, state), {
        systemError: action.systemError
      });

    case QUESTION_CLEAR_SYSTEM_ERROR:
      return __assign(__assign({}, state), {
        systemError: undefined
      });

    case QUESTION_SET_IMPROVEMENT_STATUS:
      return __assign(__assign({}, state), {
        improvementStatus: action.status
      });

    case QUESTION_CLEAR_IMPROVEMENT_STATUS:
      return __assign(__assign({}, state), {
        improvementStatus: undefined
      });

    case QUESTION_SET_ANSWER_SUGGESTIONS:
      {
        var answerSuggestionsControls = {};

        for (var _i = 0, _c = Object.keys(action.answerSuggestions); _i < _c.length; _i++) {
          var answerId = _c[_i];
          answerSuggestionsControls[answerId] = {
            dismissed: false
          };
        }

        return __assign(__assign({}, state), {
          answerSuggestions: action.answerSuggestions,
          answerSuggestionsControls: answerSuggestionsControls
        });
      }

    case QUESTION_DISMISS_ANSWER_SUGGESTION:
      return !((_b = state.answerSuggestionsControls) === null || _b === void 0 ? void 0 : _b[action.answerId]) ? state : __assign(__assign({}, state), {
        answerSuggestionsControls: __assign(__assign({}, state.answerSuggestionsControls), (_a = {}, _a[action.answerId] = __assign(__assign({}, state.answerSuggestionsControls[action.answerId]), {
          dismissed: true
        }), _a))
      });

    default:
      return state;
  }
};

var getDefaultMultipleChoiceState = function (data) {
  var defaults = getDefaultQuestionState();
  var inputKey = Object.keys(data.options)[0];
  var answers = data.options[inputKey];
  var numAnswers = Object.keys(answers).length;
  var answerOrder = range(0, numAnswers);

  if (data.shuffle !== false) {
    answerOrder = shuffle(answerOrder);
  }

  return __assign(__assign({}, defaults), {
    response: unanswered,
    answerOrder: answerOrder,
    revealedAnswer: undefined,
    improvementStatus: undefined
  });
};
var mergeEvaluatedState$2 = function (state, evaluatedData) {
  var response = unanswered;
  var submitted = state.submitted,
      answerCorrect = state.answerCorrect,
      feedback = state.feedback,
      improvementStatus = state.improvementStatus;

  if (evaluatedData) {
    if (evaluatedData.answers) {
      var inputKey = Object.keys(evaluatedData.answers)[0];
      var val = evaluatedData.answers[inputKey];

      if (val) {
        response = val;
      }
    }

    submitted = evaluatedData.correct !== undefined;

    if (evaluatedData.correct !== undefined) {
      answerCorrect = evaluatedData.correct;
    }

    if (evaluatedData.feedback) {
      feedback = evaluatedData.feedback;
    }

    if (evaluatedData.improvementStatus) {
      improvementStatus = evaluatedData.improvementStatus;
    }
  }

  return __assign(__assign({}, state), {
    response: response,
    revealedAnswer: undefined,
    submitted: submitted,
    answerCorrect: answerCorrect,
    feedback: feedback,
    improvementStatus: improvementStatus
  });
};
var multipleChoiceRootReducer = function (state, action) {
  var newState = questionStateReducer(state, action);
  newState = multipleChoiceStateReducer(newState, action);
  return newState;
};

var _a$1;
var NotificationType;

(function (NotificationType) {
  NotificationType[NotificationType["Bad"] = 0] = "Bad";
  NotificationType[NotificationType["Good"] = 1] = "Good";
  NotificationType[NotificationType["Neutral"] = 2] = "Neutral";
})(NotificationType || (NotificationType = {}));

var NotificationIconType;

(function (NotificationIconType) {
  NotificationIconType[NotificationIconType["None"] = 0] = "None";
  NotificationIconType[NotificationIconType["ExclamationCircle"] = 1] = "ExclamationCircle";
})(NotificationIconType || (NotificationIconType = {}));

var iconMapping$1 = (_a$1 = {}, _a$1[NotificationIconType.None] = null, _a$1[NotificationIconType.ExclamationCircle] = ExclamationCircle, _a$1);
var NotificationContainer = styled.div.withConfig({
  displayName: "Notification__NotificationContainer"
})(templateObject_2$x || (templateObject_2$x = __makeTemplateObject(["\n  background: #fff2f4;\n  border: 1px solid #e71d3a;\n  border-radius: 4px;\n  padding: 24px;\n  ", "\n  &:focus {\n    ", "\n  }\n  margin-bottom: 20px;\n  position: relative;\n"], ["\n  background: #fff2f4;\n  border: 1px solid #e71d3a;\n  border-radius: 4px;\n  padding: 24px;\n  ", "\n  &:focus {\n    ", "\n  }\n  margin-bottom: 20px;\n  position: relative;\n"])), function (_a) {
  var withoutMarginTop = _a.withoutMarginTop;
  return !withoutMarginTop && css(templateObject_1$I || (templateObject_1$I = __makeTemplateObject(["\n      margin-top: 20px;\n    "], ["\n      margin-top: 20px;\n    "])));
}, focusRect);
var IconHolder = styled.span.withConfig({
  displayName: "Notification__IconHolder"
})(templateObject_3$n || (templateObject_3$n = __makeTemplateObject(["\n  position: absolute;\n  svg {\n    fill: #e71d3a;\n  }\n"], ["\n  position: absolute;\n  svg {\n    fill: #e71d3a;\n  }\n"])));
var NotificationMsg = styled.div.withConfig({
  displayName: "Notification__NotificationMsg"
})(templateObject_4$j || (templateObject_4$j = __makeTemplateObject(["\n  font-weight: 500;\n  font-size: 1.6rem;\n  color: #1c1c1c;\n  margin-left: 34px;\n"], ["\n  font-weight: 500;\n  font-size: 1.6rem;\n  color: #1c1c1c;\n  margin-left: 34px;\n"])));

var Notification = function (_a) {
  var id = _a.id,
      children = _a.children,
      message = _a.message,
      focused = _a.focused,
      _b = _a.withoutMarginTop,
      withoutMarginTop = _b === void 0 ? false : _b,
      _c = _a.notificationIcon,
      notificationIcon = _c === void 0 ? NotificationIconType.None : _c;
  var intl = useIntl();
  var ref = useRef(null);
  useTakeFocus(ref, {
    takeFocus: focused,
    temporary: true,
    smoothScrollIntoView: true
  });
  var Icon = iconMapping$1[notificationIcon];
  return /*#__PURE__*/React.createElement(NotificationContainer, {
    id: id,
    key: id,
    ref: ref,
    withoutMarginTop: withoutMarginTop
  }, Icon && /*#__PURE__*/React.createElement(IconHolder, null, /*#__PURE__*/React.createElement(Icon, null)), /*#__PURE__*/React.createElement(A11y, null, intl.formatMessage(messagesQuestion.notificationAttention), " "), /*#__PURE__*/React.createElement(NotificationMsg, null, message || children));
};
var templateObject_1$I, templateObject_2$x, templateObject_3$n, templateObject_4$j;

var AnswerFeedbackPadding;

(function (AnswerFeedbackPadding) {
  AnswerFeedbackPadding[AnswerFeedbackPadding["Normal"] = 0] = "Normal";
})(AnswerFeedbackPadding || (AnswerFeedbackPadding = {}));

var AnswerFeedbackContainer = styled.div.withConfig({
  displayName: "AnswerFeedback__AnswerFeedbackContainer"
})(templateObject_1$H || (templateObject_1$H = __makeTemplateObject(["\n  ", ";\n  margin-top: 32px;\n  line-height: 24px;\n  &:focus {\n    ", "\n  }\n"], ["\n  ", ";\n  margin-top: 32px;\n  line-height: 24px;\n  &:focus {\n    ", "\n  }\n"])), markupHost, focusRect);
var AnswerFeedbackMessage = styled.div.withConfig({
  displayName: "AnswerFeedback__AnswerFeedbackMessage"
})(templateObject_2$w || (templateObject_2$w = __makeTemplateObject(["\n  font-size: 1.6rem;\n  font-weight: 900;\n  margin-bottom: 8px;\n  color: ", ";\n"], ["\n  font-size: 1.6rem;\n  font-weight: 900;\n  margin-bottom: 8px;\n  color: ", ";\n"])), function (props) {
  return props.correct === true ? green.getShade(6) : props.correct === false ? red.getShade(6) : themePrimaryShade(6, props.theme);
});
var AnswerFeedbackContent = styled.div.withConfig({
  displayName: "AnswerFeedback__AnswerFeedbackContent"
})(templateObject_3$m || (templateObject_3$m = __makeTemplateObject(["\n  font-weight: 400;\n  &:first-child {\n    margin-top: 0;\n  }\n  &:last-child {\n    margin-bottom: 0;\n  }\n"], ["\n  font-weight: 400;\n  &:first-child {\n    margin-top: 0;\n  }\n  &:last-child {\n    margin-bottom: 0;\n  }\n"])));

var AnswerFeedback = function (_a) {
  var id = _a.id,
      children = _a.children,
      correct = _a.correct,
      numAnswers = _a.numAnswers,
      wrongAnswersIndicated = _a.wrongAnswersIndicated,
      message = _a.message,
      content = _a.content,
      _b = _a.padding,
      padding = _b === void 0 ? AnswerFeedbackPadding.Normal : _b,
      focused = _a.focused;
  var intl = useIntl();
  var ref = useRef(null);
  useTakeFocus(ref, {
    takeFocus: focused,
    temporary: true,
    smoothScrollIntoView: true
  });

  if (!message) {
    if (correct === true) {
      message = intl.formatMessage(messagesQuestion.feedbackAnswerCorrect);
    } else if (correct === false) {
      message = intl.formatMessage(numAnswers > 1 ? messagesQuestion.feedbackWrongAnswersGeneric : messagesQuestion.feedbackAnswerIncorrect);
    } else {
      message = intl.formatMessage(messagesQuestion.feedbackSubmissionThanks);
    }
  }

  return /*#__PURE__*/React.createElement(AnswerFeedbackContainer, {
    id: id,
    ref: ref
  }, /*#__PURE__*/React.createElement(AnswerFeedbackMessage, {
    correct: correct,
    padding: padding
  }, message), numAnswers > 1 && correct === false && /*#__PURE__*/React.createElement(A11y, null, intl.formatMessage(wrongAnswersIndicated ? messagesQuestion.feedbackWrongAnswerPrompt : messagesQuestion.feedbackWrongAnswerPromptNotIndicated)), content ? /*#__PURE__*/React.createElement(AnswerFeedbackContent, {
    padding: padding,
    dangerouslySetInnerHTML: {
      __html: content
    }
  }) : children ? /*#__PURE__*/React.createElement(AnswerFeedbackContent, null, children) : null);
};
var templateObject_1$H, templateObject_2$w, templateObject_3$m;

var ControlsAlignment;

(function (ControlsAlignment) {
  ControlsAlignment["Left"] = "Left";
  ControlsAlignment["Center"] = "Center";
  ControlsAlignment["Right"] = "Right";
})(ControlsAlignment || (ControlsAlignment = {}));

var ControlsType;

(function (ControlsType) {
  /** docked to bottom with solid buttons and divider shadow */
  ControlsType[ControlsType["Primary"] = 0] = "Primary";
  /** Inline with hollowed buttons */

  ControlsType[ControlsType["Secondary"] = 1] = "Secondary";
})(ControlsType || (ControlsType = {}));

var Styled$w = {
  Container: styled.div.withConfig({
    displayName: "Controls__Container"
  })(templateObject_4$i || (templateObject_4$i = __makeTemplateObject(["\n    font-size: 1.6rem;\n    padding: 16px;\n    position: relative;\n    text-align: ", ";\n    ", ";\n    ", ";\n    ", ";\n  "], ["\n    font-size: 1.6rem;\n    padding: 16px;\n    position: relative;\n    text-align: ", ";\n    ", ";\n    ", ";\n    ", ";\n  "])), function (_a) {
    var align = _a.align;
    return align === ControlsAlignment.Right ? "right" : align === ControlsAlignment.Center ? "center" : "left";
  }, function (_a) {
    var type = _a.type;
    return type === ControlsType.Primary && css(templateObject_1$G || (templateObject_1$G = __makeTemplateObject(["\n        &:before {\n          content: \"\";\n          display: block;\n          box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.2);\n          height: 1px;\n          top: 0;\n          left: 0;\n          width: 100%;\n          position: absolute;\n        }\n      "], ["\n        &:before {\n          content: \"\";\n          display: block;\n          box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.2);\n          height: 1px;\n          top: 0;\n          left: 0;\n          width: 100%;\n          position: absolute;\n        }\n      "])));
  }, function (_a) {
    var type = _a.type;
    return type === ControlsType.Secondary && css(templateObject_2$v || (templateObject_2$v = __makeTemplateObject(["\n        button:not(:last-of-type) {\n          margin-right: 12px;\n        }\n      "], ["\n        button:not(:last-of-type) {\n          margin-right: 12px;\n        }\n      "])));
  }, function (_a) {
    var dockToBottom = _a.dockToBottom;
    return dockToBottom && css(templateObject_3$l || (templateObject_3$l = __makeTemplateObject(["\n        position: absolute;\n        bottom: 0;\n        width: 100%;\n        box-sizing: border-box;\n      "], ["\n        position: absolute;\n        bottom: 0;\n        width: 100%;\n        box-sizing: border-box;\n      "])));
  })
};
var Controls = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var type = _a.type,
      align = _a.align,
      children = _a.children,
      dockToBottom = _a.dockToBottom,
      _b = _a.visible,
      visible = _b === void 0 ? true : _b;

  if (!visible) {
    return null;
  }

  return /*#__PURE__*/React.createElement(Styled$w.Container, {
    ref: ref || undefined,
    type: type,
    align: align,
    dockToBottom: dockToBottom
  }, children);
});
var templateObject_1$G, templateObject_2$v, templateObject_3$l, templateObject_4$i;

var QuestionSubmitMode;

(function (QuestionSubmitMode) {
  QuestionSubmitMode[QuestionSubmitMode["CheckAnswer"] = 0] = "CheckAnswer";
  QuestionSubmitMode[QuestionSubmitMode["SubmitAnswer"] = 1] = "SubmitAnswer";
})(QuestionSubmitMode || (QuestionSubmitMode = {}));

var QuestionControls = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var intl = useIntl();
  var type = props.type,
      align = props.align,
      submitted = props.submitted,
      dispatch = props.dispatch,
      _a = props.visible,
      visible = _a === void 0 ? true : _a;

  if (!visible) {
    return null;
  }

  if (props.type === ControlsType.Primary) {
    // main controls e.g. Check Answer docked to bottom
    var mode = props.mode,
        type_1 = props.type,
        align_1 = props.align,
        submitted_1 = props.submitted,
        dockToBottom = props.dockToBottom,
        dispatch_1 = props.dispatch,
        showNextQuestion = props.showNextQuestion,
        onNextQuestion_1 = props.onNextQuestion,
        onDone_1 = props.onDone;
    return /*#__PURE__*/React.createElement(Controls, {
      ref: ref,
      type: type_1,
      align: align_1,
      dockToBottom: dockToBottom,
      visible: visible
    }, !submitted_1 && /*#__PURE__*/React.createElement(Button, {
      fullWidth: true,
      onClick: function () {
        dispatch_1(questionSubmitRequested(true, true));
      }
    }, mode === QuestionSubmitMode.CheckAnswer ? /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsCheckAnswer)) : /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsSubmit))), submitted_1 && showNextQuestion && /*#__PURE__*/React.createElement(Button, {
      fullWidth: true,
      onClick: function () {
        onNextQuestion_1 === null || onNextQuestion_1 === void 0 ? void 0 : onNextQuestion_1();
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsNextQuestion))), submitted_1 && !showNextQuestion && /*#__PURE__*/React.createElement(Button, {
      fullWidth: true,
      onClick: function () {
        onDone_1 === null || onDone_1 === void 0 ? void 0 : onDone_1();
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsDone))));
  } else {
    // QuestionControlsType.Secondary inline with question content
    var showReveal = props.showReveal,
        answerCorrect = props.answerCorrect,
        revealAnswer = props.revealAnswer,
        getResetQuestionState_1 = props.getResetQuestionState,
        onRevealAnswer_1 = props.onRevealAnswer,
        onReset_1 = props.onReset;
    return /*#__PURE__*/React.createElement(Controls, {
      type: type,
      align: align
    }, submitted && showReveal && !answerCorrect && !revealAnswer && /*#__PURE__*/React.createElement(Button, {
      outline: true,
      onClick: function () {
        onRevealAnswer_1 === null || onRevealAnswer_1 === void 0 ? void 0 : onRevealAnswer_1();
        dispatch(questionRevealAnswer(true));
        dispatch(questionFocusStart());
        dispatch(questionAriaLiveMessage(intl.formatMessage(messagesQuestion.feedbackRevealAnswerAnnouncement), AriaLiveMode.Assertive));
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsRevealAnswer))), submitted && /*#__PURE__*/React.createElement(Button, {
      outline: true,
      onClick: function () {
        dispatch(questionReset(getResetQuestionState_1()));
        onReset_1 === null || onReset_1 === void 0 ? void 0 : onReset_1();
        defer(function () {
          // defer so we don't reset the aria live message
          dispatch(questionFocusStart());
          dispatch(questionAriaLiveMessage(intl.formatMessage(messagesQuestion.feedbackResetAnnouncement), AriaLiveMode.Assertive));
        });
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsRetry))));
  }
});

var usePrevious = function (value) {
  var ref = useRef(value);
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};

var AriaLiveHiddenDiv = styled.div.withConfig({
  displayName: "AriaLiveRegion__AriaLiveHiddenDiv"
})(templateObject_1$F || (templateObject_1$F = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), visuallyHidden);

var AriaLiveRegion = function (_a) {
  var text = _a.text,
      children = _a.children,
      _b = _a.mode,
      mode = _b === void 0 ? AriaLiveMode.Polite : _b,
      onClear = _a.onClear;
  var clearAfterwards = useRef(debounce(function () {
    onClear();
  }, 2000));

  if (text || children) {
    clearAfterwards.current();
  }

  return /*#__PURE__*/React.createElement(AriaLiveHiddenDiv, {
    role: "status",
    "aria-live": mode === AriaLiveMode.Assertive ? "assertive" : "polite",
    "aria-atomic": "true"
  }, text || children);
};
var templateObject_1$F;

var BASE_DOT_SIZE = 18;
var BASE_DOT_MARGIN = 10;
var Container = styled.div.withConfig({
  displayName: "Loader__Container"
})(templateObject_1$E || (templateObject_1$E = __makeTemplateObject(["\n  overflow: visible;\n  height: 50px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  flex-direction: row;\n  width: 250px;\n  &:focus {\n    outline: 1px solid ", ";\n  }\n  @keyframes loadingPulse {\n    0% {\n      transform: scale(1);\n    }\n    10% {\n      transform: scale(1.5);\n    }\n    20% {\n      transform: scale(2);\n    }\n    30% {\n      transform: scale(1.5);\n    }\n    40% {\n      transform: scale(1);\n    }\n  }\n  & div:first-child {\n    animation: loadingPulse 1.5s infinite linear;\n  }\n  & div:nth-child(2) {\n    animation: loadingPulse 1.5s 0.15s infinite linear;\n  }\n  & div:nth-child(3) {\n    animation: loadingPulse 1.5s 0.3s infinite linear;\n  }\n  & div:nth-child(4) {\n    animation: loadingPulse 1.5s 0.45s infinite linear;\n  }\n  & div:last-child {\n    animation: loadingPulse 1.5s 0.6s infinite linear;\n  }\n"], ["\n  overflow: visible;\n  height: 50px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  flex-direction: row;\n  width: 250px;\n  &:focus {\n    outline: 1px solid ", ";\n  }\n  @keyframes loadingPulse {\n    0% {\n      transform: scale(1);\n    }\n    10% {\n      transform: scale(1.5);\n    }\n    20% {\n      transform: scale(2);\n    }\n    30% {\n      transform: scale(1.5);\n    }\n    40% {\n      transform: scale(1);\n    }\n  }\n  & div:first-child {\n    animation: loadingPulse 1.5s infinite linear;\n  }\n  & div:nth-child(2) {\n    animation: loadingPulse 1.5s 0.15s infinite linear;\n  }\n  & div:nth-child(3) {\n    animation: loadingPulse 1.5s 0.3s infinite linear;\n  }\n  & div:nth-child(4) {\n    animation: loadingPulse 1.5s 0.45s infinite linear;\n  }\n  & div:last-child {\n    animation: loadingPulse 1.5s 0.6s infinite linear;\n  }\n"])), primaryShade(6));
var Dot = styled.div.withConfig({
  displayName: "Loader__Dot"
})(templateObject_2$u || (templateObject_2$u = __makeTemplateObject(["\n  height: ", ";\n  width: ", ";\n  border-radius: 100%;\n  margin: 0 ", ";\n  background: ", ";\n"], ["\n  height: ", ";\n  width: ", ";\n  border-radius: 100%;\n  margin: 0 ", ";\n  background: ", ";\n"])), function (props) {
  return props.size + "px";
}, function (props) {
  return props.size + "px";
}, function (props) {
  return props.size * (BASE_DOT_MARGIN / BASE_DOT_SIZE) + "px";
}, function (props) {
  return props.theme.vstui.color.primary.getShade(props.bgColor);
});

var DotLoader = function (_a) {
  var _b = _a.size,
      size = _b === void 0 ? 18 : _b,
      _c = _a.takeFocus,
      takeFocus = _c === void 0 ? false : _c;
  var intl = useIntl();
  var dotColors = [2, 3, 4, 6, 7];
  var containerRef = useRef(null);
  useEffect(function () {
    var _a;

    if (takeFocus) {
      (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }, [takeFocus]);
  return /*#__PURE__*/React.createElement(Container, {
    ref: containerRef,
    tabIndex: takeFocus ? 0 : undefined,
    "aria-label": intl.formatMessage(messagesBase.loading)
  }, dotColors.map(function (c) {
    return /*#__PURE__*/React.createElement(Dot, {
      key: "dot " + c,
      bgColor: c,
      size: size
    });
  }));
};

var Loader = DotLoader;
var templateObject_1$E, templateObject_2$u;

var OuterContainer = styled.div.withConfig({
  displayName: "CenteredLoader__OuterContainer"
})(templateObject_2$t || (templateObject_2$t = __makeTemplateObject(["\n  display: flex;\n  width: 100%;\n  justify-content: center;\n  ", ";\n"], ["\n  display: flex;\n  width: 100%;\n  justify-content: center;\n  ", ";\n"])), function (_a) {
  var vertCenter = _a.vertCenter;
  return vertCenter && css(templateObject_1$D || (templateObject_1$D = __makeTemplateObject(["\n      align-items: center;\n      height: 100%;\n    "], ["\n      align-items: center;\n      height: 100%;\n    "])));
});

var CenteredLoader = function (props) {
  return /*#__PURE__*/React.createElement(OuterContainer, {
    vertCenter: props.vertCenter,
    className: props.className
  }, /*#__PURE__*/React.createElement(Loader, __assign({}, props)));
};
var templateObject_1$D, templateObject_2$t;

var Styled$v = {
  Background: styled.div.withConfig({
    displayName: "ModalBlock__Background"
  })(templateObject_1$C || (templateObject_1$C = __makeTemplateObject(["\n    position: absolute;\n    left: 0;\n    top: 0;\n    width: 100%;\n    height: 100%;\n    z-index: 999;\n  "], ["\n    position: absolute;\n    left: 0;\n    top: 0;\n    width: 100%;\n    height: 100%;\n    z-index: 999;\n  "])))
};

var ModalBlock = function (_a) {
  var blocked = _a.blocked;

  var _b = useState(false),
      showLoader = _b[0],
      setShowLoader = _b[1];

  useEffect(function () {
    setShowLoader(false);

    if (blocked) {
      setTimeout(function () {
        setShowLoader(true);
      }, 750);
    }
  }, [blocked]);

  if (!blocked) {
    return null;
  }

  return /*#__PURE__*/React.createElement(Styled$v.Background, null, showLoader && /*#__PURE__*/React.createElement(CenteredLoader, {
    vertCenter: true
  }));
};
var templateObject_1$C;

var Notifications = function (_a) {
  var open = _a.open,
      _b = _a.text,
      _c = _b === void 0 ? {
    title: "",
    description: ""
  } : _b,
      title = _c.title,
      description = _c.description,
      detail = _c.detail;

  var extendedDescription = "" + description + (detail ? " " + detail : "");
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Toast, {
    open: open,
    toastTitleText: title,
    toastDescriptionText: extendedDescription,
    toastType: "error"
  }));
};

var QuestionNotifications = function (_a) {
  var systemError = _a.systemError;
  var intl = useIntl();
  var text = useMemo(function () {
    if ((systemError === null || systemError === void 0 ? void 0 : systemError.type) == QuestionSystemErrorType.EvaluationError) {
      return {
        title: intl.formatMessage(messagesErrors.evaluateErrorTitle),
        description: intl.formatMessage(messagesErrors.evaluateErrorDescription),
        detail: systemError.detail
      };
    } else if ((systemError === null || systemError === void 0 ? void 0 : systemError.type) == QuestionSystemErrorType.RevealAnswerError) {
      return {
        title: intl.formatMessage(messagesErrors.revealAnswerErrorTitle),
        description: intl.formatMessage(messagesErrors.revealAnswerErrorDescription),
        detail: systemError.detail
      };
    } else if ((systemError === null || systemError === void 0 ? void 0 : systemError.type) == QuestionSystemErrorType.ImprovementFeedbackError) {
      return {
        title: intl.formatMessage(messagesErrors.evaluateErrorTitle),
        description: intl.formatMessage(messagesErrors.improvementFeedbackErrorDescription),
        detail: systemError.detail
      };
    }

    return;
  }, [intl, systemError === null || systemError === void 0 ? void 0 : systemError.detail, systemError === null || systemError === void 0 ? void 0 : systemError.type]);
  return /*#__PURE__*/React.createElement(Notifications, {
    open: !!systemError,
    text: text
  });
};

var ThumbsUpIcon = function (_a) {
  var selected = _a.selected,
      theme = _a.theme,
      _b = _a.down,
      down = _b === void 0 ? false : _b;
  var style = down ? {
    transform: "rotate(180deg)",
    transformOrigin: "center"
  } : undefined;
  return /*#__PURE__*/React.createElement("svg", {
    style: style,
    width: "15",
    height: "15",
    xmlns: "http://www.w3.org/2000/svg",
    focusable: "false"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M2.357 12.164c.421.03 1.036.086 1.577.19.436.084.636.155.94.262.306.109.687.243 1.448.447 1.053.283 2.726.252 3.766.077 1.001-.168 1.5-.76 1.551-1.271a.951.951 0 00-.032-.368c.336-.157.639-.435.708-.892.052-.358-.011-.639-.122-.848.22-.205.4-.508.4-.925 0-.363-.135-.641-.315-.844.138-.174.236-.411.236-.729 0-.86-.712-1.126-1.016-1.239-.549-.205-2.317-.28-2.924-.302-.027-.076-.026-.197.097-.366.067-.105 1.65-2.563.432-3.924-.082-.101-.52-.599-1.053-.599l-.014.001c-.264.003-.5.129-.681.362l-.082.105-.007.135c0 .019-.105 1.79-1.031 2.721-.804.809-1.501 1.743-2.116 2.567-.172.23-.34.455-.506.67l-1.224.037c-.427.013-.762.378-.762.83v3.074c0 .435.32.8.73.83z",
    stroke: "#757575",
    fill: selected ? theme.vstui.color.primary.getShade(6) : "none",
    fillRule: "evenodd"
  }));
};

var ThumbsDownIconBase = function (props) {
  return /*#__PURE__*/React.createElement(ThumbsUpIcon, __assign({}, props, {
    down: true
  }));
};

var ThumbsUpIcon$1 = withTheme(ThumbsUpIcon);
var ThumbsDownIcon = withTheme(ThumbsDownIconBase);

var issues = [{
  type: QuestionImprovementIssueType.CorrectAnswerWrong,
  message: messagesQuestionImprovementDialog.issueWrongAnswer
}, {
  type: QuestionImprovementIssueType.UserAnswerAlsoCorrect,
  message: messagesQuestionImprovementDialog.issueMineAlsoCorrect
}, {
  type: QuestionImprovementIssueType.QuestionUnclear,
  message: messagesQuestionImprovementDialog.issueQuestionUnclear
}, {
  type: QuestionImprovementIssueType.QuestionNotRelevant,
  message: messagesQuestionImprovementDialog.issueQuestionNotRelevant
}];
var Styled$u = {
  CheckListContainer: styled.fieldset.withConfig({
    displayName: "QuestionImprovementDialog__CheckListContainer"
  })(templateObject_1$B || (templateObject_1$B = __makeTemplateObject(["\n    border: none;\n    padding: 24px 0 0 0;\n  "], ["\n    border: none;\n    padding: 24px 0 0 0;\n  "]))),
  InfoText: styled.div.withConfig({
    displayName: "QuestionImprovementDialog__InfoText"
  })(templateObject_2$s || (templateObject_2$s = __makeTemplateObject(["\n    font-size: 1.4rem;\n    color: ", ";\n  "], ["\n    font-size: 1.4rem;\n    color: ", ";\n  "])), cloud.getShade(6))
};

var QuestionImprovementDialog = function (_a) {
  var open = _a.open,
      onFeedback = _a.onFeedback;
  var intl = useIntl();

  var _b = useState(""),
      otherFeedback = _b[0],
      setOtherFeedback = _b[1];

  var _c = useState([]),
      selectedIssues = _c[0],
      setSelectedIssues = _c[1];

  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    requireInteraction: false,
    primaryActionButton: {
      label: intl.formatMessage(__assign({}, messagesBase.submit)),
      onClick: function () {
        onFeedback({
          issues: selectedIssues,
          otherFeedback: otherFeedback
        });
      }
    },
    secondaryActionButton: {
      label: intl.formatMessage(__assign({}, messagesBase.cancel)),
      onClick: function () {
        onFeedback();
      }
    },
    onRequestClose: function () {
      return onFeedback();
    },
    size: "large",
    title: intl.formatMessage(__assign({}, messagesQuestionImprovementDialog.title))
  }, /*#__PURE__*/React.createElement(Styled$u.InfoText, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestionImprovementDialog.information))), /*#__PURE__*/React.createElement(Styled$u.CheckListContainer, null, issues.map(function (issue) {
    return /*#__PURE__*/React.createElement(Checkbox, {
      key: issue.type,
      checked: selectedIssues.indexOf(issue.type) !== -1,
      onChange: function (e) {
        if (e.target.checked) {
          setSelectedIssues(__spreadArray(__spreadArray([], selectedIssues, true), [issue.type], false));
        } else {
          setSelectedIssues(selectedIssues.filter(function (selectedIssue) {
            return selectedIssue !== issue.type;
          }));
        }
      },
      label: intl.formatMessage(issue.message),
      fullWidth: true
    });
  })), /*#__PURE__*/React.createElement(TextField, {
    label: intl.formatMessage(__assign({}, messagesQuestionImprovementDialog.otherFeedback)),
    type: "text",
    onChange: function (e) {
      return setOtherFeedback(e.target.value);
    },
    value: otherFeedback,
    multiline: true,
    fullWidth: true,
    placeholder: intl.formatMessage(__assign({}, messagesQuestionImprovementDialog.feedbackPlaceholder))
  }));
};
var templateObject_1$B, templateObject_2$s;

var useEventListener = function (eventName, handler, ref) {
  var element = ref ? ref.current : window;
  var savedHandler = useRef(handler);
  useEffect(function () {
    savedHandler.current = handler;
  }, [handler]);
  useEffect(function () {
    if (!element || !element.addEventListener) {
      return;
    }

    var eventListener = function (event) {
      var _a;

      return (_a = savedHandler.current) === null || _a === void 0 ? void 0 : _a.call(savedHandler, event);
    };

    element.addEventListener(eventName, eventListener);
    return function () {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
};

var fadeInWithDelay = keyframes(templateObject_1$A || (templateObject_1$A = __makeTemplateObject(["\n  from {\n    opacity: 0;\n  }\n  50%  { opacity: 0 }   \n  to {\n    opacity: 1;\n  }\n"], ["\n  from {\n    opacity: 0;\n  }\n  50%  { opacity: 0 }   \n  to {\n    opacity: 1;\n  }\n"])));
var Styled$t = {
  Container: styled.div.withConfig({
    displayName: "QuestionImprovement__Container"
  })(templateObject_4$h || (templateObject_4$h = __makeTemplateObject(["\n    margin-top: 16px;\n    display: flex;\n    align-items: center;\n    justify-content: flex-end;    \n    padding: 0 16px 16px;\n    text-align: end;\n    color: ", "\n    font-weight: 500;\n    font-size: 1.4rem;\n    ", ";    \n    ", ";\n  "], ["\n    margin-top: 16px;\n    display: flex;\n    align-items: center;\n    justify-content: flex-end;    \n    padding: 0 16px 16px;\n    text-align: end;\n    color: ", "\n    font-weight: 500;\n    font-size: 1.4rem;\n    ", ";    \n    ", ";\n  "])), cloud.getShade(6), function (_a) {
    var animateIn = _a.animateIn;
    return animateIn && css(templateObject_2$r || (templateObject_2$r = __makeTemplateObject(["\n        animation: ", " 1.25s ease;\n      "], ["\n        animation: ", " 1.25s ease;\n      "])), fadeInWithDelay);
  }, function (_a) {
    var fadeOut = _a.fadeOut;
    return fadeOut && css(templateObject_3$k || (templateObject_3$k = __makeTemplateObject(["\n        pointer-events: none;\n        opacity: 0;\n        transition: opacity 0.75s ease 0.5s;\n      "], ["\n        pointer-events: none;\n        opacity: 0;\n        transition: opacity 0.75s ease 0.5s;\n      "])));
  }),
  Caption: styled.span.withConfig({
    displayName: "QuestionImprovement__Caption"
  })(templateObject_5$c || (templateObject_5$c = __makeTemplateObject(["\n    margin-right: 4px;\n  "], ["\n    margin-right: 4px;\n  "]))),
  FeedbackButton: styled.button.withConfig({
    displayName: "QuestionImprovement__FeedbackButton"
  })(templateObject_6$a || (templateObject_6$a = __makeTemplateObject(["\n    border: none;\n    background-color: transparent;\n    cursor: pointer;\n    padding: 2px 4px;\n    border-radius: 4px;\n    &:focus {\n      outline: none;\n      box-shadow: 0 0 0 2px ", ";\n    }\n  "], ["\n    border: none;\n    background-color: transparent;\n    cursor: pointer;\n    padding: 2px 4px;\n    border-radius: 4px;\n    &:focus {\n      outline: none;\n      box-shadow: 0 0 0 2px ", ";\n    }\n  "])), primaryShade(6))
};

var QuestionImprovement = function (_a) {
  var appearEffect = _a.appearEffect,
      onStatus = _a.onStatus,
      onAppearStart = _a.onAppearStart,
      scrollWhenAppearing = _a.scrollWhenAppearing;
  var intl = useIntl();
  var ref = useRef(null);

  var _b = useState(false),
      feedbackDialogOpen = _b[0],
      setFeedbackDialogOpen = _b[1];

  var _c = useState(undefined),
      feedback = _c[0],
      setFeedback = _c[1];

  var _d = useState(false),
      complete = _d[0],
      setComplete = _d[1];

  var _e = useState(null),
      result = _e[0],
      setResult = _e[1];

  var _f = useState(false),
      fadeIn = _f[0],
      setFadeIn = _f[1];

  var _g = useState(false),
      fadeOut = _g[0],
      setFadeOut = _g[1];

  var handleOnFadeOutEnd = useCallback(function () {
    if (fadeOut && !complete) {
      setComplete(true); // go out of document flow
    }
  }, [fadeOut, complete]);
  useEventListener("transitionend", handleOnFadeOutEnd, ref);

  if (appearEffect && !fadeIn) {
    setFadeIn(true);
  }

  useEffect(function () {
    if (complete) {
      if (result === null) {
        throw "Question improvement expected a result but got null";
      }

      onStatus({
        flag: result,
        feedback: feedback
      });
    }
  }, [complete, feedback, onStatus, result]);

  if (complete) {
    return null;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styled$t.Container, {
    ref: ref,
    animateIn: fadeIn,
    fadeOut: fadeOut,
    onAnimationStart: function () {
      var _a;

      onAppearStart === null || onAppearStart === void 0 ? void 0 : onAppearStart(ref.current);

      if (ref.current && scrollWhenAppearing) {
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
          behavior: "smooth"
        });
      }
    }
  }, /*#__PURE__*/React.createElement(Styled$t.Caption, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestionImprovement.prompt))), /*#__PURE__*/React.createElement(Styled$t.FeedbackButton, {
    "aria-label": intl.formatMessage(__assign({}, messagesQuestionImprovement.thumbsUp)),
    onClick: function () {
      setResult(QuestionImprovementFlag.FlaggedGood);
      setFadeOut(true);
    }
  }, /*#__PURE__*/React.createElement(ThumbsUpIcon$1, {
    selected: result === QuestionImprovementFlag.FlaggedGood
  })), /*#__PURE__*/React.createElement(Styled$t.FeedbackButton, {
    "aria-label": intl.formatMessage(__assign({}, messagesQuestionImprovement.thumbsDown)),
    onClick: function () {
      setFeedbackDialogOpen(true);
    }
  }, /*#__PURE__*/React.createElement(ThumbsDownIcon, {
    selected: result === QuestionImprovementFlag.FlaggedBad
  }))), feedbackDialogOpen && /*#__PURE__*/React.createElement(QuestionImprovementDialog, {
    open: feedbackDialogOpen,
    onFeedback: function (feedback) {
      setFeedbackDialogOpen(false);

      if (feedback) {
        setFeedback(feedback);
      }

      setResult(QuestionImprovementFlag.FlaggedBad);
      setFadeOut(true);
    }
  }));
};
var templateObject_1$A, templateObject_2$r, templateObject_3$k, templateObject_4$h, templateObject_5$c, templateObject_6$a;

var Styled$s = {
  QuestionDiv: styled.div.withConfig({
    displayName: "QuestionContainer__QuestionDiv"
  })(templateObject_4$g || (templateObject_4$g = __makeTemplateObject(["\n    background: #ffffff;\n    & ul,\n    ol {\n      // if question is part of question group the wrapping list tag caused hollow bullets\n      list-style-type: disc;\n    }\n    ", ";\n    ", ";\n    ", ";\n  "], ["\n    background: #ffffff;\n    & ul,\n    ol {\n      // if question is part of question group the wrapping list tag caused hollow bullets\n      list-style-type: disc;\n    }\n    ", ";\n    ", ";\n    ", ";\n  "])), function (_a) {
    var standalone = _a.standalone;
    return standalone && css(templateObject_1$z || (templateObject_1$z = __makeTemplateObject(["\n        height: 100%;\n        display: flex;\n        flex-direction: column;\n      "], ["\n        height: 100%;\n        display: flex;\n        flex-direction: column;\n      "])));
  }, function (_a) {
    var border = _a.border;
    return border === QuestionBorder.HaloShadow && css(templateObject_2$q || (templateObject_2$q = __makeTemplateObject(["\n        box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);\n        border-radius: 4px;\n        font-weight: 500;\n        margin: 8px;\n      "], ["\n        box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);\n        border-radius: 4px;\n        font-weight: 500;\n        margin: 8px;\n      "])));
  }, function (_a) {
    var standalone = _a.standalone,
        _b = _a.needsDivider,
        needsDivider = _b === void 0 ? true : _b;
    return needsDivider && !standalone && css(templateObject_3$j || (templateObject_3$j = __makeTemplateObject(["\n        :not(:last-child):after {\n          content: \"\";\n          display: inline-block;\n          width: calc(100% - 32px);\n          margin-left: 16px;\n          margin-top: 16px;\n          margin-bottom: 16px;\n          border-bottom: 1px solid ", ";\n        }\n      "], ["\n        :not(:last-child):after {\n          content: \"\";\n          display: inline-block;\n          width: calc(100% - 32px);\n          margin-left: 16px;\n          margin-top: 16px;\n          margin-bottom: 16px;\n          border-bottom: 1px solid ", ";\n        }\n      "])), cloud.getShade(4));
  }),
  PrologueWrapper: styled.div.withConfig({
    displayName: "QuestionContainer__PrologueWrapper"
  })(templateObject_6$9 || (templateObject_6$9 = __makeTemplateObject(["\n    ", ";\n    ", ";\n  "], ["\n    ", ";\n    ", ";\n  "])), function (_a) {
    var vertMargin = _a.vertMargin;
    return vertMargin ? "margin-top: 16px" : "";
  }, function (_a) {
    var isFixed = _a.isFixed;
    return isFixed && css(templateObject_5$b || (templateObject_5$b = __makeTemplateObject(["\n        position: sticky;\n        top: 0;\n        z-index: 1;\n      "], ["\n        position: sticky;\n        top: 0;\n        z-index: 1;\n      "])));
  }),
  ScrollingFrame: styled.div.withConfig({
    displayName: "QuestionContainer__ScrollingFrame"
  })(templateObject_8$3 || (templateObject_8$3 = __makeTemplateObject(["\n    ", ";\n  "], ["\n    ", ";\n  "])), function (_a) {
    var needInternalScroll = _a.needInternalScroll;
    return needInternalScroll && css(templateObject_7$6 || (templateObject_7$6 = __makeTemplateObject(["\n        flex: 1;\n        overflow-y: auto;\n        overflow-x: hidden;\n      "], ["\n        flex: 1;\n        overflow-y: auto;\n        overflow-x: hidden;\n      "])));
  }),
  ScrollingContent: styled.div.withConfig({
    displayName: "QuestionContainer__ScrollingContent"
  })(templateObject_9$1 || (templateObject_9$1 = __makeTemplateObject(["\n    padding-top: ", ";\n    margin: ", " 16px;\n    position: relative;\n  "], ["\n    padding-top: ", ";\n    margin: ", " 16px;\n    position: relative;\n  "])), function (_a) {
    var isGapFill = _a.isGapFill;
    return isGapFill ? "9px" : "0";
  }, function (_a) {
    var needVertMargin = _a.needVertMargin;
    return needVertMargin ? "16px" : "0";
  })
};

var QuestionContainer = function (_a) {
  var id = _a.id,
      className = _a.className,
      mode = _a.mode,
      _b = _a.border,
      border = _b === void 0 ? QuestionBorder.None : _b,
      questionIndex = _a.questionIndex,
      siblingQuestionCount = _a.siblingQuestionCount,
      controlsVisible = _a.controlsVisible,
      _c = _a.secondaryControlsVisible,
      secondaryControlsVisible = _c === void 0 ? true : _c,
      controlsMode = _a.controlsMode,
      feedbackNumAnswers = _a.feedbackNumAnswers,
      feedbackWrongAnswersIndicated = _a.feedbackWrongAnswersIndicated,
      systemFeedbackMessage = _a.systemFeedbackMessage,
      feedbackMessage = _a.feedbackMessage,
      feedbackPadding = _a.feedbackPadding,
      feedbackNeutral = _a.feedbackNeutral,
      errorMsgDisplayID = _a.errorMsgDisplayID,
      questionTypeLabel = _a.questionTypeLabel,
      _d = _a.state,
      submitted = _d.submitted,
      submitActive = _d.submitActive,
      answerCorrect = _d.answerCorrect,
      validationErrorMsg = _d.validationErrorMsg,
      revealAnswer = _d.revealAnswer,
      ariaLiveMessage = _d.ariaLiveMessage,
      ariaLiveMode = _d.ariaLiveMode,
      focusQuestionStart = _d.focusQuestionStart,
      locked = _d.locked,
      systemError = _d.systemError,
      improvementStatus = _d.improvementStatus,
      loaded = _d.loaded,
      numSubmitRequestedAttempts = _d.numSubmitRequestedAttempts,
      dispatch = _a.dispatch,
      getResetQuestionState = _a.getResetQuestionState,
      _e = _a.fixedPrologue,
      fixedPrologue = _e === void 0 ? false : _e,
      prologue = _a.prologue,
      body = _a.body,
      epilogue = _a.epilogue,
      events = _a.events,
      isGapFill = _a.isGapFill;
  var intl = useIntl();
  var prevSubmitted = usePrevious(submitted);
  var ctlsRef = React.useRef(null);
  var isStandalone = mode === QuestionMode.Standalone || mode === QuestionMode.StandaloneInGroup;
  var showPrimaryControls = mode === QuestionMode.Standalone && !submitted || mode === QuestionMode.StandaloneInGroup;
  var showSecondaryControls = isStandalone && submitted && secondaryControlsVisible;
  var isPartOfTutorial = mode === QuestionMode.Tutorial || mode === QuestionMode.TutorialFollowup; // this is for a11y focus

  var prevSubmitRequestAttempts = usePrevious(numSubmitRequestedAttempts);
  var validationChange = useMemo(function () {
    return numSubmitRequestedAttempts !== prevSubmitRequestAttempts;
  }, [numSubmitRequestedAttempts, prevSubmitRequestAttempts]);

  var _f = useState(false),
      validationFocus = _f[0],
      setValidationFocus = _f[1];

  useEffect(function () {
    if (validationChange) {
      setValidationFocus(true);
    }
  }, [validationChange]);
  useEffect(function () {
    if (validationFocus) {
      setValidationFocus(false);
    }
  }, [validationFocus]);

  if (systemError) {
    defer(function () {
      return dispatch(questionClearSystemError());
    });
  }

  useEffect(function () {
    var _a;

    if (ctlsRef.current) {
      (_a = events === null || events === void 0 ? void 0 : events.onSetControlsSize) === null || _a === void 0 ? void 0 : _a.call(events, id, ctlsRef.current.offsetHeight);
    }
  }, [events, id]);
  return /*#__PURE__*/React.createElement(Styled$s.QuestionDiv, {
    key: id,
    className: className,
    border: border,
    standalone: isStandalone
  }, /*#__PURE__*/React.createElement(AriaLiveRegion, {
    mode: ariaLiveMode,
    onClear: function () {
      dispatch(questionAriaLiveMessage(""));
    }
  }, ariaLiveMessage), /*#__PURE__*/React.createElement(A11y, {
    focused: focusQuestionStart
  }, questionTypeLabel), prologue && fixedPrologue && /*#__PURE__*/React.createElement(Styled$s.PrologueWrapper, {
    vertMargin: isStandalone,
    isFixed: true
  }, prologue), /*#__PURE__*/React.createElement(Styled$s.ScrollingFrame, {
    needInternalScroll: mode === QuestionMode.Standalone
  }, /*#__PURE__*/React.createElement(Styled$s.ScrollingContent, {
    isGapFill: isGapFill,
    needVertMargin: isStandalone && !(prologue && fixedPrologue)
  }, prologue && !fixedPrologue && /*#__PURE__*/React.createElement(Styled$s.PrologueWrapper, {
    vertMargin: false,
    isFixed: false
  }, prologue), validationErrorMsg && /*#__PURE__*/React.createElement(Notification, {
    id: errorMsgDisplayID,
    message: intl.formatMessage(validationErrorMsg.descriptor),
    notificationIcon: NotificationIconType.ExclamationCircle,
    focused: validationFocus,
    withoutMarginTop: isGapFill,
    key: errorMsgDisplayID
  }), body, submitted && /*#__PURE__*/React.createElement(AnswerFeedback, {
    correct: feedbackNeutral || answerCorrect === undefined ? undetermined : answerCorrect,
    numAnswers: feedbackNumAnswers,
    wrongAnswersIndicated: feedbackWrongAnswersIndicated,
    message: systemFeedbackMessage,
    content: feedbackMessage,
    padding: feedbackPadding,
    focused: loaded && !prevSubmitted && submitted && mode !== QuestionMode.Child
  })), showSecondaryControls && /*#__PURE__*/React.createElement(QuestionControls, {
    type: ControlsType.Secondary,
    align: ControlsAlignment.Left,
    submitted: submitted,
    answerCorrect: answerCorrect,
    revealAnswer: revealAnswer,
    dispatch: dispatch,
    getResetQuestionState: getResetQuestionState,
    showReveal: isStandalone && answerCorrect !== null,
    onRevealAnswer: function () {
      var _a;

      return (_a = events === null || events === void 0 ? void 0 : events.onRevealAnswer) === null || _a === void 0 ? void 0 : _a.call(events, id);
    },
    onReset: function () {
      var _a;

      return (_a = events === null || events === void 0 ? void 0 : events.onReset) === null || _a === void 0 ? void 0 : _a.call(events, id);
    }
  }), epilogue || null, submitted && !isPartOfTutorial && !improvementStatus && answerCorrect !== undefined && /*#__PURE__*/React.createElement(QuestionImprovement, {
    status: improvementStatus,
    appearEffect: submitActive,
    onStatus: function (status) {
      dispatch(questionSetImprovementStatus(status));
    },
    scrollWhenAppearing: false
  })), showPrimaryControls && /*#__PURE__*/React.createElement(QuestionControls, {
    ref: ctlsRef,
    type: ControlsType.Primary,
    align: ControlsAlignment.Center,
    visible: controlsVisible,
    mode: controlsMode,
    submitted: submitted,
    answerCorrect: answerCorrect,
    revealAnswer: revealAnswer,
    dockToBottom: mode === QuestionMode.StandaloneInGroup,
    dispatch: dispatch,
    showNextQuestion: isStandalone && questionIndex !== undefined && siblingQuestionCount !== undefined && questionIndex < siblingQuestionCount - 1,
    onNextQuestion: function () {
      var _a;

      (_a = events === null || events === void 0 ? void 0 : events.onNextQuestion) === null || _a === void 0 ? void 0 : _a.call(events, id);
    },
    onDone: function () {
      var _a;

      (_a = events === null || events === void 0 ? void 0 : events.onDone) === null || _a === void 0 ? void 0 : _a.call(events, id);
    }
  }), /*#__PURE__*/React.createElement(QuestionNotifications, {
    systemError: systemError
  }), /*#__PURE__*/React.createElement(ModalBlock, {
    blocked: locked
  }));
};
var templateObject_1$z, templateObject_2$q, templateObject_3$j, templateObject_4$g, templateObject_5$b, templateObject_6$9, templateObject_7$6, templateObject_8$3, templateObject_9$1;

var FeedbackIncorrectIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "20",
    height: "20",
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("circle", {
    fill: "#E71D3A",
    cx: "10",
    cy: "10",
    r: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11.414 10l3.293-3.293a.999.999 0 10-1.414-1.414L10 8.586 6.707 5.293a.999.999 0 10-1.414 1.414L8.586 10l-3.293 3.293a.999.999 0 101.414 1.414L10 11.414l3.293 3.293a.997.997 0 001.414 0 .999.999 0 000-1.414L11.414 10z",
    fill: "#FFF"
  })));
};

var FeedbackCorrectIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "20",
    height: "20",
    viewBox: "0 0 18 18",
    "aria-hidden": "true",
    focusable: "false",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "#048A04",
    d: "M13.706 7.466l-5.24 5.24a1 1 0 0 1-1.416 0L4.281 9.939a1 1 0 0 1 1.414-1.414l2.063 2.062 4.534-4.535a1 1 0 1 1 1.414 1.415M9 0a9 9 0 0 0-9 9 9 9 0 0 0 9 9 9 9 0 0 0 9-9 9 9 0 0 0-9-9"
  }));
};

var FeedbackCorrectRevealedIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "20",
    height: "20",
    viewBox: "0 0 18 18",
    "aria-hidden": "true",
    focusable: "false",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "#048A04",
    d: "M9 16c-3.859 0-7-3.141-7-7s3.141-7 7-7 7 3.141 7 7-3.141 7-7 7M9 0a9 9 0 0 1 9 9 9 9 0 0 1-9 9 9 9 0 0 1-9-9 9 9 0 0 1 9-9zm4.706 6.05a.999.999 0 0 0-1.414 0l-4.534 4.536-2.063-2.062a.999.999 0 1 0-1.414 1.414l2.77 2.769a1 1 0 0 0 1.414 0l5.241-5.241a1 1 0 0 0 0-1.415z",
    fillRule: "evenodd"
  }));
};

var iconMargin = 16;
var radioStyle = css(templateObject_1$y || (templateObject_1$y = __makeTemplateObject(["\n  position: absolute;\n  display: inline-block;\n  content: \"\";\n  width: 1.6rem;\n  height: 1.6rem;\n  border-radius: 100%;\n  border: 1px solid rgba(0, 0, 0, 0.43);\n  box-sizing: border-box;\n  margin-left: ", "px;\n  background-color: white;\n  margin-top: 3px;\n"], ["\n  position: absolute;\n  display: inline-block;\n  content: \"\";\n  width: 1.6rem;\n  height: 1.6rem;\n  border-radius: 100%;\n  border: 1px solid rgba(0, 0, 0, 0.43);\n  box-sizing: border-box;\n  margin-left: ", "px;\n  background-color: white;\n  margin-top: 3px;\n"])), -2 * iconMargin);
var Answer = styled.label.withConfig({
  displayName: "MultipleChoiceAnswer__Answer"
})(templateObject_3$i || (templateObject_3$i = __makeTemplateObject(["\n  ", ";\n  position: relative;\n  display: block;\n  font-weight: 400;\n  padding: 16px;\n  margin-top: 16px;\n  cursor: ", ";\n  &:hover {\n    ", ";\n  }\n  ", "\n  input {\n    border: 0;\n    clip: rect(0 0 0 0);\n    height: 1px;\n    overflow: hidden;\n    padding: 0;\n    position: absolute;\n    width: 1px;\n  }\n  input + span::before {\n    ", "\n  }\n  input:checked + span::before {\n    border: 5px solid ", ";\n  }\n  input:focus + span::before {\n    box-shadow: 0 0 0 1px ", ", 0 0 0 6px ", ";\n  }\n  ", "\n  svg {\n    margin-top: 1px;\n    position: absolute;\n  }\n  > span {\n    padding-left: ", "px;\n    vertical-align: middle;\n    display: inline-block;\n    line-height: 1.5;\n  }\n"], ["\n  ", ";\n  position: relative;\n  display: block;\n  font-weight: 400;\n  padding: 16px;\n  margin-top: 16px;\n  cursor: ", ";\n  &:hover {\n    ", ";\n  }\n  ", "\n  input {\n    border: 0;\n    clip: rect(0 0 0 0);\n    height: 1px;\n    overflow: hidden;\n    padding: 0;\n    position: absolute;\n    width: 1px;\n  }\n  input + span::before {\n    ", "\n  }\n  input:checked + span::before {\n    border: 5px solid ", ";\n  }\n  input:focus + span::before {\n    box-shadow: 0 0 0 1px ", ", 0 0 0 6px ", ";\n  }\n  ", "\n  svg {\n    margin-top: 1px;\n    position: absolute;\n  }\n  > span {\n    padding-left: ", "px;\n    vertical-align: middle;\n    display: inline-block;\n    line-height: 1.5;\n  }\n"])), function (_a) {
  var correct = _a.correct,
      submitted = _a.submitted,
      checked = _a.checked,
      revealed = _a.revealed;
  return !submitted ? boxStyle : correct === true && checked ? correctBoxStyle : revealed && correct ? revealedBoxStyle : correct === false && checked ? incorrectBoxStyle : correct === null && checked ? surveyBoxStyle : boxStyle;
}, function (_a) {
  var submitted = _a.submitted;
  return submitted ? "auto" : "pointer";
}, function (_a) {
  var submitted = _a.submitted;
  return !submitted ? "background-color: " + cloud.getShade(1) + ";" : "";
}, function (_a) {
  var checked = _a.checked,
      submitted = _a.submitted;
  return checked && !submitted ? "background-color: " + cloud.getShade(1) + ";" : "";
}, radioStyle, primaryShade(6), ninja.getShade(11), primaryShade(3), function (_a) {
  var checked = _a.checked,
      submitted = _a.submitted,
      correct = _a.correct;
  return checked && submitted && correct === null && css(templateObject_2$p || (templateObject_2$p = __makeTemplateObject(["\n      span::before {\n        ", ";\n        border: 5px solid ", ";\n      }\n    "], ["\n      span::before {\n        ", ";\n        border: 5px solid ", ";\n      }\n    "])), radioStyle, primaryShade(6));
}, iconMargin * 2);

var MultipleChoiceAnswer = function (_a) {
  var text = _a.text,
      value = _a.value,
      groupID = _a.groupID,
      answerID = _a.answerID,
      index = _a.index,
      checked = _a.checked,
      submitted = _a.submitted,
      disabled = _a.disabled,
      correct = _a.correct,
      revealed = _a.revealed,
      errorMsg = _a.errorMsg,
      errorMsgDisplayID = _a.errorMsgDisplayID,
      onSelectAnswer = _a.onSelectAnswer;
  var intl = useIntl();
  var a11yFeedback = "";

  if (submitted && revealed && correct) {
    a11yFeedback = intl.formatMessage(messagesQuestion.feedbackItemRevealedCorrectAnswer);
  } else if (submitted && checked) {
    a11yFeedback = correct ? intl.formatMessage(messagesQuestion.feedbackItemCorrectAnswer) : intl.formatMessage(messagesQuestion.feedbackItemIncorrectAnswer);
  }

  return /*#__PURE__*/React.createElement(Answer, {
    htmlFor: answerID,
    correct: correct,
    checked: checked,
    submitted: submitted,
    revealed: revealed
  }, submitted && revealed && correct ? /*#__PURE__*/React.createElement(FeedbackCorrectRevealedIcon, null) : !submitted || !checked ? /*#__PURE__*/React.createElement("input", {
    type: "radio",
    name: groupID,
    id: answerID,
    checked: checked,
    value: value,
    disabled: disabled,
    "aria-invalid": errorMsg ? true : undefined,
    "aria-describedby": errorMsg ? errorMsgDisplayID : undefined,
    onChange: function () {
      onSelectAnswer(value, index);
    }
  }) : correct === true ? /*#__PURE__*/React.createElement(FeedbackCorrectIcon, null) : correct === false ? /*#__PURE__*/React.createElement(FeedbackIncorrectIcon, null) : null, /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: text
    }
  }), a11yFeedback && /*#__PURE__*/React.createElement(A11y, null, a11yFeedback));
};
var templateObject_1$y, templateObject_2$p, templateObject_3$i;

var ProcessTextOption;

(function (ProcessTextOption) {
  /** populate context references as plain text */
  ProcessTextOption[ProcessTextOption["Plain"] = 0] = "Plain";
  /** mark context references as class="answer" so they stand out */

  ProcessTextOption[ProcessTextOption["Inline"] = 1] = "Inline";
})(ProcessTextOption || (ProcessTextOption = {}));

var processText = function (s, context, options) {
  if (options === void 0) {
    options = ProcessTextOption.Inline;
  }

  if (context === null || context === void 0 ? void 0 : context.previousAnswers) {
    return s.replace(/({{.*?}})/g, function (substring) {
      var value;
      var pathParts = substring.slice(2, -2).trim().split(".");

      if (pathParts.pop() === "value") {
        var answerId = pathParts.shift();

        if (answerId) {
          var record = context.previousAnswers[answerId];
          value = get(record.inputVars, pathParts.join("."));
        }
      }

      return value ? options === ProcessTextOption.Inline ? "<span class=\"answer\">" + value + "</span>" : value : substring;
    });
  } else {
    return s;
  }
};
var insertSpanBeforeMarkup = function (markup, span) {
  if (!span) {
    return markup;
  }

  if (markup.trim().search(/<p\s*?>/) === 0) {
    var insert = markup.trim().indexOf(">");
    return markup.substring(0, insert + 1) + span + markup.substring(insert + 1);
  } else {
    return span + markup;
  }
};

var calcMarginOffset = function (fontSize, lineHeight) {
  if (lineHeight === void 0) {
    lineHeight = 1.5;
  } // text with larger line-heights tends to skew start point at top of text
  // divide space between font size and line height to get top margin for negative offset


  return (fontSize * 16 * lineHeight - fontSize * 16) / 2 / 16;
};

var getLineHeight = function (isGapFill) {
  if (isGapFill === void 0) {
    isGapFill = false;
  }

  return isGapFill ? 2.5 : 1.5;
};

var Styled$r = {
  QuestionDiv: styled.div.withConfig({
    displayName: "QuestionText__QuestionDiv"
  })(templateObject_1$x || (templateObject_1$x = __makeTemplateObject(["\n    ", ";\n    font-weight: 500;\n    font-size: 1.6rem;\n    line-height: ", ";\n    & p:first-of-type {\n      margin-top: 0;\n      &:before {\n        content: \"\";\n        display: block;\n        margin-top: -", "rem;\n      }\n    }\n    & p:last-of-type {\n      margin-bottom: 0;\n    }\n  "], ["\n    ", ";\n    font-weight: 500;\n    font-size: 1.6rem;\n    line-height: ", ";\n    & p:first-of-type {\n      margin-top: 0;\n      &:before {\n        content: \"\";\n        display: block;\n        margin-top: -", "rem;\n      }\n    }\n    & p:last-of-type {\n      margin-bottom: 0;\n    }\n  "])), markupHost, function (_a) {
    var isGapFill = _a.isGapFill;
    return getLineHeight(isGapFill);
  }, function (_a) {
    var isGapFill = _a.isGapFill;
    return calcMarginOffset(1.25, getLineHeight(isGapFill));
  })
};
var QuestionText = function (_a) {
  var isGapFill = _a.isGapFill,
      questionIndex = _a.questionIndex,
      text = _a.text,
      children = _a.children;
  var numSpan = questionIndex !== undefined ? "<span>" + (questionIndex + 1) + ". </span>" : null;

  if (children) {
    return /*#__PURE__*/React.createElement(Styled$r.QuestionDiv, {
      isGapFill: isGapFill
    }, numSpan, children);
  } else {
    if (numSpan && text) {
      // inject question number as not using standard list item marker as that by default
      // is block indented but the design has it inline
      text = insertSpanBeforeMarkup(text, numSpan);
    }

    return /*#__PURE__*/React.createElement(Styled$r.QuestionDiv, {
      dangerouslySetInnerHTML: {
        __html: text !== null && text !== void 0 ? text : ""
      }
    });
  }
};
var templateObject_1$x;

/**
 *  a question parent (e.g. QuestionGroup ) can pass in commands
 *  as a way to remote control the question
 */

function useControlledQuestion(questionControlProps, state, dispatch, getDefaultState, preventSubmit) {
  var controlledSubmit = questionControlProps.controlledSubmit,
      controlledReveal = questionControlProps.controlledReveal,
      controlledReset = questionControlProps.controlledReset;
  var submitted = state.submitted,
      revealAnswer = state.revealAnswer;
  useEffect(function () {
    if (controlledSubmit && !submitted && !preventSubmit) {
      dispatch(questionSubmitRequested(true));
    }
  }, [controlledSubmit, dispatch, preventSubmit, submitted]);
  useEffect(function () {
    if (controlledReveal && !revealAnswer) {
      dispatch(questionRevealAnswer(true));
    }
  }, [controlledReveal, dispatch, revealAnswer]);
  useEffect(function () {
    if (controlledReset) {
      dispatch(questionReset(__assign({}, getDefaultState())));
    }
  }, [controlledReset, dispatch, getDefaultState]);
}

var EvaluatedQuestionType;

(function (EvaluatedQuestionType) {
  EvaluatedQuestionType[EvaluatedQuestionType["Normal"] = 0] = "Normal";
  EvaluatedQuestionType[EvaluatedQuestionType["Parent"] = 1] = "Parent";
})(EvaluatedQuestionType || (EvaluatedQuestionType = {}));

var useEvaluatedQuestion = function (evaluatedData, dispatch, state, merge, data, type) {
  if (type === void 0) {
    type = EvaluatedQuestionType.Normal;
  }

  var isLoaded = useRef(false); // to prevent save during restore

  var isParent = type === EvaluatedQuestionType.Parent;
  useEffect(function () {
    if (evaluatedData && (!isLoaded.current || isParent) && !state.submitted) {
      dispatch(questionEvaluated(merge(state, evaluatedData, data)));
      isLoaded.current = true;
    }
  }, [dispatch, evaluatedData, merge, data, state, isParent]);
  useEffect(function () {
    if (isParent && !state.loaded) {
      defer(function () {
        return dispatch(questionLoaded());
      });
    }
  }, [dispatch, evaluatedData, isParent, state.loaded]);
};

/**
 *  question boilerplate
 */

function useQuestion(id, evaluator, state, dispatch) {
  var improvementStatus = state.improvementStatus;
  var isLoaded = useRef(false);
  var isSent = useRef(false);
  useEffect(function () {
    if (!state || !isLoaded.current || isSent.current || (improvementStatus === null || improvementStatus === void 0 ? void 0 : improvementStatus.flag) === undefined) {
      return;
    } // send status to api


    evaluator.submitQuestionImprovementStatus(id, improvementStatus).then(function () {
      isSent.current = true;
    }).catch(function (e) {
      // if status saves fails we need to clear status so user can retry
      dispatch(questionClearImprovementStatus());
      dispatch(questionSystemError({
        type: QuestionSystemErrorType.ImprovementFeedbackError,
        detail: e.message
      }));
      defer(function () {
        return isSent.current = false;
      });
    });
  }, [dispatch, evaluator, id, improvementStatus, state]);
  useEffect(function () {
    if (state.loaded && !isLoaded.current) {
      isLoaded.current = true;
      isSent.current = (improvementStatus === null || improvementStatus === void 0 ? void 0 : improvementStatus.flag) !== undefined;
    }
  }, [improvementStatus === null || improvementStatus === void 0 ? void 0 : improvementStatus.flag, state.loaded]);
}

var MultipleChoiceQuestion = function (_a) {
  var className = _a.className,
      id = _a.id,
      visible = _a.visible,
      data = _a.data,
      events = _a.events,
      _b = _a.mode,
      mode = _b === void 0 ? QuestionMode.Standalone : _b,
      questionIndex = _a.questionIndex,
      siblingQuestionCount = _a.siblingQuestionCount,
      showQuestionIndex = _a.showQuestionIndex,
      controlledSubmit = _a.controlledSubmit,
      controlledReveal = _a.controlledReveal,
      controlledReset = _a.controlledReset,
      dontValidate = _a.dontValidate,
      evaluator = _a.evaluator,
      evaluatedData = _a.evaluatedData,
      parentEvaluatedData = _a.parentEvaluatedData,
      tutorialId = _a.tutorialId,
      tutorialStep = _a.tutorialStep;
  var intl = useIntl();

  var getDefaultState = function () {
    return getDefaultMultipleChoiceState(data);
  };

  var getResetState = function () {
    return __assign(__assign({}, getDefaultState()), {
      answerOrder: answerOrder,
      id: id
    });
  };

  var _c = useReducer(multipleChoiceRootReducer, getDefaultState()),
      state = _c[0],
      dispatch = _c[1];

  var submitted = state.submitted,
      validationErrorMsg = state.validationErrorMsg,
      response = state.response,
      answerOrder = state.answerOrder,
      answerCorrect = state.answerCorrect,
      revealAnswer = state.revealAnswer,
      revealedAnswer = state.revealedAnswer,
      feedback = state.feedback; // element ids

  var radioGroupID = id + "-radio-group";
  var radioIDBase = id + "-radio-";
  var errorMsgDisplayID = id + "-error";
  var errorMessage = feedback ? intl.formatMessage(feedback.descriptor, feedback.values) : ""; // question boilerplate

  useQuestion(id, evaluator, state, dispatch); // handle remote control coming from outer group

  useControlledQuestion({
    controlledSubmit: controlledSubmit,
    controlledReveal: controlledReveal,
    controlledReset: controlledReset
  }, state, dispatch, getResetState);
  useEvaluatedQuestion(evaluatedData, dispatch, state, mergeEvaluatedState$2, data);
  useEvaluatedQuestion(parentEvaluatedData, dispatch, state, mergeEvaluatedState$2, data, EvaluatedQuestionType.Parent);

  if (!initQuestion(data, id, visible, state, dispatch, getDefaultState)) {
    return null;
  } // this will validate and evaluate / reveal the user answer and update state accordingly


  questionHandler({
    mode: mode,
    id: id,
    tutorialId: tutorialId,
    tutorialStep: tutorialStep,
    data: data,
    events: events,
    state: state,
    dispatch: dispatch,
    evaluator: evaluator,
    // declare validation requirements
    validation: dontValidate ? undefined : {
      errorMessage: {
        descriptor: messagesQuestion.validationPleaseSelectAnswer
      },
      condition: function () {
        return response === unanswered;
      }
    },
    // declare how the user answer is extracted from the UI controls
    buildUserAnswer: function () {
      var _a;

      return _a = {}, _a[data.inputs[0].name] = response !== null && response !== void 0 ? response : "", _a;
    },
    // declare what to do when the revealed answer comes back from the evaluation service
    onRevealAnswer: function (solution) {
      if (solution.correct_answers) {
        var answer = Object.values(solution.correct_answers)[0];

        if (answer) {
          dispatch(multipleChoiceSetRevealedAnswerAction(answer));
        }
      }
    }
  });
  return /*#__PURE__*/React.createElement(QuestionContainer, {
    key: id,
    className: className,
    id: id,
    mode: mode,
    questionIndex: questionIndex,
    siblingQuestionCount: siblingQuestionCount,
    controlsMode: QuestionSubmitMode.CheckAnswer,
    feedbackNumAnswers: 1,
    feedbackWrongAnswersIndicated: true,
    feedbackMessage: errorMessage,
    feedbackPadding: AnswerFeedbackPadding.Normal,
    errorMsgDisplayID: errorMsgDisplayID,
    questionTypeLabel: intl.formatMessage(messagesQuestion.typeMultipleChoice),
    getResetQuestionState: getResetState,
    state: state,
    dispatch: dispatch,
    events: events,
    prologue: /*#__PURE__*/React.createElement(QuestionText, {
      text: data.stem,
      questionIndex: showQuestionIndex ? questionIndex : undefined
    }),
    body: answerOrder.map(function (answerIndex, index) {
      var answers = data.options[Object.keys(data.options)[0]];
      var _a = Object.entries(answers)[answerIndex],
          answerResponse = _a[0],
          answerText = _a[1];
      var revealed = revealAnswer && revealedAnswer === answerResponse;
      var checked = response === answerResponse;
      return /*#__PURE__*/React.createElement(MultipleChoiceAnswer, {
        key: radioIDBase + index,
        text: answerText,
        value: answerResponse,
        groupID: radioGroupID,
        answerID: radioIDBase + index,
        index: answerIndex,
        checked: checked,
        submitted: submitted,
        disabled: submitted,
        correct: answerCorrect === null && checked ? null : answerCorrect && checked || revealed,
        revealed: revealed,
        errorMsg: validationErrorMsg,
        errorMsgDisplayID: errorMsgDisplayID,
        onSelectAnswer: function (value) {
          var _a;

          var _b;

          dispatch(multipleChoiceSetResponseAction(value));
          (_b = events === null || events === void 0 ? void 0 : events.onAnswerGiven) === null || _b === void 0 ? void 0 : _b.call(events, id, true, (_a = {}, _a[data.inputs[0].name] = value, _a));
        }
      });
    })
  });
};

var GapFillType;

(function (GapFillType) {
  GapFillType[GapFillType["TypeIn"] = 0] = "TypeIn";
  GapFillType[GapFillType["DropDown"] = 1] = "DropDown";
})(GapFillType || (GapFillType = {}));

var GapFillValidateError;

(function (GapFillValidateError) {
  // all input fields must be entered
  GapFillValidateError[GapFillValidateError["NotFilledIn"] = 0] = "NotFilledIn"; // in word bank mode can't use the same answer more than once

  GapFillValidateError[GapFillValidateError["SameAnswerRepeated"] = 1] = "SameAnswerRepeated";
})(GapFillValidateError || (GapFillValidateError = {}));

// action types
var GAP_FILL_SET_RESPONSE = "GAP_FILL_SET_RESPONSE";
var GAP_FILL_EVALUATE = "GAP_FILL_EVALUATE";
var GAP_FILL_SET_CORRECT_ANSWERS = "GAP_FILL_SET_CORRECT_ANSWERS";

var gapFillSetResponse = function (index, response) {
  return {
    type: GAP_FILL_SET_RESPONSE,
    index: index,
    response: response
  };
};
var gapFillEvaluate = function (evaluation) {
  return {
    type: GAP_FILL_EVALUATE,
    evaluation: evaluation
  };
};
var gapFillSetCorrectAnswers = function (correctAnswers) {
  return {
    type: GAP_FILL_SET_CORRECT_ANSWERS,
    correctAnswers: correctAnswers
  };
};

var gapFillStateReducer = function (state, action) {
  var _a;

  switch (action.type) {
    case GAP_FILL_SET_RESPONSE:
      {
        var gap = __assign({}, state.gaps[action.index]);

        gap.response = action.response;
        var gaps = Object.assign(__spreadArray([], state.gaps, true), (_a = {}, _a[action.index] = gap, _a));
        var duplicates = [];

        if (state.wordBank) {
          duplicates = gaps.map(function (gap) {
            return gap.response;
          }).filter(function (response) {
            return response;
          }).reduce(function (acc, el, i, arr) {
            if (el && arr.indexOf(el) !== i && acc.indexOf(el) === -1) {
              acc.push(el);
            }

            return acc;
          }, []);
        }

        return __assign(__assign({}, state), {
          gaps: gaps,
          responseDuplicates: state.wordBank ? duplicates : null
        });
      }

    case GAP_FILL_EVALUATE:
      return __assign(__assign({}, state), {
        gaps: state.gaps.map(function (gap) {
          return __assign(__assign({}, gap), {
            correct: action.evaluation[gap.inputVarName]
          });
        })
      });

    case GAP_FILL_SET_CORRECT_ANSWERS:
      return __assign(__assign({}, state), {
        gaps: state.gaps.map(function (gap) {
          return __assign(__assign({}, gap), {
            revealedAnswer: action.correctAnswers[gap.inputVarName]
          });
        })
      });

    default:
      return state;
  }
};

var gapPattern = /({{.*?}})/g; // NB capturing group needed for reactStringReplace

var getDefaultGapFillState = function (data, context) {
  var defaults = getDefaultQuestionState();
  var type = data.question_type === "TextEntry" ? GapFillType.TypeIn : GapFillType.DropDown;
  var wordBank = null;

  if (data.question_type === "Matching") {
    wordBank = data.options.map(function (option) {
      return {
        value: option,
        text: option
      };
    });
  }

  var gaps = (processText(data.stem, context).match(gapPattern) || []).map(function (gap, i) {
    var gapData = {
      type: type,
      index: i,
      inputVarName: gap.slice(2, -2).trim(),
      response: unanswered,
      nextChar: data.stem.charAt(data.stem.indexOf(gap) + gap.length),
      correct: undetermined,
      revealedAnswer: undefined
    };

    if (type === GapFillType.DropDown) {
      var question = data;

      if (wordBank) {
        gapData.options = wordBank;
        gapData.optionsOrder = range(wordBank.length);
      } else {
        var options = question.options[gapData.inputVarName];
        gapData.options = Object.entries(options).map(function (entry) {
          return {
            value: entry[0],
            text: entry[1]
          };
        });
        var numOptions = gapData.options.length;
        gapData.optionsOrder = question.shuffle ? shuffle(range(numOptions)) : range(numOptions);
      }
    }

    return gapData;
  });
  var wordBankNoDistractors = wordBank ? wordBank.length === gaps.length : null;
  return __assign(__assign({}, defaults), {
    type: type,
    wordBank: wordBank,
    wordBankNoDistractors: wordBankNoDistractors,
    responseDuplicates: wordBank ? [] : null,
    gaps: gaps
  });
};
var mergeEvaluatedState$1 = function (state, evaluatedData) {
  var submitted = state.submitted,
      answerCorrect = state.answerCorrect,
      feedback = state.feedback,
      systemFeedback = state.systemFeedback,
      improvementStatus = state.improvementStatus;

  if (evaluatedData) {
    submitted = evaluatedData.correct !== undefined;

    if (evaluatedData.correct !== undefined) {
      answerCorrect = evaluatedData.correct;
    }

    if (evaluatedData.feedback) {
      feedback = evaluatedData.feedback;
    }

    if (evaluatedData.improvementStatus) {
      improvementStatus = evaluatedData.improvementStatus;
    }

    if (evaluatedData.answers) {
      var correctResponses_1 = evaluatedData.input_correctness;
      state.gaps.forEach(function (gap) {
        var _a;

        var varName = gap.inputVarName;
        var answer = (_a = evaluatedData.answers) === null || _a === void 0 ? void 0 : _a[varName];
        gap.response = answer || gap.response;

        if (correctResponses_1) {
          var isCorrect = correctResponses_1 === null || correctResponses_1 === void 0 ? void 0 : correctResponses_1[varName];

          if (typeof isCorrect !== "undefined") {
            gap.correct = isCorrect;
          }
        }
      });

      if (correctResponses_1) {
        if (!answerCorrect && state.wordBank && state.wordBankNoDistractors) {
          var wrongCount = Object.entries(correctResponses_1).filter(function (entry) {
            return !entry[1];
          }).length;
          systemFeedback = {
            descriptor: messagesQuestion.feedbackAnswerIncorrect
          };
          feedback = {
            descriptor: messagesQuestion.feedbackWrongAnswersSpecific,
            values: {
              wrongCount: wrongCount
            }
          };
        }
      }
    }
  }

  return __assign(__assign({}, state), {
    systemFeedback: systemFeedback,
    feedback: feedback,
    improvementStatus: improvementStatus,
    submitted: submitted,
    answerCorrect: answerCorrect
  });
};
var gapFillRootReducer = function (state, action) {
  var newState = questionStateReducer(state, action);
  newState = gapFillStateReducer(newState, action);
  return newState;
};

var FormErrorIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "14",
    height: "14",
    viewBox: "0 0 14 14",
    "aria-hidden": "true",
    focusable: "false",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "#E71D3A",
    d: "M8 6a1 1 0 1 1-2 0V4a1 1 0 1 1 2 0v2zm-1 5a1 1 0 1 1 0-2 1 1 0 1 1 0 2zM7 0a7 7 0 1 0 0 14A7 7 0 0 0 7 0z"
  }));
};

var CaretDownLargeIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "12.8",
    height: "7.7",
    viewBox: "0 0 14 8",
    "aria-hidden": "true",
    focusable: "false",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 1A.999.999 0 0 1 1.707.293L7 5.586 12.293.293a.999.999 0 1 1 1.414 1.414l-6 6a.999.999 0 0 1-1.414 0l-6-6A.997.997 0 0 1 0 1"
  }));
};

var _a;
var GapFillGapAdornmentType;

(function (GapFillGapAdornmentType) {
  GapFillGapAdornmentType[GapFillGapAdornmentType["Error"] = 0] = "Error";
  GapFillGapAdornmentType[GapFillGapAdornmentType["Correct"] = 1] = "Correct";
  GapFillGapAdornmentType[GapFillGapAdornmentType["InCorrect"] = 2] = "InCorrect";
  GapFillGapAdornmentType[GapFillGapAdornmentType["Revealed"] = 3] = "Revealed";
  GapFillGapAdornmentType[GapFillGapAdornmentType["DropDown"] = 4] = "DropDown";
})(GapFillGapAdornmentType || (GapFillGapAdornmentType = {}));

var iconMapping = (_a = {}, _a[GapFillGapAdornmentType.Error] = FormErrorIcon, _a[GapFillGapAdornmentType.Correct] = FeedbackCorrectIcon, _a[GapFillGapAdornmentType.InCorrect] = FeedbackIncorrectIcon, _a[GapFillGapAdornmentType.Revealed] = FeedbackCorrectRevealedIcon, _a[GapFillGapAdornmentType.DropDown] = CaretDownLargeIcon, _a);
var GapAdornmentHolder = styled.span.withConfig({
  displayName: "GapFillGapAdornment__GapAdornmentHolder"
})(templateObject_1$w || (templateObject_1$w = __makeTemplateObject(["\n  display: inline-flex;\n  align-items: center;\n  position: absolute;\n  right: 0;\n  top: 0;\n  bottom: 0;\n  margin-right: ", ";\n  pointer-events: none;\n  svg {\n    ", ";\n    vertical-align: middle;\n    width: ", ";\n    height: ", ";\n  }\n"], ["\n  display: inline-flex;\n  align-items: center;\n  position: absolute;\n  right: 0;\n  top: 0;\n  bottom: 0;\n  margin-right: ", ";\n  pointer-events: none;\n  svg {\n    ", ";\n    vertical-align: middle;\n    width: ", ";\n    height: ", ";\n  }\n"])), function (_a) {
  var insetAmount = _a.insetAmount;
  return insetAmount ? insetAmount : "1.6rem";
}, function (_a) {
  var fillColor = _a.fillColor;
  return fillColor ? "fill: " + fillColor : "";
}, function (_a) {
  var size = _a.size;
  return size ? size : "";
}, function (_a) {
  var size = _a.size;
  return size ? size : "";
});

var GapFillGapAdornment = function (_a) {
  var type = _a.type,
      insetAmount = _a.insetAmount,
      size = _a.size,
      fillColor = _a.fillColor;
  var Icon = iconMapping[type];
  return /*#__PURE__*/React.createElement(GapAdornmentHolder, {
    insetAmount: insetAmount,
    size: size,
    fillColor: fillColor
  }, /*#__PURE__*/React.createElement(Icon, null));
};
var templateObject_1$w;

// such as a gap fill question using <p> tags

var Styled$q = {
  Container: styled.span.withConfig({
    displayName: "TagSelectorPanel__Container"
  })(templateObject_1$v || (templateObject_1$v = __makeTemplateObject(["\n    display: inline-block;\n    width: 100%;\n    box-sizing: border-box;\n    position: relative;\n    &:focus {\n      scroll-margin: ", ";\n      outline: none;\n      border-radius: 4px;\n      box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.2), 0 0 0 2px #ffffff, 0 0 0 4px ", ";\n    }\n  "], ["\n    display: inline-block;\n    width: 100%;\n    box-sizing: border-box;\n    position: relative;\n    &:focus {\n      scroll-margin: ", ";\n      outline: none;\n      border-radius: 4px;\n      box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.2), 0 0 0 2px #ffffff, 0 0 0 4px ", ";\n    }\n  "])), function (_a) {
    var scrollMargin = _a.scrollMargin;
    return scrollMargin;
  }, primaryShade(6)),
  InlineBox: styled.span.withConfig({
    displayName: "TagSelectorPanel__InlineBox"
  })(templateObject_2$o || (templateObject_2$o = __makeTemplateObject(["\n    ", ";\n    display: inline-block;\n    padding: 16px;\n    width: 100%;\n    box-sizing: border-box;\n  "], ["\n    ", ";\n    display: inline-block;\n    padding: 16px;\n    width: 100%;\n    box-sizing: border-box;\n  "])), surveyBoxStyle),
  Prompt: styled.span.withConfig({
    displayName: "TagSelectorPanel__Prompt"
  })(templateObject_4$f || (templateObject_4$f = __makeTemplateObject(["\n    display: inline-block;\n    width: 100%;\n    font-size: 1.6rem;\n    position: relative;\n    font-weight: 500;\n    line-height: 1.75;\n    margin-bottom: 8px;\n    ", "\n    svg {\n      fill: ", ";\n      vertical-align: middle;\n      margin-right: 10px;\n    }\n  "], ["\n    display: inline-block;\n    width: 100%;\n    font-size: 1.6rem;\n    position: relative;\n    font-weight: 500;\n    line-height: 1.75;\n    margin-bottom: 8px;\n    ", "\n    svg {\n      fill: ", ";\n      vertical-align: middle;\n      margin-right: 10px;\n    }\n  "])), function (hasClose) {
    return hasClose && css(templateObject_3$h || (templateObject_3$h = __makeTemplateObject(["\n        padding-right: 32px;\n        box-sizing: border-box;\n      "], ["\n        padding-right: 32px;\n        box-sizing: border-box;\n      "])));
  }, primaryShade(6)),
  TagButton: styled.button.withConfig({
    displayName: "TagSelectorPanel__TagButton"
  })(templateObject_6$8 || (templateObject_6$8 = __makeTemplateObject(["\n    font-size: 1.4rem;\n    background-color: ", ";\n    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.5);\n    border-radius: 1rem;\n    margin-right: 6px;\n    padding: 4px 12px;\n    border: 1px solid rgba(0, 0, 0, 0);\n    color: ", ";\n    line-height: initial;\n    position: relative;\n    display: inline-flex;\n    justify-content: center;\n    white-space: nowrap;\n    vertical-align: middle;\n    cursor: ", ";\n    pointer-events: ", ";\n    ", ";\n    outline: none;\n    &:focus {\n      border: 1px solid ", ";\n      box-shadow: 0 0 0 1px ", ";\n      outline: none;\n    }\n  "], ["\n    font-size: 1.4rem;\n    background-color: ", ";\n    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.5);\n    border-radius: 1rem;\n    margin-right: 6px;\n    padding: 4px 12px;\n    border: 1px solid rgba(0, 0, 0, 0);\n    color: ", ";\n    line-height: initial;\n    position: relative;\n    display: inline-flex;\n    justify-content: center;\n    white-space: nowrap;\n    vertical-align: middle;\n    cursor: ", ";\n    pointer-events: ", ";\n    ", ";\n    outline: none;\n    &:focus {\n      border: 1px solid ", ";\n      box-shadow: 0 0 0 1px ", ";\n      outline: none;\n    }\n  "])), function (_a) {
    var selected = _a.selected;
    return selected ? primaryShade(3) : "#ffffff";
  }, cloud.getShade(10), function (_a) {
    var selected = _a.selected;
    return selected ? "default" : "pointer";
  }, function (_a) {
    var disabled = _a.disabled;
    return disabled && "none";
  }, function (_a) {
    var disabled = _a.disabled,
        selected = _a.selected;
    return !disabled && css(templateObject_5$a || (templateObject_5$a = __makeTemplateObject(["\n        &:hover {\n          background-color: ", ";\n        }\n      "], ["\n        &:hover {\n          background-color: ", ";\n        }\n      "])), primaryShade(selected ? 3 : 2));
  }, cloud.getShade(10), cloud.getShade(10)),
  Feedback: styled.span.withConfig({
    displayName: "TagSelectorPanel__Feedback"
  })(templateObject_7$5 || (templateObject_7$5 = __makeTemplateObject(["\n    display: inline-block;\n    width: 100%;\n    margin-top: 24px;\n    color: ", ";\n    &:focus {\n      outline: none;\n      border-radius: 4px;\n      box-shadow: 0 0 0 3px ", ", 0 0 0 4px ", ";\n    }\n    scroll-margin-bottom: 20px;\n  "], ["\n    display: inline-block;\n    width: 100%;\n    margin-top: 24px;\n    color: ", ";\n    &:focus {\n      outline: none;\n      border-radius: 4px;\n      box-shadow: 0 0 0 3px ", ", 0 0 0 4px ", ";\n    }\n    scroll-margin-bottom: 20px;\n  "])), cloud.getShade(10), primaryShade(1), primaryShade(6)),
  CloseBtn: styled.button.withConfig({
    displayName: "TagSelectorPanel__CloseBtn"
  })(templateObject_8$2 || (templateObject_8$2 = __makeTemplateObject(["\n    position: absolute;\n    top: 11px;\n    right: 10px;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    width: 32px;\n    height: 32px;\n    padding: 0;\n    border: none;\n    border-radius: 50%;\n    background-color: transparent;\n    cursor: pointer;\n    &:hover {\n      background-color: ", ";\n    }\n    &:focus {\n      outline: none;\n      box-shadow: ", " 0 0 0 2px, ", " 0 0 0 4px;\n    }\n  "], ["\n    position: absolute;\n    top: 11px;\n    right: 10px;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    width: 32px;\n    height: 32px;\n    padding: 0;\n    border: none;\n    border-radius: 50%;\n    background-color: transparent;\n    cursor: pointer;\n    &:hover {\n      background-color: ", ";\n    }\n    &:focus {\n      outline: none;\n      box-shadow: ", " 0 0 0 2px, ", " 0 0 0 4px;\n    }\n  "])), cloud.getShade(3), primaryShade(1), primaryShade(6))
};
var TagSelectorPanel = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var id = _a.id,
      prompt = _a.prompt,
      options = _a.options,
      selectedOption = _a.selectedOption,
      feedback = _a.feedback,
      disabled = _a.disabled,
      onSelect = _a.onSelect,
      onClose = _a.onClose,
      closeBtnCaption = _a.closeBtnCaption,
      className = _a.className,
      _b = _a.scrollMargin,
      scrollMargin = _b === void 0 ? "20px" : _b;
  var intl = useIntl();

  var _c = useState(false),
      feedbackFocused = _c[0],
      setFeedbackFocused = _c[1];

  var feedbackRef = useRef(null);
  var legendID = id + "-selector-legend";
  useTakeFocus(feedbackRef, {
    takeFocus: feedbackFocused && !!feedbackRef.current,
    temporary: true,
    smoothScrollIntoView: true
  });

  var tagButtonClick = function (option) {
    var _a;

    onSelect((_a = option.value) !== null && _a !== void 0 ? _a : option.option);
    defer(function () {
      setFeedbackFocused(true);
    });
  };

  return /*#__PURE__*/React.createElement(Styled$q.Container, {
    className: className,
    ref: ref,
    scrollMargin: scrollMargin,
    role: "group",
    "aria-labelledby": legendID
  }, /*#__PURE__*/React.createElement(Styled$q.InlineBox, null, /*#__PURE__*/React.createElement(Styled$q.Prompt, {
    "aria-hidden": true,
    id: legendID,
    hasClose: !!onClose
  }, /*#__PURE__*/React.createElement(QuestionCircle, null), prompt), onClose && /*#__PURE__*/React.createElement(Styled$q.CloseBtn, {
    "aria-label": closeBtnCaption || intl.formatMessage(messagesBase.close),
    onClick: function () {
      return onClose();
    }
  }, /*#__PURE__*/React.createElement(XSmall, {
    color: cloud.getShade(9)
  })), options.map(function (option) {
    var _a;

    var value = (_a = option.value) !== null && _a !== void 0 ? _a : option.option;
    var current = !!selectedOption && selectedOption === value;
    return /*#__PURE__*/React.createElement(Styled$q.TagButton, {
      key: option.value,
      selected: current,
      disabled: !!disabled,
      onClick: function () {
        return tagButtonClick(option);
      },
      "aria-pressed": current
    }, option.option);
  }), feedback && /*#__PURE__*/React.createElement(Styled$q.Feedback, {
    ref: feedbackRef
  }, feedback)));
});
var templateObject_1$v, templateObject_2$o, templateObject_3$h, templateObject_4$f, templateObject_5$a, templateObject_6$8, templateObject_7$5, templateObject_8$2;

var popinYOffset = 50;
var popinBottomMargin = 10;
var gapPadderName = "gapPadder";
var gapPanelName = "gapPanel";
var Styled$p = {
  Container: styled.span.withConfig({
    displayName: "GapFillGapSuggestions__Container"
  })(templateObject_1$u || (templateObject_1$u = __makeTemplateObject(["\n    left: 0;\n    width: 100%;\n    display: inline-block;\n    box-sizing: border-box;\n    position: absolute;\n    font-weight: 500;\n    margin-top: ", "px;\n    scroll-margin: 100px;\n  "], ["\n    left: 0;\n    width: 100%;\n    display: inline-block;\n    box-sizing: border-box;\n    position: absolute;\n    font-weight: 500;\n    margin-top: ", "px;\n    scroll-margin: 100px;\n  "])), function (_a) {
    var yOffset = _a.yOffset;
    return yOffset;
  }),
  IconHolder: styled.span.withConfig({
    displayName: "GapFillGapSuggestions__IconHolder"
  })(templateObject_2$n || (templateObject_2$n = __makeTemplateObject(["\n    margin-right: 10px;\n    svg {\n      vertical-align: middle;\n    }\n  "], ["\n    margin-right: 10px;\n    svg {\n      vertical-align: middle;\n    }\n  "]))),
  InsertCollator: styled.span.withConfig({
    displayName: "GapFillGapSuggestions__InsertCollator"
  })(templateObject_3$g || (templateObject_3$g = __makeTemplateObject(["\n    // keeps the yOffset of padder and panel the same\n    white-space: nowrap;\n    span > * {\n      white-space: normal;\n    }\n  "], ["\n    // keeps the yOffset of padder and panel the same\n    white-space: nowrap;\n    span > * {\n      white-space: normal;\n    }\n  "]))),
  VertPadder: styled.span.withConfig({
    displayName: "GapFillGapSuggestions__VertPadder"
  })(templateObject_4$e || (templateObject_4$e = __makeTemplateObject(["\n    display: inline-block;\n    vertical-align: top;\n    margin-bottom: ", "px;\n    white-space: nowrap;\n  "], ["\n    display: inline-block;\n    vertical-align: top;\n    margin-bottom: ", "px;\n    white-space: nowrap;\n  "])), popinBottomMargin)
};

var GapFillGapSuggestions = function (_a) {
  var id = _a.id,
      suggestions = _a.suggestions,
      onSelect = _a.onSelect,
      onClose = _a.onClose,
      selectedSuggestion = _a.selectedSuggestion,
      takeFocus = _a.takeFocus;
  var intl = useIntl();
  var padRef = useRef(null);
  var panelRef = useRef(null);

  var _b = useState(0),
      panelExtent = _b[0],
      setPanelExtent = _b[1];

  var _c = useState(popinYOffset),
      yOffset = _c[0],
      setYOffset = _c[1];

  var options = useMemo(function () {
    return !suggestions ? [] : suggestions.map(function (suggestion) {
      return {
        option: suggestion
      };
    });
  }, [suggestions]);
  var handleResize = useCallback(function () {
    var _a, _b;

    if (!padRef.current || !panelRef.current) {
      setPanelExtent(0);
      setYOffset(popinYOffset);
    } else {
      // if multiple gaps on same line then we need to push subsequent panels down
      var padEl_1 = padRef.current;
      var padParentEl_1 = padEl_1.parentElement;
      var gapFillEl = (_a = padEl_1.parentElement) === null || _a === void 0 ? void 0 : _a.parentElement;

      if (!padParentEl_1 || !gapFillEl) {
        throw new Error("Bad gap fill suggestions dom structure");
      }

      var extraPad = Array.from((_b = gapFillEl.querySelectorAll("." + gapPadderName)) !== null && _b !== void 0 ? _b : []).filter(function (padder, index, array) {
        var _a;

        return array.indexOf(padEl_1) > array.indexOf(padder) && padParentEl_1.offsetTop === ((_a = padder.parentElement) === null || _a === void 0 ? void 0 : _a.offsetTop);
      }).reduce(function (acc, padder) {
        var _a, _b;

        var panel = (_a = padder.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector("." + gapPanelName);
        return acc + ((_b = panel === null || panel === void 0 ? void 0 : panel.offsetHeight) !== null && _b !== void 0 ? _b : 0) + popinBottomMargin;
      }, 0);
      setPanelExtent(popinYOffset + panelRef.current.offsetHeight + extraPad);
      setYOffset(popinYOffset + extraPad);
    }
  }, []);
  useEventListener("resize", handleResize); // force resize on first appearance

  useEffect(handleResize, [padRef.current, panelRef.current, handleResize]); // take focus if requested

  useTakeFocus(panelRef, {
    takeFocus: takeFocus,
    temporary: true
  }); // note, layout not achievable by css alone so some imperative styling here
  // do not adjust html structure without also considering the resize function above

  return /*#__PURE__*/React.createElement(Styled$p.InsertCollator, null, /*#__PURE__*/React.createElement(Styled$p.VertPadder, {
    className: gapPadderName,
    ref: padRef,
    style: {
      height: panelExtent + "px"
    }
  }), /*#__PURE__*/React.createElement(Styled$p.Container, {
    yOffset: yOffset
  }, /*#__PURE__*/React.createElement(TagSelectorPanel, {
    id: id,
    className: gapPanelName,
    ref: panelRef,
    prompt: intl.formatMessage(messagesQuestion.suggestionsPrompt, {
      count: options.length
    }),
    options: options,
    selectedOption: selectedSuggestion,
    onSelect: onSelect,
    onClose: onClose,
    closeBtnCaption: intl.formatMessage(messagesQuestion.suggestionsClose),
    // to keep the gap in view with the suggestion when scrolled into view
    scrollMargin: "80px"
  })));
};
var templateObject_1$u, templateObject_2$n, templateObject_3$g, templateObject_4$e;

var Styled$o = {
  GapContainer: styled.span.withConfig({
    displayName: "GapFillTypedGap__GapContainer"
  })(templateObject_1$t || (templateObject_1$t = __makeTemplateObject(["\n    display: inline-block;\n    position: relative;\n    vertical-align: top;\n    margin-top: 0;\n    margin-bottom: 3px;\n  "], ["\n    display: inline-block;\n    position: relative;\n    vertical-align: top;\n    margin-top: 0;\n    margin-bottom: 3px;\n  "]))),
  GapInput: styled.input.withConfig({
    displayName: "GapFillTypedGap__GapInput"
  })(templateObject_2$m || (templateObject_2$m = __makeTemplateObject(["\n    width: 200px;\n    ", ";\n    background: #ffffff;\n    border: 1px solid rgba(0, 0, 0, 0.43);\n    border-radius: 3px;\n    appearance: none;\n    font-size: 1.6rem;\n    padding: 8px 16px 9px 16px;\n    box-sizing: border-box;\n    font-family: inherit;\n    ", ";\n    &:focus {\n      outline: none;\n      border-color: ", ";\n      box-shadow: 0 0 0 1px ", ";\n    }\n  "], ["\n    width: 200px;\n    ", ";\n    background: #ffffff;\n    border: 1px solid rgba(0, 0, 0, 0.43);\n    border-radius: 3px;\n    appearance: none;\n    font-size: 1.6rem;\n    padding: 8px 16px 9px 16px;\n    box-sizing: border-box;\n    font-family: inherit;\n    ", ";\n    &:focus {\n      outline: none;\n      border-color: ", ";\n      box-shadow: 0 0 0 1px ", ";\n    }\n  "])), function (_a) {
    var gapNextChar = _a.gapNextChar;
    return gapNextChar !== " " ? "margin-right: 2px" : "";
  }, function (_a) {
    var validationError = _a.validationError;
    return validationError && "border-color: " + red.getShade(6) + ";";
  }, ninja.getShade(11), ninja.getShade(11))
};

var GapFillTypedGap = function (_a) {
  var response = _a.response,
      gapIndex = _a.gapIndex,
      validationError = _a.validationError,
      errorMsgDisplayID = _a.errorMsgDisplayID,
      gapNextChar = _a.gapNextChar,
      onGapChange = _a.onGapChange,
      id = _a.id,
      suggestions = _a.suggestions,
      isFirstSuggestion = _a.isFirstSuggestion,
      onCloseSuggestion = _a.onCloseSuggestion;
  var intl = useIntl();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styled$o.GapContainer, null, /*#__PURE__*/React.createElement(Styled$o.GapInput, {
    type: "text",
    gapNextChar: gapNextChar,
    validationError: validationError,
    "aria-invalid": validationError ? true : undefined,
    "aria-describedby": validationError ? errorMsgDisplayID : undefined,
    "aria-label": intl.formatMessage(messagesGapFill.fillInTheBlankPrompt, {
      blankIndex: gapIndex + 1
    }),
    name: id,
    id: id,
    spellCheck: "false",
    autoComplete: "off",
    value: response !== null && response !== void 0 ? response : "",
    onChange: function (event) {
      onGapChange(event.currentTarget.value, gapIndex);
    }
  }), validationError && /*#__PURE__*/React.createElement(GapFillGapAdornment, {
    type: GapFillGapAdornmentType.Error,
    size: "1.6rem"
  })), suggestions && /*#__PURE__*/React.createElement(GapFillGapSuggestions, {
    id: id + "-suggestions",
    suggestions: suggestions,
    takeFocus: isFirstSuggestion,
    selectedSuggestion: response !== null && response !== void 0 ? response : undefined,
    onSelect: function (suggestion) {
      return onGapChange(suggestion, gapIndex);
    },
    onClose: onCloseSuggestion
  }));
};
var templateObject_1$t, templateObject_2$m;

var GapHolder = styled.span.withConfig({
  displayName: "GapFillGapFeedback__GapHolder"
})(templateObject_1$s || (templateObject_1$s = __makeTemplateObject(["\n  display: inline-block;\n  position: relative;\n  vertical-align: top;\n  margin-top: 1px;\n  margin-bottom: 4px;\n"], ["\n  display: inline-block;\n  position: relative;\n  vertical-align: top;\n  margin-top: 1px;\n  margin-bottom: 4px;\n"])));
var suppressedBoxStyle = css(templateObject_2$l || (templateObject_2$l = __makeTemplateObject(["\n  ", ";\n  background: ", ";\n  border: 1px solid ", ";\n"], ["\n  ", ";\n  background: ", ";\n  border: 1px solid ", ";\n"])), boxStyle, cloud.getShade(1), cloud.getShade(4));
var GapFeedback = styled.span.withConfig({
  displayName: "GapFillGapFeedback__GapFeedback"
})(templateObject_3$f || (templateObject_3$f = __makeTemplateObject(["\n  ", ";\n  position: relative;\n  display: block;\n  width: 200px;\n  border-radius: 3px;\n  font-weight: 400;\n  font-size: 1.6rem;\n  padding: 0.8rem 1.6rem;\n  box-sizing: border-box;\n  min-height: 2.5rem;\n  line-height: normal;\n  vertical-align: baseline;\n  ", ";\n  ", ";\n"], ["\n  ", ";\n  position: relative;\n  display: block;\n  width: 200px;\n  border-radius: 3px;\n  font-weight: 400;\n  font-size: 1.6rem;\n  padding: 0.8rem 1.6rem;\n  box-sizing: border-box;\n  min-height: 2.5rem;\n  line-height: normal;\n  vertical-align: baseline;\n  ", ";\n  ", ";\n"])), function (_a) {
  var correct = _a.correct,
      revealedAnswer = _a.revealedAnswer,
      suppressed = _a.suppressed;
  return revealedAnswer ? revealedBoxStyle : suppressed ? suppressedBoxStyle : correct === true ? correctBoxStyle : correct === false ? incorrectBoxStyle : correct === null ? surveyBoxStyle : null;
}, function (_a) {
  var nextChar = _a.nextChar;
  return nextChar !== " " ? "margin-right: 2px" : "";
}, function (_a) {
  var revealedAnswer = _a.revealedAnswer;
  return revealedAnswer ? "margin-top: 0.5rem" : "";
}); // this replaces native control (e.g.) input and this nudges baseline for a better match

var GapFeedbackText = styled.span.withConfig({
  displayName: "GapFillGapFeedback__GapFeedbackText"
})(templateObject_4$d || (templateObject_4$d = __makeTemplateObject(["\n  display: inline-flex;\n  margin-bottom: 1px;\n  margin-right: 1.5rem;\n  overflow: hidden;\n  text-overflow: ellipsis;\n"], ["\n  display: inline-flex;\n  margin-bottom: 1px;\n  margin-right: 1.5rem;\n  overflow: hidden;\n  text-overflow: ellipsis;\n"])));
/**
 *  replaces a typed input field or dropdown with a static evaluated version of the answer
 *  optionally also showing the correct answer if the evaluated answer is wrong
 */

var GapFillGapFeedback = function (_a) {
  var _b = _a.gap,
      index = _b.index,
      type = _b.type,
      response = _b.response,
      nextChar = _b.nextChar,
      correct = _b.correct,
      revealedAnswer = _b.revealedAnswer,
      options = _b.options,
      suppressFeedback = _a.suppressFeedback;
  var intl = useIntl();

  var findLabel = function (response) {
    // in dropdown get the text e.g. "increases" from a value e.g. "b"
    // in typein the value is the same as the actual text
    if (!response) {
      return "\u00A0"; // non-breaking space to preserve height
    }

    if (type === GapFillType.TypeIn) {
      return response;
    } else {
      if (!options) {
        throw new Error("Found Dropdown gapfill with no options");
      } else {
        return options.filter(function (option) {
          return option.value === response;
        })[0].text;
      }
    }
  };

  var a11yFeedback = findLabel(response) + ". " + intl.formatMessage(messagesGapFill.gapNumberLabel, {
    blankIndex: index + 1
  });

  if (!suppressFeedback && correct !== undetermined) {
    if (correct) {
      a11yFeedback += " " + intl.formatMessage(messagesQuestion.feedbackItemCorrectAnswer);
    } else {
      a11yFeedback += " " + intl.formatMessage(messagesQuestion.feedbackItemIncorrectAnswer);
    }

    if (revealedAnswer && !correct) {
      a11yFeedback += " " + intl.formatMessage(messagesQuestion.feedbackCorrectAnswerIs, {
        correctAnswer: findLabel(revealedAnswer)
      }) + ".";
    }
  }

  return /*#__PURE__*/React.createElement(GapHolder, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(GapFeedback, {
    suppressed: suppressFeedback === true,
    nextChar: nextChar,
    correct: correct,
    revealedAnswer: undefined
  }, /*#__PURE__*/React.createElement(GapFeedbackText, null, findLabel(response)), correct !== undetermined && !suppressFeedback && /*#__PURE__*/React.createElement(GapFillGapAdornment, {
    type: correct ? GapFillGapAdornmentType.Correct : GapFillGapAdornmentType.InCorrect,
    size: "1.6rem"
  })), revealedAnswer && !correct && /*#__PURE__*/React.createElement(GapFeedback, {
    nextChar: nextChar,
    correct: correct,
    revealedAnswer: revealedAnswer
  }, /*#__PURE__*/React.createElement(GapFeedbackText, null, findLabel(revealedAnswer)), /*#__PURE__*/React.createElement(GapFillGapAdornment, {
    type: GapFillGapAdornmentType.Revealed,
    size: "1.6rem"
  }))), /*#__PURE__*/React.createElement(A11y, null, a11yFeedback)));
};
var templateObject_1$s, templateObject_2$l, templateObject_3$f, templateObject_4$d;

var GapContainer = styled.span.withConfig({
  displayName: "GapFillDropDownGap__GapContainer"
})(templateObject_1$r || (templateObject_1$r = __makeTemplateObject(["\n  display: inline-block;\n  position: relative;\n  vertical-align: top;\n  margin-bottom: 3px;\n"], ["\n  display: inline-block;\n  position: relative;\n  vertical-align: top;\n  margin-bottom: 3px;\n"])));
var GapSelect = styled.select.withConfig({
  displayName: "GapFillDropDownGap__GapSelect"
})(templateObject_2$k || (templateObject_2$k = __makeTemplateObject(["\n  appearance: none;\n  padding: 8px 16px 9px 16px;\n  width: 200px;\n  font-size: 1.6rem;\n  font-family: inherit;\n  border-radius: 4px;\n  border: 1px solid rgba(0, 0, 0, 0.43);\n  ", ";\n  ", ";\n  ", ";\n  &:focus {\n    outline: none;\n    border-color: ", ";\n    box-shadow: 0 0 0 1px ", ";\n  }\n"], ["\n  appearance: none;\n  padding: 8px 16px 9px 16px;\n  width: 200px;\n  font-size: 1.6rem;\n  font-family: inherit;\n  border-radius: 4px;\n  border: 1px solid rgba(0, 0, 0, 0.43);\n  ", ";\n  ", ";\n  ", ";\n  &:focus {\n    outline: none;\n    border-color: ", ";\n    box-shadow: 0 0 0 1px ", ";\n  }\n"])), function (_a) {
  var gapNextChar = _a.gapNextChar;
  return gapNextChar !== " " ? "margin-right: 2px;" : "";
}, function (_a) {
  var validationError = _a.validationError;
  return validationError && "border-color: " + red.getShade(6) + ";";
}, function (_a) {
  var response = _a.response;
  return response ? "" : "color: " + cloud.getShade(6) + ";";
}, ninja.getShade(11), ninja.getShade(11));

var GapFillDropDownGap = function (_a) {
  var response = _a.response,
      gapIndex = _a.gapIndex,
      validationError = _a.validationError,
      errorMsgDisplayID = _a.errorMsgDisplayID,
      gapNextChar = _a.gapNextChar,
      options = _a.options,
      optionsOrder = _a.optionsOrder,
      onGapChange = _a.onGapChange,
      id = _a.id,
      theme = _a.theme; // todo in wordbank mode should give a11y user indicator for already "used"

  var intl = useIntl();
  return /*#__PURE__*/React.createElement(GapContainer, null, /*#__PURE__*/React.createElement(GapSelect, {
    response: response,
    placeholder: intl.formatMessage(messagesQuestion.controlsSelectPlaceHolder),
    gapNextChar: gapNextChar,
    validationError: validationError,
    "aria-invalid": validationError ? true : undefined,
    "aria-describedby": validationError ? errorMsgDisplayID : undefined,
    "aria-label": intl.formatMessage(messagesGapFill.fillInTheBlankPrompt, {
      blankIndex: gapIndex + 1
    }),
    name: "name",
    id: id,
    spellCheck: "false",
    autoComplete: "off",
    value: response !== null && response !== void 0 ? response : "",
    onChange: function (event) {
      onGapChange(event.currentTarget.value, gapIndex);
    }
  }, /*#__PURE__*/React.createElement("option", {
    disabled: true,
    value: ""
  }, intl.formatMessage(messagesQuestion.controlsSelectPlaceHolder)), optionsOrder.map(function (n, i) {
    return /*#__PURE__*/React.createElement("option", {
      key: i,
      value: options[n].value
    }, options[n].text);
  })), /*#__PURE__*/React.createElement(GapFillGapAdornment, {
    type: GapFillGapAdornmentType.DropDown,
    fillColor: validationError ? red.getShade(6) : themePrimaryShade(6, theme)
  }), validationError && /*#__PURE__*/React.createElement(GapFillGapAdornment, {
    type: GapFillGapAdornmentType.Error,
    insetAmount: "4.5rem",
    size: "1.6rem"
  }));
};

var GapFillDropDownGap$1 = withTheme(GapFillDropDownGap);
var templateObject_1$r, templateObject_2$k;

var WordBankBox = styled.div.withConfig({
  displayName: "GapFillWordBank__WordBankBox"
})(templateObject_1$q || (templateObject_1$q = __makeTemplateObject(["\n  background-color: ", ";\n  border-radius: 4px;\n  text-align: left;\n  margin: 0 16px 16px;\n  padding: 14px 16px;\n  line-height: 2;\n"], ["\n  background-color: ", ";\n  border-radius: 4px;\n  text-align: left;\n  margin: 0 16px 16px;\n  padding: 14px 16px;\n  line-height: 2;\n"])), frost.getShade(1));
var WordBankWord = styled.div.withConfig({
  displayName: "GapFillWordBank__WordBankWord"
})(templateObject_2$j || (templateObject_2$j = __makeTemplateObject(["\n  display: inline-block;\n  background: ", ";\n  border: 1px solid ", ";\n  border-radius: 16px;\n  margin: 0 8px 8px;\n  padding: 4px 12px;\n  line-height: normal;\n  font-weight: 400;\n  font-size: 1.4rem;\n  ", ";\n"], ["\n  display: inline-block;\n  background: ", ";\n  border: 1px solid ", ";\n  border-radius: 16px;\n  margin: 0 8px 8px;\n  padding: 4px 12px;\n  line-height: normal;\n  font-weight: 400;\n  font-size: 1.4rem;\n  ", ";\n"])), cloud.getShade(1), cloud.getShade(4), function (_a) {
  var spent = _a.spent;
  return spent ? "opacity: 0.4" : "";
});

var GapFillWordBank = function (_a) {
  var wordBank = _a.wordBank,
      gaps = _a.gaps;
  return /*#__PURE__*/React.createElement(WordBankBox, null, /*#__PURE__*/React.createElement(A11y, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesGapFill.wordBankHeaderLabel))), wordBank.map(function (option) {
    var spent = !!gaps.find(function (gap) {
      return gap.response === option.value;
    });
    return /*#__PURE__*/React.createElement(WordBankWord, {
      spent: spent,
      key: option.text
    }, option.text, spent && /*#__PURE__*/React.createElement(A11y, null, ". ", /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesGapFill.wordBankWordSpent))));
  }), /*#__PURE__*/React.createElement(A11y, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesGapFill.wordBankFooterLabel))));
};
var templateObject_1$q, templateObject_2$j;

var GapFillQuestion = function (_a) {
  var className = _a.className,
      id = _a.id,
      visible = _a.visible,
      data = _a.data,
      _b = _a.mode,
      mode = _b === void 0 ? QuestionMode.Standalone : _b,
      questionIndex = _a.questionIndex,
      siblingQuestionCount = _a.siblingQuestionCount,
      showQuestionIndex = _a.showQuestionIndex,
      events = _a.events,
      context = _a.context,
      controlledSubmit = _a.controlledSubmit,
      controlledReveal = _a.controlledReveal,
      controlledReset = _a.controlledReset,
      dontValidate = _a.dontValidate,
      evaluator = _a.evaluator,
      evaluatedData = _a.evaluatedData,
      parentEvaluatedData = _a.parentEvaluatedData,
      tutorialId = _a.tutorialId,
      tutorialStep = _a.tutorialStep;
  var intl = useIntl();

  var getDefaultState = function () {
    return getDefaultGapFillState(data, context);
  };

  var getResetState = function () {
    return __assign(__assign({}, getDefaultState()), {
      id: id
    });
  };

  var _c = useReducer(gapFillRootReducer, getDefaultState()),
      state = _c[0],
      dispatch = _c[1];

  var type = state.type,
      submitRequested = state.submitRequested,
      submitted = state.submitted,
      validationErrorMsg = state.validationErrorMsg,
      validationErrorCode = state.validationErrorCode,
      answerCorrect = state.answerCorrect,
      revealAnswer = state.revealAnswer,
      wordBank = state.wordBank,
      wordBankNoDistractors = state.wordBankNoDistractors,
      gaps = state.gaps,
      responseDuplicates = state.responseDuplicates,
      feedback = state.feedback,
      answerSuggestions = state.answerSuggestions,
      answerSuggestionsControls = state.answerSuggestionsControls; // element ids

  var errorMsgDisplayID = id + "-error"; // active errors

  var errorNotFilledIn = validationErrorCode === GapFillValidateError.NotFilledIn;
  var errorSameAnswerRepeated = validationErrorCode === GapFillValidateError.SameAnswerRepeated; // feedback

  var feedbackMessage = feedback ? intl.formatMessage(feedback.descriptor, feedback.values) : ""; // question boilerplate

  useQuestion(id, evaluator, state, dispatch); // handle remote control coming from outer group

  useControlledQuestion({
    controlledSubmit: controlledSubmit,
    controlledReveal: controlledReveal,
    controlledReset: controlledReset
  }, state, dispatch, getResetState);
  useEvaluatedQuestion(evaluatedData, dispatch, state, mergeEvaluatedState$1, data);
  useEvaluatedQuestion(parentEvaluatedData, dispatch, state, mergeEvaluatedState$1, data, EvaluatedQuestionType.Parent);

  if (!initQuestion(data, id, visible, state, dispatch, getDefaultState)) {
    return null;
  }

  var buildUserAnswer = function () {
    return Object.fromEntries(gaps.map(function (gap) {
      var _a;

      return [gap.inputVarName, (_a = gap.response) !== null && _a !== void 0 ? _a : ""];
    }));
  }; // this will validate and evaluate / reveal the user answer and update state accordingly


  questionHandler({
    mode: mode,
    id: id,
    tutorialId: tutorialId,
    tutorialStep: tutorialStep,
    data: data,
    events: events,
    state: state,
    dispatch: dispatch,
    evaluator: evaluator,
    // preprocess inputs before validation
    preprocessing: function () {
      // typed gaps need to be trimmed of excess spaces
      var changed = false;

      if (submitRequested && type === GapFillType.TypeIn) {
        for (var _i = 0, gaps_1 = gaps; _i < gaps_1.length; _i++) {
          var gap = gaps_1[_i];

          if (gap.response) {
            var response = gap.response.trim().replace(/  +/g, " ");

            if (gap.response !== response) {
              dispatch(gapFillSetResponse(gap.index, response));
              changed = true;
            }
          }
        }
      }

      return changed;
    },
    // declare validation requirements
    validation: dontValidate ? undefined : [{
      errorMessage: {
        descriptor: messagesGapFill.validationNotFilledIn
      },
      errorCode: GapFillValidateError.NotFilledIn,
      condition: function () {
        return gaps.some(function (gap) {
          return !gap.response;
        });
      }
    }, {
      errorMessage: {
        descriptor: messagesGapFill.validationSameAnswerRepeated
      },
      errorCode: GapFillValidateError.SameAnswerRepeated,
      condition: function () {
        return !!wordBank && !!responseDuplicates && responseDuplicates.length > 0;
      }
    }],
    // declare how the user answer is extracted from the UI controls
    buildUserAnswer: buildUserAnswer,
    // declare what to do when user answer is evaluated
    onEvaluate: function (evaluation) {
      // the default handler would have set "correctness" and feedback
      // but wordbank with no distractors uses a different feedback mechanism so as
      // not to give away which individual items are wrong
      var correct = evaluation.correct;
      var systemFeedback;

      if (!correct && correct !== null && wordBank && wordBankNoDistractors) {
        var wrongCount = Object.entries(evaluation.input_correctness).filter(function (entry) {
          return !entry[1];
        }).length;
        systemFeedback = {
          descriptor: messagesQuestion.feedbackAnswerIncorrect
        };
        var feedback_1 = {
          descriptor: messagesQuestion.feedbackWrongAnswersSpecific,
          values: {
            wrongCount: wrongCount
          }
        };
        dispatch(questionSetFeedback(feedback_1, systemFeedback));
      } // gapfill specific evaluation


      dispatch(gapFillEvaluate(evaluation.input_correctness));
    },
    // declare what to do when the revealed answer comes back from the evaluation service
    onRevealAnswer: function (solution) {
      if (solution.correct_answers) {
        dispatch(gapFillSetCorrectAnswers(solution.correct_answers));
      }
    }
  });
  var gapIndex = 0;
  var questionText = processText(data.stem, context);
  var firstSuggestionAnswerId;
  return /*#__PURE__*/React.createElement(QuestionContainer, {
    className: className,
    id: id,
    mode: mode,
    questionIndex: questionIndex,
    siblingQuestionCount: siblingQuestionCount,
    controlsMode: QuestionSubmitMode.CheckAnswer,
    // in child mode (e.g. summative) correctness feedback is freely given on submit
    // so no need to obfuscate with e.g. "2 of your answers are incorrect"
    feedbackMessage: mode === QuestionMode.Child ? undefined : feedbackMessage,
    feedbackNumAnswers: gaps.length,
    feedbackWrongAnswersIndicated: !(!!wordBank && wordBankNoDistractors),
    getResetQuestionState: getResetState,
    errorMsgDisplayID: errorMsgDisplayID,
    questionTypeLabel: intl.formatMessage(messagesQuestion.typeGapFill),
    state: state,
    dispatch: dispatch,
    events: events,
    isGapFill: true,
    fixedPrologue: true,
    prologue: wordBank ? /*#__PURE__*/React.createElement(GapFillWordBank, {
      wordBank: wordBank,
      gaps: gaps
    }) : undefined,
    body: /*#__PURE__*/React.createElement(QuestionText, {
      isGapFill: true
    }, HTMLReactParser(insertSpanBeforeMarkup(questionText.replace(gapPattern, "<gap></gap>"), questionIndex !== undefined && showQuestionIndex ? "<span>" + (questionIndex + 1) + ". </span>" : ""), {
      replace: function (domNode) {
        var _a, _b;

        if (domNode.name !== "gap") {
          return;
        }

        var i = gapIndex++;
        var gap = gaps[i];
        var suggestions = answerSuggestions === null || answerSuggestions === void 0 ? void 0 : answerSuggestions[gap.inputVarName];

        if (suggestions && !firstSuggestionAnswerId) {
          firstSuggestionAnswerId = gap.inputVarName;
        }

        if (suggestions && answerSuggestionsControls && answerSuggestionsControls[gap.inputVarName].dismissed) {
          suggestions = undefined;
        }

        return submitted ? /*#__PURE__*/React.createElement(GapFillGapFeedback, {
          gap: gap,
          suppressFeedback: answerCorrect === false && !revealAnswer && !!wordBank && !!wordBankNoDistractors && mode !== QuestionMode.Child
        }) : type === GapFillType.TypeIn ? /*#__PURE__*/React.createElement(GapFillTypedGap, {
          id: id + "-gap-" + i,
          gapIndex: i,
          response: gap.response,
          gapNextChar: gap.nextChar,
          validationError: !!validationErrorMsg && !gap.response,
          errorMsgDisplayID: errorMsgDisplayID,
          onGapChange: function (value, index) {
            var _a;

            dispatch(gapFillSetResponse(index, value)); // todo some kind of thunk to get updated answer and avoid closure

            var userAnswer = buildUserAnswer();
            userAnswer[gaps[index].inputVarName] = value;
            (_a = events === null || events === void 0 ? void 0 : events.onAnswerGiven) === null || _a === void 0 ? void 0 : _a.call(events, id, isGapQuestionComplete(gaps, value, index), userAnswer, {
              debounce: true
            });
          },
          isFirstSuggestion: !!firstSuggestionAnswerId && firstSuggestionAnswerId === gap.inputVarName,
          suggestions: suggestions,
          onCloseSuggestion: function () {
            dispatch(questionDismissAnswerSuggestion(gap.inputVarName)); // this will cause any other suggestion panels to reposition themselves

            defer(function () {
              return window.dispatchEvent(new Event("resize"));
            });
          }
        }) : /*#__PURE__*/React.createElement(GapFillDropDownGap$1, {
          id: id + "-gap-" + i,
          gapIndex: i,
          response: gap.response,
          gapNextChar: gap.nextChar,
          validationError: errorNotFilledIn && !gap.response || errorSameAnswerRepeated && gap.response !== unanswered && (responseDuplicates === null || responseDuplicates === void 0 ? void 0 : responseDuplicates.indexOf(gap.response)) !== -1,
          errorMsgDisplayID: errorMsgDisplayID,
          options: (_a = gap.options) !== null && _a !== void 0 ? _a : [],
          optionsOrder: (_b = gap.optionsOrder) !== null && _b !== void 0 ? _b : [],
          onGapChange: function (value, index) {
            var _a;

            dispatch(gapFillSetResponse(index, value));
            var userAnswer = buildUserAnswer();
            userAnswer[gaps[index].inputVarName] = value;
            (_a = events === null || events === void 0 ? void 0 : events.onAnswerGiven) === null || _a === void 0 ? void 0 : _a.call(events, id, isGapQuestionComplete(gaps, value, index), userAnswer);
          }
        });
      }
    }))
  });
};

var isGapQuestionComplete = function (gaps, response, index) {
  var responses = gaps.map(function (gap) {
    return gap.response;
  });
  responses[index] = response;
  return responses.every(function (response) {
    return response && response.trim().length > 0;
  });
};

var GapFill = function (props) {
  return /*#__PURE__*/React.createElement(GapFillQuestion, __assign({}, props));
};

// action types
var SUBMIT_COMPARE_SET_RESPONSE = "SUBMIT_COMPARE_SET_RESPONSE";
var SUBMIT_COMPARE_SHOW_SELF_ASSESSMENT = "SUBMIT_COMPARE_SHOW_SELF_ASSESSMENT";
var SUBMIT_COMPARE_SET_SELF_ASSESSMENT = "SUBMIT_COMPARE_SET_SELF_ASSESSMENT"; // action creators
// set the user's answer

var submitCompareSetResponse = function (response) {
  return {
    type: SUBMIT_COMPARE_SET_RESPONSE,
    response: response
  };
}; // if self assessment active, this shows 2 secs after model answer revealed

var submitCompareShowSelfAssessment = function (showSelfAssessment) {
  return {
    type: SUBMIT_COMPARE_SHOW_SELF_ASSESSMENT,
    showSelfAssessment: showSelfAssessment
  };
}; // the user's assessment of their own work e.g. Correct? "Yes", "Partly" etc

var submitCompareSetSelfAssessment = function (selfAssessment) {
  return {
    type: SUBMIT_COMPARE_SET_SELF_ASSESSMENT,
    selfAssessment: selfAssessment
  };
};

var submitCompareStateReducer = function (state, action) {
  switch (action.type) {
    case SUBMIT_COMPARE_SET_RESPONSE:
      return __assign(__assign({}, state), {
        response: action.response
      });

    case SUBMIT_COMPARE_SHOW_SELF_ASSESSMENT:
      return __assign(__assign({}, state), {
        showSelfAssessment: action.showSelfAssessment
      });

    case SUBMIT_COMPARE_SET_SELF_ASSESSMENT:
      return __assign(__assign({}, state), {
        selfAssessment: action.selfAssessment
      });

    default:
      return state;
  }
};

var getDefaultSubmitCompareState = function () {
  var defaults = getDefaultQuestionState();
  return __assign(__assign({}, defaults), {
    response: unanswered,
    selfAssessment: undefined,
    showSelfAssessment: false
  });
};
var mergeEvaluatedState = function (state, evaluatedData, data) {
  var submitted = state.submitted,
      response = state.response,
      feedback = state.feedback,
      selfAssessment = state.selfAssessment,
      showSelfAssessment = state.showSelfAssessment,
      improvementStatus = state.improvementStatus,
      answerCorrect = state.answerCorrect;

  if (evaluatedData) {
    if (evaluatedData.answers) {
      var inputKey = Object.keys(evaluatedData.answers)[0];
      var val = evaluatedData.answers[inputKey];

      if (val) {
        response = val;
      }

      submitted = true;
      var selfEvals = data.inputs.filter(function (input) {
        return input.type === "yes_no";
      });
      var selfEvalValue = selfEvals.length && evaluatedData.answers[selfEvals[0].name];

      if (selfEvalValue) {
        selfAssessment = selfEvalValue;
        showSelfAssessment = true;
      }
    }

    if (evaluatedData.feedback) {
      feedback = evaluatedData.feedback;
    }

    if (evaluatedData.improvementStatus) {
      improvementStatus = evaluatedData.improvementStatus;
    }

    answerCorrect = evaluatedData.correct;
  }

  return __assign(__assign({}, state), {
    feedback: feedback,
    improvementStatus: improvementStatus,
    submitted: submitted,
    response: response,
    selfAssessment: selfAssessment,
    showSelfAssessment: showSelfAssessment,
    answerCorrect: answerCorrect
  });
};
var submitCompareRootReducer = function (state, action) {
  var newState = questionStateReducer(state, action);
  newState = submitCompareStateReducer(newState, action);
  return newState;
};

var Styled$n = {
  Container: styled.div.withConfig({
    displayName: "SelfAssessment__Container"
  })(templateObject_1$p || (templateObject_1$p = __makeTemplateObject(["\n    margin: 16px;\n  "], ["\n    margin: 16px;\n  "])))
};

var SelfAssessment = function (_a) {
  var id = _a.id,
      prompt = _a.prompt,
      choices = _a.choices,
      selectedChoice = _a.selectedChoice,
      feedback = _a.feedback,
      disabled = _a.disabled,
      onAssessment = _a.onAssessment;
  return /*#__PURE__*/React.createElement(Styled$n.Container, null, /*#__PURE__*/React.createElement(TagSelectorPanel, {
    id: id,
    prompt: prompt,
    options: choices,
    disabled: disabled,
    onSelect: function (option) {
      return onAssessment(option);
    },
    feedback: feedback,
    selectedOption: selectedChoice
  }));
};
var templateObject_1$p;

var InputBox = styled.textarea.withConfig({
  displayName: "SubmitCompareQuestion__InputBox"
})(templateObject_2$i || (templateObject_2$i = __makeTemplateObject(["\n  display: block;\n  width: 100%;\n  padding: 10px 16px;\n  margin-top: 16px;\n  margin-bottom: 32px;\n  background-color: #ffffff;\n  border: 1px solid rgba(0, 0, 0, 0.43);\n  border-radius: 3px;\n  height: 194px;\n  line-height: 1.5;\n  resize: none;\n  box-sizing: border-box;\n  color: #1c1c1c;\n  font-size: 1.6rem;\n  font-family: inherit;\n  &:focus {\n    border: 1px solid ", ";\n    box-shadow: 0 0 0 1px ", ";\n    outline: none;\n  }\n  ::placeholder {\n    font-weight: 300;\n    color: rgba(0, 0, 0, 0.57);\n  }\n  ", ";\n"], ["\n  display: block;\n  width: 100%;\n  padding: 10px 16px;\n  margin-top: 16px;\n  margin-bottom: 32px;\n  background-color: #ffffff;\n  border: 1px solid rgba(0, 0, 0, 0.43);\n  border-radius: 3px;\n  height: 194px;\n  line-height: 1.5;\n  resize: none;\n  box-sizing: border-box;\n  color: #1c1c1c;\n  font-size: 1.6rem;\n  font-family: inherit;\n  &:focus {\n    border: 1px solid ", ";\n    box-shadow: 0 0 0 1px ", ";\n    outline: none;\n  }\n  ::placeholder {\n    font-weight: 300;\n    color: rgba(0, 0, 0, 0.57);\n  }\n  ", ";\n"])), cloud.getShade(10), cloud.getShade(10), function (_a) {
  var submitted = _a.submitted;
  return submitted && css(templateObject_1$o || (templateObject_1$o = __makeTemplateObject(["\n      background: ", ";\n      border: 1px solid rgba(0, 0, 0, 0.43);\n    "], ["\n      background: ", ";\n      border: 1px solid rgba(0, 0, 0, 0.43);\n    "])), cloud.getShade(2));
}); // delay between model answer and self evaluation

var EVAL_DELAY = 2000;

var SubmitCompareQuestion = function (_a) {
  var className = _a.className,
      id = _a.id,
      visible = _a.visible,
      data = _a.data,
      context = _a.context,
      events = _a.events,
      _b = _a.mode,
      mode = _b === void 0 ? QuestionMode.Standalone : _b,
      questionIndex = _a.questionIndex,
      siblingQuestionCount = _a.siblingQuestionCount,
      controlledSubmit = _a.controlledSubmit,
      controlledReveal = _a.controlledReveal,
      controlledReset = _a.controlledReset,
      evaluator = _a.evaluator,
      evaluatedData = _a.evaluatedData,
      parentEvaluatedData = _a.parentEvaluatedData,
      tutorialId = _a.tutorialId,
      tutorialStep = _a.tutorialStep;
  var intl = useIntl();

  var getDefaultState = function () {
    return getDefaultSubmitCompareState();
  };

  var _c = useReducer(submitCompareRootReducer, getDefaultState()),
      state = _c[0],
      dispatch = _c[1];

  var submitRequested = state.submitRequested,
      submitted = state.submitted,
      response = state.response,
      showSelfAssessment = state.showSelfAssessment,
      selfAssessment = state.selfAssessment,
      validationErrorMsg = state.validationErrorMsg,
      feedback = state.feedback;
  var selfAssessed = data.question_type === "SelfGradedSubmitAndCompare";
  var feedbackMessage = feedback ? intl.formatMessage(feedback.descriptor, feedback.values) : ""; // question boilerplate

  useQuestion(id, evaluator, state, dispatch); // handle remote control coming from outer group

  useControlledQuestion({
    controlledSubmit: controlledSubmit,
    controlledReveal: controlledReveal,
    controlledReset: controlledReset
  }, state, dispatch, getDefaultState, selfAssessed && showSelfAssessment);
  useEffect(function () {
    if (submitted && selfAssessed && !showSelfAssessment) {
      var timer_1 = setTimeout(function () {
        dispatch(questionAriaLiveMessage(intl.formatMessage(messagesSubmitCompare.selfAssessmentAnnouncement)));
        dispatch(submitCompareShowSelfAssessment(true));
      }, EVAL_DELAY);
      return function () {
        clearTimeout(timer_1);
      };
    } else {
      return;
    }
  }, [submitted, showSelfAssessment, selfAssessed, intl]); // element ids

  var errorMsgDisplayID = id + "-error";
  var selfAssessmentID = id + "-self-assessment";
  useEvaluatedQuestion(evaluatedData, dispatch, state, mergeEvaluatedState, data);
  useEvaluatedQuestion(parentEvaluatedData, dispatch, state, mergeEvaluatedState, data, EvaluatedQuestionType.Parent);

  if (!initQuestion(data, id, visible, state, dispatch, getDefaultState)) {
    return null;
  }

  if (submitRequested && response) {
    dispatch(submitCompareSetResponse(response.trim()));
  } // this will validate and evaluate / reveal the user answer and update state accordingly


  questionHandler({
    mode: mode,
    id: id,
    tutorialId: tutorialId,
    tutorialStep: tutorialStep,
    data: data,
    events: events,
    state: state,
    dispatch: dispatch,
    // this has a two-step submit if selfAssessed (driven by dontEval), an initial submit that elicits
    // a self eval widget once the self eval is present and user responded we externally eval to get the final result
    dontEval: selfAssessed && !selfAssessment,
    evaluator: evaluator,
    // declare validation requirements
    validation: {
      errorMessage: {
        descriptor: messagesQuestion.validationTypeAnAnswer
      },
      condition: function () {
        return !(response === null || response === void 0 ? void 0 : response.trim().length);
      }
    },
    // declare how the user answer is extracted from the UI controls
    buildUserAnswer: function () {
      var _a, _b;

      var _c, _d;

      var textInput = (_c = data.inputs.filter(function (input) {
        return input.type === "line" || input.type === "box";
      })) === null || _c === void 0 ? void 0 : _c[0];
      var selfEvalInput = (_d = data.inputs.filter(function (input) {
        return input.type === "yes_no";
      })) === null || _d === void 0 ? void 0 : _d[0];
      return __assign(__assign({}, (textInput === null || textInput === void 0 ? void 0 : textInput.name) && (_a = {}, _a[textInput.name] = response !== null && response !== void 0 ? response : "", _a)), (selfEvalInput === null || selfEvalInput === void 0 ? void 0 : selfEvalInput.name) && (_b = {}, _b[selfEvalInput.name] = selfAssessment ? selfAssessment.toLowerCase() : "", _b));
    }
  });
  return /*#__PURE__*/React.createElement(QuestionContainer, {
    className: className,
    id: id,
    mode: mode,
    questionIndex: questionIndex,
    siblingQuestionCount: siblingQuestionCount,
    controlsVisible: !selfAssessed || !(submitted && !selfAssessment),
    secondaryControlsVisible: false,
    controlsMode: QuestionSubmitMode.SubmitAnswer,
    feedbackNumAnswers: 1,
    feedbackWrongAnswersIndicated: true,
    feedbackMessage: selfAssessed ? data.model_answer : feedbackMessage,
    systemFeedbackMessage: selfAssessed ? intl.formatMessage(messagesSubmitCompare.compareWithExpert) : intl.formatMessage(messagesSubmitCompare.answerSaved),
    feedbackNeutral: selfAssessed,
    errorMsgDisplayID: errorMsgDisplayID,
    questionTypeLabel: intl.formatMessage(messagesQuestion.typeSubmitCompare),
    getResetQuestionState: getDefaultState,
    state: state,
    dispatch: dispatch,
    events: events,
    prologue: /*#__PURE__*/React.createElement(QuestionText, {
      text: processText(data.stem, context)
    }),
    body: /*#__PURE__*/React.createElement(InputBox, {
      value: response !== null && response !== void 0 ? response : "",
      disabled: submitted,
      spellCheck: "false",
      autoComplete: "off",
      "aria-invalid": validationErrorMsg ? true : undefined,
      "aria-describedby": validationErrorMsg ? errorMsgDisplayID : undefined,
      "aria-label": intl.formatMessage(messagesSubmitCompare.inputFieldLabel),
      placeholder: intl.formatMessage(messagesQuestion.controlsTypedPlaceHolder),
      submitted: submitted,
      onChange: function (event) {
        dispatch(submitCompareSetResponse(event.currentTarget.value));
      }
    }),
    epilogue: submitted && showSelfAssessment ? /*#__PURE__*/React.createElement(SelfAssessment, {
      id: selfAssessmentID,
      prompt: data.grading_prompt,
      selectedChoice: selfAssessment,
      choices: [{
        option: "Yes",
        value: "yes"
      }, {
        option: "No",
        value: "no"
      }],
      onAssessment: function (choice) {
        dispatch(submitCompareSetSelfAssessment(choice)); // now that we have the second part of the input vars, the users self assessment
        // we can go out to the eval service to get feedback

        dispatch(questionSubmitRequested(true));
      },
      feedback: feedbackMessage,
      disabled: !!selfAssessment
    }) : undefined
  });
};
var templateObject_1$o, templateObject_2$i;

/**
 * question boilerplate for init
 */

var initQuestion = function (data, questionId, visible, state, dispatch, defaultStateSupplier) {
  var id = state.id,
      startTime = state.startTime;

  if (questionId !== id) {
    // reset any local question state here as new question might be rendered
    // by the same component if the question types match
    dispatch(questionReset(__assign(__assign({}, defaultStateSupplier()), {
      id: questionId
    })));
  }

  if (!visible) {
    return false;
  }

  if (startTime === NOT_STARTED) {
    dispatch(questionStartTime(Date.now()));
  }

  return true;
};
/**
 * Question handling boilerplate that acts generically across multiple question types
 * turns changes in state brought on by UI events into further changes in state
 * Cf. questionStateReducer
 * returns true if a condition was handled, or false it it wasn't
 * todo convert to hook, c.f. useQuestion
 */

var questionHandler = function (_a) {
  var mode = _a.mode,
      id = _a.id,
      tutorialId = _a.tutorialId,
      tutorialStep = _a.tutorialStep,
      events = _a.events,
      _b = _a.state,
      submitRequested = _b.submitRequested,
      validationErrorMsg = _b.validationErrorMsg,
      revealAnswer = _b.revealAnswer,
      answerRevealed = _b.answerRevealed,
      focusQuestionStart = _b.focusQuestionStart,
      locked = _b.locked,
      answerSuggestions = _b.answerSuggestions,
      dispatch = _a.dispatch,
      dontEval = _a.dontEval,
      preprocessing = _a.preprocessing,
      validation = _a.validation,
      buildUserAnswer = _a.buildUserAnswer,
      evaluator = _a.evaluator,
      onEvaluate = _a.onEvaluate,
      onRevealAnswer = _a.onRevealAnswer;

  if (focusQuestionStart) {
    // reset for next time
    dispatch(questionFocusStart(false));
  }

  if (preprocessing && submitRequested) {
    var change = preprocessing();

    if (change) {
      dispatch(questionSubmitRequested(false)); // so validation can take place on updated values

      defer(function () {
        return dispatch(questionSubmitRequested(true));
      });
      return true;
    }
  }

  var validations = [].concat(validation ? validation : []);

  if (submitRequested) {
    // make sure the ui is in a valid state to submit
    var validationIssue = validations.find(function (v) {
      return v.condition();
    });

    if (validationIssue) {
      dispatch(questionError(validationIssue.errorMessage, validationIssue.errorCode));
      dispatch(questionSubmitRequested(false));
      defer(function () {
        var _a;

        return (_a = events === null || events === void 0 ? void 0 : events.onValidate) === null || _a === void 0 ? void 0 : _a.call(events, id, false);
      });
      return true;
    } else {
      defer(function () {
        var _a;

        return (_a = events === null || events === void 0 ? void 0 : events.onValidate) === null || _a === void 0 ? void 0 : _a.call(events, id, true);
      });
    }
  }

  if (validationErrorMsg && !validations.find(function (v) {
    return v.condition();
  })) {
    // if in validation error state and error state is no longer present, remove error message
    dispatch(questionError());
    return true;
  }

  if (submitRequested) {
    // validation has passed, so we can now evaluate the user's answer
    dispatch(questionSubmitRequested(false));

    if (dontEval) {
      // a kind of fake eval in two-step evals (e.g. submit compare)
      dispatch(questionSubmitted(true));
      defer(function () {
        var _a;

        return (_a = events === null || events === void 0 ? void 0 : events.onSubmitPrep) === null || _a === void 0 ? void 0 : _a.call(events, id);
      });
      return true;
    }

    dispatch(questionAttempt());

    if (mode === QuestionMode.Standalone) {
      dispatch(questionLock(QuestionLockedReason.Waiting));
    } // ask the client to turn its UI inputs into a inputVar map to pass on for evaluation


    var userAnswer_1 = buildUserAnswer(); // call the evaluation service

    defer(function () {
      var _a;

      return (_a = events === null || events === void 0 ? void 0 : events.onEvalStart) === null || _a === void 0 ? void 0 : _a.call(events, id);
    });
    evaluator.evaluateResponse(id, userAnswer_1, tutorialId, tutorialStep, {
      skipSuggestions: !!answerSuggestions
    }).then(function (evaluation) {
      if (evaluation.suggestions) {
        // suggestions are a form of validation, full evaluation becomes postponed
        // whilst we ask the user to check their answer in the context of some suggestions
        dispatch(questionSetAnswerSuggestions(evaluation.suggestions));

        if (mode === QuestionMode.Standalone) {
          dispatch(questionUnlock());
        } // notify parent eval has not gone through yet and another step is needed


        defer(function () {
          var _a;

          return (_a = events === null || events === void 0 ? void 0 : events.onEvalDeferred) === null || _a === void 0 ? void 0 : _a.call(events, id);
        });
        return;
      }

      var correct = evaluation.correct;
      var feedback = evaluation.feedback;
      dispatch(questionAssessed(correct));
      dispatch(questionSetFeedback(feedback !== null && feedback !== void 0 ? feedback : undefined));
      dispatch(questionSubmitted(true));

      if (mode === QuestionMode.Standalone) {
        dispatch(questionUnlock());
      }

      defer(function () {
        // notify the client question evaluation has taken place if it wants to know
        onEvaluate === null || onEvaluate === void 0 ? void 0 : onEvaluate(evaluation); // possibly notify externally (e.g. Tutorial) evaluation has just occurred

        defer(function () {
          var _a, _b;

          return (_a = events === null || events === void 0 ? void 0 : events.onSubmit) === null || _a === void 0 ? void 0 : _a.call(events, id, correct, userAnswer_1, (_b = evaluation.followup) === null || _b === void 0 ? void 0 : _b.step);
        }); // if submit was driven by user interaction then clear that state

        defer(function () {
          return dispatch(questionClearSubmitActive());
        });
      });
    }).catch(function (e) {
      dispatch(questionUnlock());
      dispatch(questionSystemError({
        type: QuestionSystemErrorType.EvaluationError,
        detail: e.message
      }));
    });
    return true;
  }

  if (revealAnswer && !locked && !answerRevealed) {
    dispatch(questionLock(QuestionLockedReason.Waiting));
    evaluator.revealAnswer(id).then(function (solution) {
      if (!solution.correct_answers) {
        dispatch(questionSystemError({
          type: QuestionSystemErrorType.RevealAnswerError,
          detail: "Missing correct_answers value"
        }));
      } else {
        dispatch(questionAnswerRevealed(true));
        dispatch(questionUnlock());
        onRevealAnswer === null || onRevealAnswer === void 0 ? void 0 : onRevealAnswer(solution);
      }
    }).catch(function (e) {
      dispatch(questionRevealAnswer(false));
      dispatch(questionUnlock());
      dispatch(questionSystemError({
        type: QuestionSystemErrorType.RevealAnswerError,
        detail: e.message
      }));
    });
    return true;
  }

  return false;
};
var questionLookup = {
  MultipleChoice: MultipleChoiceQuestion,
  TextEntry: GapFill,
  Pulldown: GapFill,
  FreeResponse: SubmitCompareQuestion,
  SelfGradedSubmitAndCompare: SubmitCompareQuestion,
  Matching: GapFill,
  // these are not implemented yet, using them will throw an error
  YesNo: null,
  HotText: null,
  MultipleChoiceMultipleSelect: null
};
var questionCorrectnessToQuestionState = function (correct) {
  switch (correct) {
    case null:
      return QuestionStatus.Complete;

    case true:
      return QuestionStatus.Correct;

    case false:
      return QuestionStatus.Incorrect;

    default:
      return QuestionStatus.Incomplete;
  }
};

var questionGroupStateReducer = function (state, action) {
  var _a;

  switch (action.type) {
    case "QUESTION_GROUP_SET_ITEM_INDEX":
      {
        return __assign(__assign({}, state), {
          itemIndex: action.itemIndex
        });
      }

    case "QUESTION_GROUP_ANSWER_GIVEN_ITEM":
      {
        var item = state.itemStates[action.id];

        if (item.answerGiven !== action.answerGiven || !isEqual(item.userAnswer, action.userAnswer)) {
          var newItemStates = __assign({}, state.itemStates);

          newItemStates[action.id].answerGiven = action.answerGiven;
          newItemStates[action.id].userAnswer = action.userAnswer;
          return __assign(__assign({}, state), {
            itemStates: newItemStates
          });
        } else {
          return state;
        }
      }

    case "QUESTION_GROUP_SUBMIT_ITEM":
      {
        var newItemStates = __assign({}, state.itemStates);

        newItemStates[action.id].correct = action.correct;
        newItemStates[action.id].inputVars = action.inputVars;
        return __assign(__assign({}, state), {
          itemStates: newItemStates
        });
      }

    case "QUESTION_GROUP_RESET_ITEM":
      {
        var newItemStates = __assign({}, state.itemStates);

        newItemStates[action.id].correct = undefined;
        newItemStates[action.id].inputVars = {};
        return __assign(__assign({}, state), {
          itemStates: newItemStates
        });
      }

    case "QUESTION_GROUP_SUBMIT_REQUESTED":
      {
        return __assign(__assign({}, state), {
          submitRequested: action.submitRequested
        });
      }

    case "QUESTION_GROUP_SUBMITTED":
      {
        return __assign(__assign({}, state), {
          submitted: action.submitted,
          submitRequested: false
        });
      }

    case "QUESTION_GROUP_EVALUATED":
      {
        return __assign(__assign({}, state), {
          result: (_a = action.result.assessment) !== null && _a !== void 0 ? _a : null,
          questionEvaluations: Object.keys(action.result.questions).reduce(function (acc, questionId) {
            acc[questionId] = action.result.questions[questionId];
            acc[questionId].answers = state.itemStates[questionId].userAnswer;
            return acc;
          }, {}),
          itemStates: Object.keys(action.result.questions).reduce(function (acc, questionId) {
            acc[questionId] = __assign(__assign({}, state.itemStates[questionId]), {
              correct: action.result.questions[questionId].correct
            });
            return acc;
          }, {})
        });
      }

    case "QUESTION_GROUP_ANSWER_REVEALED":
      {
        return __assign(__assign({}, state), {
          answerRevealed: action.answerRevealed
        });
      }

    case "QUESTION_GROUP_RESET":
      {
        if (action.reset === false) {
          return __assign(__assign({}, state), {
            reset: false
          });
        } else {
          // the reset true also triggers reset in the underlying questions
          return __assign(__assign({}, action.reset), {
            reset: true,
            answerRevealed: false,
            submitted: false,
            result: null,
            questionEvaluations: undefined
          });
        }
      }

    case "QUESTION_GROUP_LOCK":
      if (!state.locked) {
        return __assign(__assign({}, state), {
          locked: true
        });
      } else {
        return state;
      }

    case "QUESTION_GROUP_UNLOCK":
      if (state.locked) {
        return __assign(__assign({}, state), {
          locked: false,
          lockedReason: undefined
        });
      } else {
        return state;
      }

    case "QUESTION_GROUP_SYSTEM_ERROR":
      {
        return __assign(__assign({}, state), {
          systemError: action.error
        });
      }

    case "QUESTION_GROUP_CLEAR_SYSTEM_ERROR":
      {
        return __assign(__assign({}, state), {
          systemError: undefined
        });
      }

    case "QUESTION_GROUP_SEND_ARIA_LIVE_MESSAGE":
      {
        return __assign(__assign({}, state), {
          ariaLiveMessage: action.message,
          ariaLiveMode: action.mode
        });
      }

    default:
      return state;
  }
};

var QuestionGroupLockedReason;

(function (QuestionGroupLockedReason) {
  QuestionGroupLockedReason["Waiting"] = "Waiting";
})(QuestionGroupLockedReason || (QuestionGroupLockedReason = {}));

var getDefaultQuestionGroupState = function (questions, evaluatedData, questionIndex) {
  var _a;

  var state = {
    itemStates: questions.reduce(function (acc, question) {
      var id = getQuestionLikeId(question);
      var questionState = evaluatedData === null || evaluatedData === void 0 ? void 0 : evaluatedData.questions[id];

      if (isTutorial(question)) {
        acc[id] = getTutorialLastItemState(question, evaluatedData);
      } else {
        acc[id] = questionState && questionState.answers ? {
          correct: questionState.correct,
          inputVars: questionState.answers,
          answerGiven: true,
          userAnswer: questionState.answers
        } : {
          correct: (evaluatedData === null || evaluatedData === void 0 ? void 0 : evaluatedData.assessment) ? false : undefined,
          inputVars: {},
          answerGiven: false,
          userAnswer: {}
        };
      }

      return acc;
    }, {}),
    itemIndex: questionIndex !== null && questionIndex !== void 0 ? questionIndex : 0,
    submitted: false,
    submitRequested: false,
    result: null,
    answerRevealed: false,
    reset: false,
    locked: false
  };

  if (typeof ((_a = evaluatedData === null || evaluatedData === void 0 ? void 0 : evaluatedData.assessment) === null || _a === void 0 ? void 0 : _a.score) === "number") {
    state.submitted = true;
    state.result = evaluatedData.assessment;
  }

  return state;
};
var questionGroupStateRootReducer = function (state, action) {
  return questionGroupStateReducer(state, action);
};

var walkTutorialQuestions = function (step, ids, depth) {
  if (step.question) {
    ids.push({
      id: step.question.id,
      depth: depth
    });

    if (step.followup) {
      for (var _i = 0, _a = step.followup; _i < _a.length; _i++) {
        var followup = _a[_i];

        if (followup.step) {
          walkTutorialQuestions(followup.step, ids, depth + 1);
        }
      }
    }
  }
};

var getTutorialLastItemState = function (question, evaluatedData) {
  if (!evaluatedData) {
    return {
      correct: undefined,
      inputVars: {},
      answerGiven: false,
      userAnswer: {}
    };
  } else {
    var hierarchy = [];
    walkTutorialQuestions(question.step, hierarchy, 0);
    hierarchy = hierarchy.reverse();

    for (var _i = 0, hierarchy_1 = hierarchy; _i < hierarchy_1.length; _i++) {
      var item = hierarchy_1[_i];
      var questionState = evaluatedData === null || evaluatedData === void 0 ? void 0 : evaluatedData.questions[item.id];

      if (questionState && questionState.answers) {
        return {
          correct: questionState.correct,
          inputVars: questionState.answers,
          answerGiven: true,
          userAnswer: questionState.answers
        };
      }
    }

    return {
      correct: undefined,
      inputVars: {},
      answerGiven: false,
      userAnswer: {}
    };
  }
};

var isTutorial = function (question) {
  return question.object_type === "Tutorial" && !!question.step.question;
};
var isQuestion = function (question) {
  return !!question.question_type;
};
var getQuestionLikeId = function (question) {
  if (isQuestion(question) || isTutorial(question)) {
    return question.id;
  } else {
    throw new Error("Cannot determine id for a question like object");
  }
};
var QuestionGroupContext = /*#__PURE__*/React.createContext(null);

var QuestionGroupContextProvider = function (_a) {
  var groupId = _a.groupId,
      questions = _a.questions,
      questionIndex = _a.questionIndex,
      children = _a.children,
      evaluator = _a.evaluator,
      evaluatedData = _a.evaluatedData,
      hostApi = _a.hostApi,
      bookLocation = _a.bookLocation;

  var _b = useReducer(questionGroupStateRootReducer, getDefaultQuestionGroupState(questions, evaluatedData, questionIndex)),
      state = _b[0],
      dispatch = _b[1];

  var contextValue = useMemo(function () {
    return {
      groupId: groupId,
      state: state,
      dispatch: dispatch,
      questions: questions,
      evaluator: evaluator,
      evaluatedData: evaluatedData,
      hostApi: hostApi,
      bookLocation: bookLocation
    };
  }, [groupId, state, questions, evaluator, evaluatedData, hostApi, bookLocation]);
  return /*#__PURE__*/React.createElement(QuestionGroupContext.Provider, {
    value: contextValue
  }, children);
};

// action types
var QUESTION_GROUP_SET_ITEM_INDEX = "QUESTION_GROUP_SET_ITEM_INDEX";
var QUESTION_GROUP_ANSWER_GIVEN_ITEM = "QUESTION_GROUP_ANSWER_GIVEN_ITEM";
var QUESTION_GROUP_SUBMIT_ITEM = "QUESTION_GROUP_SUBMIT_ITEM";
var QUESTION_GROUP_RESET_ITEM = "QUESTION_GROUP_RESET_ITEM";
var QUESTION_GROUP_SUBMIT_REQUESTED = "QUESTION_GROUP_SUBMIT_REQUESTED";
var QUESTION_GROUP_SUBMITTED = "QUESTION_GROUP_SUBMITTED";
var QUESTION_GROUP_EVALUATED = "QUESTION_GROUP_EVALUATED";
var QUESTION_GROUP_ANSWER_REVEALED = "QUESTION_GROUP_ANSWER_REVEALED";
var QUESTION_GROUP_RESET = "QUESTION_GROUP_RESET";
var QUESTION_GROUP_LOCK = "QUESTION_GROUP_LOCK";
var QUESTION_GROUP_UNLOCK = "QUESTION_GROUP_UNLOCK";
var QUESTION_GROUP_SYSTEM_ERROR = "QUESTION_GROUP_SYSTEM_ERROR";
var QUESTION_GROUP_CLEAR_SYSTEM_ERROR = "QUESTION_GROUP_CLEAR_SYSTEM_ERROR";
var QUESTION_GROUP_SEND_ARIA_LIVE_MESSAGE = "QUESTION_GROUP_SEND_ARIA_LIVE_MESSAGE"; // action creators

var questionGroupSetItemIndexAction = function (itemIndex) {
  return {
    type: QUESTION_GROUP_SET_ITEM_INDEX,
    itemIndex: itemIndex
  };
};
var questionGroupAnswerGivenItemAction = function (id, answerGiven, userAnswer) {
  return {
    type: QUESTION_GROUP_ANSWER_GIVEN_ITEM,
    id: id,
    answerGiven: answerGiven,
    userAnswer: userAnswer
  };
};
var questionGroupSubmitItemAction = function (id, correct, inputVars) {
  return {
    type: QUESTION_GROUP_SUBMIT_ITEM,
    id: id,
    correct: correct,
    inputVars: inputVars
  };
};
var questionGroupResetItemAction = function (id) {
  return {
    type: QUESTION_GROUP_RESET_ITEM,
    id: id
  };
};
var questionGroupSubmitRequestedAction = function (submitRequested) {
  return {
    type: QUESTION_GROUP_SUBMIT_REQUESTED,
    submitRequested: submitRequested
  };
};
var questionGroupSubmittedAction = function (submitted) {
  return {
    type: QUESTION_GROUP_SUBMITTED,
    submitted: submitted
  };
};
var questionGroupEvaluatedAction = function (result) {
  return {
    type: QUESTION_GROUP_EVALUATED,
    result: result
  };
};
var questionGroupRevealAnswerAction = function (answerRevealed) {
  return {
    type: QUESTION_GROUP_ANSWER_REVEALED,
    answerRevealed: answerRevealed
  };
};
var questionGroupResetAction = function (reset) {
  return {
    type: QUESTION_GROUP_RESET,
    reset: reset
  };
};
var questionGroupLock = function (lockedReason) {
  return {
    type: QUESTION_GROUP_LOCK,
    lockedReason: lockedReason
  };
};
var questionGroupUnlock = function () {
  return {
    type: QUESTION_GROUP_UNLOCK
  };
};
var questionGroupSystemErrorAction = function (error) {
  return {
    type: QUESTION_GROUP_SYSTEM_ERROR,
    error: error
  };
};
var questionGroupClearSystemErrorAction = function () {
  return {
    type: QUESTION_GROUP_CLEAR_SYSTEM_ERROR
  };
};
var questionGroupSendAriaLiveMessageAction = function (message, mode) {
  if (mode === void 0) {
    mode = AriaLiveMode.Polite;
  }

  return {
    type: QUESTION_GROUP_SEND_ARIA_LIVE_MESSAGE,
    message: message,
    mode: mode
  };
};

var useQuestionGroupContext = function () {
  var context = useContext(QuestionGroupContext);

  if (!context) {
    // question group ancestor must include a QuestionGroupContextProvider
    throw new Error("Found missing question group context in question group");
  }

  var state = context.state,
      dispatch = context.dispatch,
      questions = context.questions,
      evaluator = context.evaluator,
      evaluatedData = context.evaluatedData,
      hostApi = context.hostApi,
      groupId = context.groupId,
      bookLocation = context.bookLocation;
  var itemIndex = state.itemIndex,
      itemStates = state.itemStates,
      submitRequested = state.submitRequested,
      submitted = state.submitted,
      result = state.result,
      questionEvaluations = state.questionEvaluations,
      answerRevealed = state.answerRevealed,
      reset = state.reset,
      locked = state.locked,
      systemError = state.systemError,
      ariaLiveMessage = state.ariaLiveMessage,
      ariaLiveMode = state.ariaLiveMode;

  var setItemIndex = function (itemIndex) {
    dispatch(questionGroupSetItemIndexAction(itemIndex));
  };

  var setItemAnswerGiven = function (id, answerGiven, userAnswer) {
    dispatch(questionGroupAnswerGivenItemAction(id, answerGiven, userAnswer));
  };

  var submitItem = function (id, correct, userAnswer) {
    dispatch(questionGroupSubmitItemAction(id, correct, userAnswer));
  };

  var resetItem = function (id) {
    dispatch(questionGroupResetItemAction(id));
  };

  var setSubmitRequested = function (submitRequested) {
    dispatch(questionGroupSubmitRequestedAction(submitRequested));
  };

  var setSubmitted = function (submitted) {
    dispatch(questionGroupSubmittedAction(submitted));
  };

  var setEvaluatedResult = function (result) {
    dispatch(questionGroupEvaluatedAction(result));
  };

  var setAnswerRevealed = function (answerRevealed) {
    dispatch(questionGroupRevealAnswerAction(answerRevealed));
  };

  var setReset = function (reset) {
    if (reset) {
      dispatch(questionGroupResetAction(getDefaultQuestionGroupState(questions)));
    } else {
      dispatch(questionGroupResetAction(false));
    }
  };

  var setLocked = function (lockedReason) {
    dispatch(questionGroupLock(lockedReason));
  };

  var setUnlocked = function () {
    dispatch(questionGroupUnlock());
  };

  var notifySystemError = function (error) {
    dispatch(questionGroupSystemErrorAction(error));
  };

  var clearNotifySystemError = function () {
    dispatch(questionGroupClearSystemErrorAction());
  };

  var sendAriaLiveMessage = function (message, mode) {
    if (mode === void 0) {
      mode = AriaLiveMode.Polite;
    }

    dispatch(questionGroupSendAriaLiveMessageAction(message, mode));
  };

  var clearAriaLiveMessage = function () {
    dispatch(questionGroupSendAriaLiveMessageAction(""));
  };

  useEffect(function () {
    if (systemError) {
      defer(function () {
        return dispatch(questionGroupClearSystemErrorAction());
      });
    }
  });
  var currentItemId = getQuestionLikeId(questions[itemIndex]);
  var currentItemStatus = questionCorrectnessToQuestionState(itemStates[currentItemId].correct);
  var totalCorrect = result ? result.correct_questions : 0;
  var totalIncorrect = result ? result.incorrect_questions : 0;
  var totalAnswersGiven = 0;
  questions.forEach(function (question) {
    var state = itemStates[getQuestionLikeId(question)];

    if (state.answerGiven) {
      totalAnswersGiven++;
    }
  });
  var totalIncomplete = questions.length - totalAnswersGiven;
  var numQuestions = questions.length;
  var totalQuestionsUnanswered = numQuestions - totalAnswersGiven;
  var allQuestionsAreAnswered = totalAnswersGiven === numQuestions;
  var scorePercent = result ? result.percent_correct : 0;
  var incorrectlyAnsweredQuestions = questions.filter(function (question) {
    var state = itemStates[getQuestionLikeId(question)];
    return submitted && (state.correct === false || !state.answerGiven);
  }).map(function (question) {
    return question.id;
  });
  return {
    groupId: groupId,
    bookLocation: bookLocation,
    questions: questions,
    evaluator: evaluator,
    itemIndex: itemIndex,
    setItemIndex: setItemIndex,
    submitItem: submitItem,
    resetItem: resetItem,
    itemStates: itemStates,
    currentItemStatus: currentItemStatus,
    setSubmitRequested: setSubmitRequested,
    submitRequested: submitRequested,
    submitted: submitted,
    setSubmitted: setSubmitted,
    result: result,
    setEvaluatedResult: setEvaluatedResult,
    questionEvaluations: questionEvaluations,
    answerRevealed: answerRevealed,
    setAnswerRevealed: setAnswerRevealed,
    reset: reset,
    setReset: setReset,
    totalCorrect: totalCorrect,
    totalIncorrect: totalIncorrect,
    totalIncomplete: totalIncomplete,
    scorePercent: scorePercent,
    numQuestions: numQuestions,
    incorrectlyAnsweredQuestions: incorrectlyAnsweredQuestions,
    setItemAnswerGiven: setItemAnswerGiven,
    totalAnswersGiven: totalAnswersGiven,
    allQuestionsAreAnswered: allQuestionsAreAnswered,
    someQuestionsAreUnanswered: !allQuestionsAreAnswered,
    totalQuestionsUnanswered: totalQuestionsUnanswered,
    evaluatedData: evaluatedData,
    locked: locked,
    setLocked: setLocked,
    setUnlocked: setUnlocked,
    systemError: systemError,
    notifySystemError: notifySystemError,
    clearNotifySystemError: clearNotifySystemError,
    hostApi: hostApi,
    ariaLiveMessage: ariaLiveMessage,
    ariaLiveMode: ariaLiveMode,
    sendAriaLiveMessage: sendAriaLiveMessage,
    clearAriaLiveMessage: clearAriaLiveMessage
  };
};

var MessagePresetType;

(function (MessagePresetType) {
  // discriminated union
  MessagePresetType[MessagePresetType["OKCancel"] = 0] = "OKCancel";
  MessagePresetType[MessagePresetType["ContinueCancel"] = 1] = "ContinueCancel";
})(MessagePresetType || (MessagePresetType = {}));

var MessageResponse;

(function (MessageResponse) {
  MessageResponse[MessageResponse["Cancel"] = 0] = "Cancel";
  MessageResponse[MessageResponse["OK"] = 1] = "OK";
  MessageResponse[MessageResponse["Continue"] = 2] = "Continue";
})(MessageResponse || (MessageResponse = {}));

var Styled$m = {
  MessageContent: styled.div.withConfig({
    displayName: "MessageBox__MessageContent"
  })(templateObject_1$n || (templateObject_1$n = __makeTemplateObject([""], [""])))
};

var MessageBox = function (props) {
  var intl = useIntl();
  var open = props.open,
      title = props.title,
      message = props.message,
      onRequestClose = props.onRequestClose,
      _a = props.size,
      size = _a === void 0 ? "small" : _a,
      _b = props.requireInteraction,
      requireInteraction = _b === void 0 ? true : _b;
  var primaryLabel = "";
  var secondaryLabel = "";
  var primaryAction;
  var secondaryAction;

  switch (props.type) {
    case MessagePresetType.OKCancel:
      // note use onResponse after discrimination
      primaryLabel = intl.formatMessage(messagesBase.ok);
      secondaryLabel = intl.formatMessage(messagesBase.cancel);

      primaryAction = function () {
        return props.onResponse(MessageResponse.OK);
      };

      secondaryAction = function () {
        return props.onResponse(MessageResponse.Cancel);
      };

      break;

    case MessagePresetType.ContinueCancel:
      primaryLabel = intl.formatMessage(messagesBase.continue);
      secondaryLabel = intl.formatMessage(messagesBase.cancel);

      primaryAction = function () {
        return props.onResponse(MessageResponse.Continue);
      };

      secondaryAction = function () {
        return props.onResponse(MessageResponse.Cancel);
      };

      break;
  }

  var callClose = function () {
    return setTimeout(function () {
      return onRequestClose();
    }, 1);
  };

  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    requireInteraction: requireInteraction,
    primaryActionButton: {
      label: primaryLabel,
      onClick: function () {
        primaryAction();
        callClose();
      }
    },
    secondaryActionButton: {
      label: secondaryLabel,
      onClick: function () {
        secondaryAction();
        callClose();
      }
    },
    onRequestClose: callClose,
    size: size,
    title: title
  }, /*#__PURE__*/React.createElement(Styled$m.MessageContent, null, message));
};
var templateObject_1$n;

var TutorialLockedReason;

(function (TutorialLockedReason) {
  TutorialLockedReason["Waiting"] = "Waiting";
})(TutorialLockedReason || (TutorialLockedReason = {}));

var TutorialSystemErrorType;

(function (TutorialSystemErrorType) {
  TutorialSystemErrorType["ImprovementFeedbackError"] = "ImprovementFeedbackError";
})(TutorialSystemErrorType || (TutorialSystemErrorType = {}));

// action types
var TUTORIAL_LOADED = "TUTORIAL_LOADED";
var TUTORIAL_STEP_SUBMITTED = "TUTORIAL_STEP_SUBMITTED";
var TUTORIAL_STEP_EVALUATING = "TUTORIAL_STEP_EVALUATING";
var TUTORIAL_STEP_COMPLETE = "TUTORIAL_STEP_COMPLETE";
var TUTORIAL_STEP_SUBMIT_PREP = "TUTORIAL_STEP_SUBMIT_PREP";
var TUTORIAL_REGISTER_ANSWER = "TUTORIAL_REGISTER_ANSWER";
var TUTORIAL_STEP_RESET = "TUTORIAL_STEP_RESET";
var TUTORIAL_EXECUTE_PENDING_STEP = "TUTORIAL_EXECUTE_PENDING_STEP";
var TUTORIAL_SYSTEM_ERROR = "TUTORIAL_SYSTEM_ERROR";
var TUTORIAL_CLEAR_SYSTEM_ERROR = "TUTORIAL_CLEAR_SYSTEM_ERROR";
var TUTORIAL_SET_IMPROVEMENT_STATUS = "TUTORIAL_SET_IMPROVEMENT_STATUS";
var TUTORIAL_CLEAR_IMPROVEMENT_STATUS = "TUTORIAL_CLEAR_IMPROVEMENT_STATUS";
var TUTORIAL_REMEMBER_FLASHCARD_CREATED = "TUTORIAL_REMEMBER_FLASHCARD_CREATED";
var TUTORIAL_LOCK = "TUTORIAL_LOCK";
var TUTORIAL_UNLOCK = "TUTORIAL_UNLOCK"; // action creators

var tutorialLoaded = function () {
  return {
    type: TUTORIAL_LOADED
  };
};
var tutorialStepSubmitted = function (stepSubmitted) {
  return {
    type: TUTORIAL_STEP_SUBMITTED,
    stepSubmitted: stepSubmitted
  };
};
var tutorialStepEvaluating = function (stepEvaluating) {
  return {
    type: TUTORIAL_STEP_EVALUATING,
    stepEvaluating: stepEvaluating
  };
};
var tutorialStepComplete = function (stepComplete) {
  return {
    type: TUTORIAL_STEP_COMPLETE,
    stepComplete: stepComplete
  };
};
var tutorialStepSubmitPrep = function (stepSubmitPrep) {
  return {
    type: TUTORIAL_STEP_SUBMIT_PREP,
    stepSubmitPrep: stepSubmitPrep
  };
};
var tutorialRegisterAnswer = function (id, record) {
  return {
    type: TUTORIAL_REGISTER_ANSWER,
    id: id,
    record: record
  };
};
var tutorialStepReset = function (stepReset) {
  return {
    type: TUTORIAL_STEP_RESET,
    stepReset: stepReset
  };
};
var tutorialExecutePendingStep = function () {
  return {
    type: TUTORIAL_EXECUTE_PENDING_STEP
  };
};
var tutorialSystemError = function (systemError) {
  return {
    type: TUTORIAL_SYSTEM_ERROR,
    systemError: systemError
  };
};
var tutorialClearSystemError = function () {
  return {
    type: TUTORIAL_CLEAR_SYSTEM_ERROR
  };
};
var tutorialSetImprovementStatus = function (improvementStatus) {
  return {
    type: TUTORIAL_SET_IMPROVEMENT_STATUS,
    improvementStatus: improvementStatus
  };
};
var tutorialClearImprovementStatus = function () {
  return {
    type: TUTORIAL_CLEAR_IMPROVEMENT_STATUS
  };
};
var tutorialRememberFlashcardCreated = function () {
  return {
    type: TUTORIAL_REMEMBER_FLASHCARD_CREATED
  };
};
var tutorialLock = function (lockedReason) {
  return {
    type: TUTORIAL_LOCK,
    lockedReason: lockedReason
  };
};
var tutorialUnLock = function () {
  return {
    type: TUTORIAL_UNLOCK
  };
};

var tutorialStateReducer = function (state, action) {
  var _a;

  var _b;

  switch (action.type) {
    case TUTORIAL_LOADED:
      return state.loaded ? state : __assign(__assign({}, state), {
        loaded: true
      });

    case TUTORIAL_STEP_SUBMITTED:
      {
        return __assign(__assign({}, state), {
          stepSubmitted: action.stepSubmitted
        });
      }

    case TUTORIAL_STEP_EVALUATING:
      {
        return __assign(__assign({}, state), {
          stepEvaluating: action.stepEvaluating
        });
      }

    case TUTORIAL_REGISTER_ANSWER:
      {
        var record = action.record;
        var curStep = state.steps[state.steps.length - 1];
        var update = {
          correct: record.correct,
          followupId: record.followupId,
          inputVars: record.inputVars
        };

        var context = __assign(__assign({}, state.context), {
          previousAnswers: __assign(__assign({}, state.context.previousAnswers), (_a = {}, _a[curStep.identifier] = update, _a))
        });

        var stepPending = findTutorialStepById(curStep, record.followupId);
        var stepUserMustRetry = !stepPending && record.correct === false;
        return __assign(__assign({}, state), {
          context: context,
          stepPending: stepPending !== null && stepPending !== void 0 ? stepPending : undefined,
          stepUserMustRetry: stepUserMustRetry,
          stepCorrect: record.correct,
          tutorialComplete: !stepPending && record.correct !== false
        });
      }

    case TUTORIAL_STEP_COMPLETE:
      {
        return __assign(__assign({}, state), {
          stepComplete: action.stepComplete
        });
      }

    case TUTORIAL_STEP_SUBMIT_PREP:
      {
        return __assign(__assign({}, state), {
          stepSubmitPrep: action.stepSubmitPrep
        });
      }

    case TUTORIAL_STEP_RESET:
      {
        if (!action.stepReset) {
          return __assign(__assign({}, state), {
            stepReset: false
          });
        }

        return __assign(__assign({}, state), {
          stepReset: true,
          stepSubmitted: false,
          stepEvaluating: false,
          stepComplete: false,
          stepCorrect: undefined,
          stepSubmitPrep: false,
          tutorialComplete: false
        });
      }

    case TUTORIAL_EXECUTE_PENDING_STEP:
      {
        if (!state.stepPending) {
          throw "Tutorial executed a pending step that wasn't defined";
        }

        return __assign(__assign({}, state), {
          steps: __spreadArray(__spreadArray([], state.steps, true), [state.stepPending], false),
          stepPending: undefined,
          stepSubmitted: false,
          stepEvaluating: false,
          stepComplete: false,
          stepSubmitPrep: false,
          stepCorrect: undefined,
          tutorialComplete: !state.stepPending.followup && !((_b = state.stepPending) === null || _b === void 0 ? void 0 : _b.question)
        });
      }

    case TUTORIAL_SYSTEM_ERROR:
      return __assign(__assign({}, state), {
        systemError: action.systemError
      });

    case TUTORIAL_CLEAR_SYSTEM_ERROR:
      return __assign(__assign({}, state), {
        systemError: undefined
      });

    case TUTORIAL_SET_IMPROVEMENT_STATUS:
      return __assign(__assign({}, state), {
        improvementStatus: action.improvementStatus
      });

    case TUTORIAL_CLEAR_IMPROVEMENT_STATUS:
      return __assign(__assign({}, state), {
        improvementStatus: undefined
      });

    case TUTORIAL_REMEMBER_FLASHCARD_CREATED:
      return __assign(__assign({}, state), {
        params: __assign(__assign({}, state.params), {
          isFlashcardCreated: true
        })
      });

    case TUTORIAL_LOCK:
      return __assign(__assign({}, state), {
        locked: true,
        lockedReason: TutorialLockedReason.Waiting
      });

    case TUTORIAL_UNLOCK:
      return __assign(__assign({}, state), {
        locked: false
      });

    default:
      return state;
  }
};

var getDefaultTutorialState = function (data, evaluatedData) {
  var state = {
    loaded: false,
    context: {
      previousAnswers: {}
    },
    steps: [data.step],
    stepPending: undefined,
    tutorialComplete: false,
    stepSubmitted: false,
    stepEvaluating: false,
    stepComplete: false,
    stepCorrect: false,
    stepReset: false,
    stepUserMustRetry: false,
    stepSubmitPrep: false,
    locked: false
  };
  state = restoreState(data, state, evaluatedData);
  return state;
};
var tutorialStateRootReducer = function (state, action) {
  return tutorialStateReducer(state, action);
};
var findTutorialStepById = function (step, stepId) {
  if (!stepId) {
    return null;
  }

  if (step.identifier === stepId) {
    return step;
  } else if (step.followup) {
    for (var _i = 0, _a = step.followup; _i < _a.length; _i++) {
      var followup = _a[_i];
      var step_1 = findTutorialStepById(followup.step, stepId);

      if (step_1) {
        return step_1;
      }
    }
  }

  return null;
};

var findRestoredSteps = function (step, previousAnswers, data, evaluatedData, restoredSteps) {
  var _a, _b;

  if (step.question && evaluatedData.questions[step.question.id]) {
    var evaluatedQuestion = evaluatedData.questions[step.question.id];

    if (evaluatedQuestion.correct === undefined || evaluatedQuestion.answers == undefined) {
      throw "Tutorial failed to load step answers from state";
    }

    previousAnswers[step.identifier] = {
      correct: evaluatedQuestion.correct,
      inputVars: evaluatedQuestion.answers
    };
    restoredSteps.push(step);

    if ((_a = evaluatedQuestion.followup) === null || _a === void 0 ? void 0 : _a.step) {
      var nextStep = findTutorialStepById(step, (_b = evaluatedQuestion.followup) === null || _b === void 0 ? void 0 : _b.step);

      if (nextStep) {
        findRestoredSteps(nextStep, previousAnswers, data, evaluatedData, restoredSteps);
      }
    }
  } else {
    restoredSteps.push(step);
  }
};

var restoreState = function (data, state, evaluatedData) {
  var _a, _b, _c, _d, _e, _f;

  if (!evaluatedData) {
    return state;
  }

  var restoredSteps = [];
  var previousAnswers = {};
  findRestoredSteps(data.step, previousAnswers, data, evaluatedData, restoredSteps);
  state.steps = restoredSteps;
  state.context.previousAnswers = previousAnswers;
  var lastStep = state.steps[state.steps.length - 1];
  var hasMoreSteps = !!((_a = lastStep.followup) === null || _a === void 0 ? void 0 : _a.length);
  var lastQuestionState = lastStep.question && evaluatedData.questions[lastStep.question.id];
  var stepUserMustRetry = lastQuestionState && lastQuestionState.correct === false && !((_b = lastQuestionState.followup) === null || _b === void 0 ? void 0 : _b.step);

  if (stepUserMustRetry) {
    state.stepUserMustRetry = stepUserMustRetry;
    state.stepComplete = true;
  } else if (lastStep.question && !lastQuestionState) {
    state.stepComplete = false;
  } else if (lastQuestionState && lastQuestionState.correct !== false && !((_c = lastQuestionState.followup) === null || _c === void 0 ? void 0 : _c.step)) {
    state.stepComplete = true;
    state.tutorialComplete = true;
  } else if (!hasMoreSteps || !lastStep.question) {
    state.stepComplete = true;
    state.tutorialComplete = true;
  }

  if (lastQuestionState) {
    state.stepCorrect = lastQuestionState.correct;
  }

  var rootQuestionId = (_d = data.step.question) === null || _d === void 0 ? void 0 : _d.id;

  if (rootQuestionId) {
    state.improvementStatus = (_e = evaluatedData.questions[rootQuestionId]) === null || _e === void 0 ? void 0 : _e.improvementStatus;
  }

  if (rootQuestionId) {
    state.params = (_f = evaluatedData.questions[rootQuestionId]) === null || _f === void 0 ? void 0 : _f.parameters;
  }

  return state;
};

var NoteIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    "aria-hidden": "true",
    focusable: "false",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M15 7a1 1 0 0 1 1 1v6.002A2 2 0 0 1 14.002 16H1.998A2 2 0 0 1 0 14.002V1.998A2 2 0 0 1 1.998 0H8a1 1 0 1 1 0 2H1.998L2 14.002 14.002 14 14 8a1 1 0 0 1 1-1zM4.747 11.253l1.768-3.89 2.121 2.122-3.889 1.768zM15.707 1a.999.999 0 0 1 0 1.414l-.172.172-6.192 6.192-2.121-2.121L13.414.465l.172-.172a.999.999 0 0 1 1.414 0l.707.707z"
  }));
};

var WidgetBox = styled.div.withConfig({
  displayName: "Widget__WidgetBox"
})(templateObject_1$m || (templateObject_1$m = __makeTemplateObject(["\n  ", ";\n  padding: 16px;\n  margin: 0 16px;\n"], ["\n  ", ";\n  padding: 16px;\n  margin: 0 16px;\n"])), surveyBoxStyle);
var WidgetPromptContainer = styled.div.withConfig({
  displayName: "Widget__WidgetPromptContainer"
})(templateObject_2$h || (templateObject_2$h = __makeTemplateObject(["\n  font-size: 1.6rem;\n  color: #1c1c1c;\n  font-weight: 500;\n  line-height: 2.25;\n  svg {\n    margin-right: 1rem;\n    vertical-align: middle;\n  }\n"], ["\n  font-size: 1.6rem;\n  color: #1c1c1c;\n  font-weight: 500;\n  line-height: 2.25;\n  svg {\n    margin-right: 1rem;\n    vertical-align: middle;\n  }\n"])));
var WidgetPrompt = styled.div.withConfig({
  displayName: "Widget__WidgetPrompt"
})(templateObject_3$e || (templateObject_3$e = __makeTemplateObject(["\n  margin-bottom: 8px;\n"], ["\n  margin-bottom: 8px;\n"])));
var WidgetFeedback = styled.div.withConfig({
  displayName: "Widget__WidgetFeedback"
})(templateObject_4$c || (templateObject_4$c = __makeTemplateObject(["\n  margin-top: 1rem;\n  margin-left: 2rem;\n  font-size: 1.6rem;\n"], ["\n  margin-top: 1rem;\n  margin-left: 2rem;\n  font-size: 1.6rem;\n"])));

var Widget = function (_a) {
  var prompt = _a.prompt,
      controls = _a.controls,
      feedback = _a.feedback;
  return /*#__PURE__*/React.createElement(WidgetBox, null, /*#__PURE__*/React.createElement(WidgetPromptContainer, null, /*#__PURE__*/React.createElement(WidgetPrompt, null, prompt), /*#__PURE__*/React.createElement("div", null, controls)), feedback && /*#__PURE__*/React.createElement(WidgetFeedback, null, feedback));
};
var templateObject_1$m, templateObject_2$h, templateObject_3$e, templateObject_4$c;

var Styled$l = {
  WidgetButton: styled.button.withConfig({
    displayName: "CreateNoteWidget__WidgetButton"
  })(templateObject_1$l || (templateObject_1$l = __makeTemplateObject(["\n    background-color: #ffffff;\n    border-radius: 16px;\n    padding: 6px;\n    border: none;\n    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);\n    color: #1c1c1c;\n    cursor: pointer;\n  "], ["\n    background-color: #ffffff;\n    border-radius: 16px;\n    padding: 6px;\n    border: none;\n    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);\n    color: #1c1c1c;\n    cursor: pointer;\n  "])))
};

var CreateNoteWidget = function (_a) {
  var prompt = _a.prompt,
      note = _a.note;
  var intl = useIntl();

  var _b = useState(false),
      created = _b[0],
      setCreated = _b[1];

  return /*#__PURE__*/React.createElement(Widget, {
    prompt: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(NoteIcon, null), prompt),
    controls: !created && /*#__PURE__*/React.createElement(Styled$l.WidgetButton, {
      onClick: function () {
        setCreated(true); // todo remove when this widget becomes real
        // eslint-disable-next-line no-console

        console.log("Call notes api to create note\n" + note);
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesWidgets.createANote))),
    feedback: created ? intl.formatMessage(messagesWidgets.noteCreated) : null
  });
};
var templateObject_1$l;

var FlashCardIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "19",
    height: "17"
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    d: "M14.957 11.293L14 11.044V7.147a2 2 0 00-2-2H6.224L7.042 2l9.678 2.52-1.763 6.773zM12 14.147H2v-7h10v7zm5.224-11.563L7.546.064a2.003 2.003 0 00-2.44 1.433l-.95 3.65H2c-1.106 0-2 .895-2 2v7c0 1.105.894 2 2 2h10a2 2 0 002-2v-1.036l.453.118a2.001 2.001 0 002.44-1.432l1.762-6.775a1.997 1.997 0 00-1.431-2.438z",
    id: "a"
  })), /*#__PURE__*/React.createElement("use", {
    fill: "#1C1C1C",
    xlinkHref: "#a",
    fillRule: "evenodd"
  }));
};

var Styled$k = {
  FlashcardMenu: styled.div.withConfig({
    displayName: "CreateFlashcardWidget__FlashcardMenu"
  })(templateObject_1$k || (templateObject_1$k = __makeTemplateObject(["\n    width: 200px;\n  "], ["\n    width: 200px;\n  "]))),
  Header: styled.div.withConfig({
    displayName: "CreateFlashcardWidget__Header"
  })(templateObject_2$g || (templateObject_2$g = __makeTemplateObject(["\n    color: ", ";\n    padding-top: 14px;\n    margin: 0 16px;\n    font-size: 1.4rem;\n    font-weight: 300;\n    &:only-child {\n      padding-bottom: 14px;\n    }\n  "], ["\n    color: ", ";\n    padding-top: 14px;\n    margin: 0 16px;\n    font-size: 1.4rem;\n    font-weight: 300;\n    &:only-child {\n      padding-bottom: 14px;\n    }\n  "])), cloud.getShade(6)),
  FlashcardButton: styled(Button).withConfig({
    displayName: "CreateFlashcardWidget__FlashcardButton"
  })(templateObject_3$d || (templateObject_3$d = __makeTemplateObject(["\n    button {\n      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.5);\n      color: ", " !important;\n    }\n  "], ["\n    button {\n      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.5);\n      color: ", " !important;\n    }\n  "])), cloud.getShade(10))
};

var CreateFlashCardWidget = function (_a) {
  var prompt = _a.prompt,
      front = _a.front,
      back = _a.back,
      hostApi = _a.hostApi,
      relatedId = _a.relatedId,
      onNotifyCreate = _a.onNotifyCreate,
      isCreated = _a.isCreated;
  var intl = useIntl();

  var _b = useState(),
      deckList = _b[0],
      setDeckList = _b[1];

  var _c = useState(false),
      autoAdd = _c[0],
      setAutoAdd = _c[1];

  useEffect(function () {
    if (hostApi === null || hostApi === void 0 ? void 0 : hostApi.getFlashcardDecks) {
      hostApi.getFlashcardDecks().then(function (res) {
        setAutoAdd(res.autoAdd);
        setDeckList(res.data);
      });
    }
  }, [hostApi]);

  var handleCreated = function () {
    setAutoAdd(false);
    onNotifyCreate();
  };

  var handleAutoAdd = function () {
    var _a;

    if (deckList) {
      (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.createFlashcard) === null || _a === void 0 ? void 0 : _a.call(hostApi, {
        deckId: deckList[0].uuid,
        front: front,
        back: back,
        relatedId: relatedId
      }).then(handleCreated);
    }
  };

  var CreateFlashcardButton = function (props) {
    return /*#__PURE__*/React.createElement(Styled$k.FlashcardButton, __assign({
      color: "white",
      size: "small",
      pill: true,
      label: intl.formatMessage(__assign({}, messagesWidgets.createAFlashcard))
    }, props), /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesWidgets.createAFlashcard)));
  };

  return /*#__PURE__*/React.createElement(Widget, {
    prompt: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FlashCardIcon, null), !isCreated ? prompt : intl.formatMessage(__assign({}, messagesWidgets.flashcardCreated))),
    controls: !isCreated ? !autoAdd ? /*#__PURE__*/React.createElement(Popover, {
      placement: "right-end",
      modifiers: {
        offset: {
          offset: "0, 12px"
        }
      },
      content: /*#__PURE__*/React.createElement(Styled$k.FlashcardMenu, null, /*#__PURE__*/React.createElement(Styled$k.Header, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesWidgets.addToFlashcard))), deckList && deckList.length > 0 && !autoAdd && !isCreated && /*#__PURE__*/React.createElement(List, {
        data: [{
          listItems: deckList === null || deckList === void 0 ? void 0 : deckList.map(function (option) {
            return {
              text: option.title,
              onClick: function () {
                var _a;

                (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.createFlashcard) === null || _a === void 0 ? void 0 : _a.call(hostApi, {
                  deckId: option.uuid,
                  front: front,
                  back: back,
                  relatedId: relatedId
                }).then(handleCreated);
              }
            };
          })
        }]
      }))
    }, /*#__PURE__*/React.createElement(CreateFlashcardButton, null)) : /*#__PURE__*/React.createElement(CreateFlashcardButton, {
      onClick: handleAutoAdd
    }) : /*#__PURE__*/React.createElement(Styled$k.FlashcardButton, {
      color: "white",
      size: "small",
      pill: true,
      label: intl.formatMessage(__assign({}, messagesWidgets.reviewFlashcard)),
      onClick: function () {
        var _a;

        return (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.reviewFlashcards) === null || _a === void 0 ? void 0 : _a.call(hostApi, relatedId);
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesWidgets.reviewFlashcard)))
  });
};
var templateObject_1$k, templateObject_2$g, templateObject_3$d;

function isScrollable(element) {
  if (!element) {
    return null;
  }

  var hasScrollableContent = element.scrollHeight > element.clientHeight;
  var overflowYStyle = window.getComputedStyle(element).overflowY;
  var isOverflowHidden = overflowYStyle.indexOf("hidden") !== -1;
  return hasScrollableContent && !isOverflowHidden;
}
function getScrollableParent(element) {
  return !element || element === document.body ? document.body : isScrollable(element) ? element : getScrollableParent(element.parentElement);
}

var slideDown = keyframes(templateObject_1$j || (templateObject_1$j = __makeTemplateObject(["\n  from {\n    transform: translate(0, -100%);\n  }\n  to {\n    transform: translate(0, 0%);\n  }\n"], ["\n  from {\n    transform: translate(0, -100%);\n  }\n  to {\n    transform: translate(0, 0%);\n  }\n"])));
var Styled$j = {
  Container: styled.div.withConfig({
    displayName: "StepHolder__Container"
  })(templateObject_2$f || (templateObject_2$f = __makeTemplateObject(["\n    overflow: hidden;\n  "], ["\n    overflow: hidden;\n  "]))),
  Step: styled.div.withConfig({
    displayName: "StepHolder__Step"
  })(templateObject_4$b || (templateObject_4$b = __makeTemplateObject(["\n    ", ";\n    margin-bottom: 19px;\n  "], ["\n    ", ";\n    margin-bottom: 19px;\n  "])), function (_a) {
    var animate = _a.animate;
    return animate && css(templateObject_3$c || (templateObject_3$c = __makeTemplateObject(["\n        animation: ", " 0.8s;\n      "], ["\n        animation: ", " 0.8s;\n      "])), slideDown);
  })
};

var StepHolder = function (_a) {
  var children = _a.children,
      stepIndex = _a.stepIndex,
      animate = _a.animate,
      onAnimationEnd = _a.onAnimationEnd;
  return /*#__PURE__*/React.createElement(Styled$j.Container, null, /*#__PURE__*/React.createElement(Styled$j.Step, {
    ref: animate ? function (element) {
      if (element) {
        var scroller_1 = getScrollableParent(element);

        if (scroller_1) {
          defer(function () {
            scroller_1.scrollTo({
              left: 0,
              top: element.offsetTop - 16,
              behavior: "smooth"
            });
          });
        }
      }
    } : undefined,
    stepIndex: stepIndex,
    animate: animate,
    onAnimationEnd: function () {
      return onAnimationEnd();
    }
  }, children));
};
var templateObject_1$j, templateObject_2$f, templateObject_3$c, templateObject_4$b;

var TutorialControlsType;

(function (TutorialControlsType) {
  TutorialControlsType[TutorialControlsType["Submit"] = 0] = "Submit";
  TutorialControlsType[TutorialControlsType["NextQuestion"] = 1] = "NextQuestion";
  TutorialControlsType[TutorialControlsType["Done"] = 2] = "Done";
})(TutorialControlsType || (TutorialControlsType = {}));

var Styled$i = {
  Container: styled.div.withConfig({
    displayName: "TutorialControls__Container"
  })(templateObject_2$e || (templateObject_2$e = __makeTemplateObject(["\n    font-size: 1.6rem;\n    padding: 16px;\n    position: relative;\n    background-color: white;\n    &:before {\n      content: \"\";\n      display: block;\n      box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.2);\n      height: 1px;\n      top: 0;\n      left: 0;\n      width: 100%;\n      position: absolute;\n    }\n    ", ";\n  ` ,\n};    \n  "], ["\n    font-size: 1.6rem;\n    padding: 16px;\n    position: relative;\n    background-color: white;\n    &:before {\n      content: \"\";\n      display: block;\n      box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.2);\n      height: 1px;\n      top: 0;\n      left: 0;\n      width: 100%;\n      position: absolute;\n    }\n    ", ";\n  \\` ,\n};    \n  "])), function (_a) {
    var dockToBottom = _a.dockToBottom;
    return dockToBottom && css(templateObject_1$i || (templateObject_1$i = __makeTemplateObject(["\n        position: absolute;\n        bottom: 0;\n        width: 100%;\n        box-sizing: border-box;\n      "], ["\n        position: absolute;\n        bottom: 0;\n        width: 100%;\n        box-sizing: border-box;\n      "])));
  })
};
var TutorialControls = /*#__PURE__*/React.forwardRef(function (props, ref) {
  if (props.type === TutorialControlsType.Submit && !props.submitted) {
    var onSubmit_1 = props.onSubmit;
    return /*#__PURE__*/React.createElement(Styled$i.Container, {
      ref: ref || undefined,
      dockToBottom: props.dockToBottom
    }, /*#__PURE__*/React.createElement(Button, {
      fullWidth: true,
      onClick: function () {
        onSubmit_1();
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsCheckAnswer))));
  } else if (props.type === TutorialControlsType.NextQuestion) {
    var onNextQuestion_1 = props.onNextQuestion;
    return /*#__PURE__*/React.createElement(Styled$i.Container, {
      ref: ref || undefined,
      dockToBottom: props.dockToBottom
    }, /*#__PURE__*/React.createElement(Button, {
      fullWidth: true,
      onClick: function () {
        onNextQuestion_1();
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsNextQuestion))));
  } else if (props.type === TutorialControlsType.Done) {
    var onDone_1 = props.onDone;
    return /*#__PURE__*/React.createElement(Styled$i.Container, {
      ref: ref || undefined,
      dockToBottom: props.dockToBottom
    }, /*#__PURE__*/React.createElement(Button, {
      fullWidth: true,
      onClick: function () {
        onDone_1();
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsDone))));
  } else {
    return null;
  }
});
var templateObject_1$i, templateObject_2$e;

/**
 *  tutorial boilerplate
 */

function useTutorial(data, evaluator, state, dispatch) {
  var _a, _b, _c;

  var isLoaded = useRef(false);
  var lastImprovementStatus = usePrevious(state.improvementStatus);
  var lastParams = usePrevious(state.params);
  var improvementStatus = state.improvementStatus,
      params = state.params,
      systemError = state.systemError; // use root question as only questions can store params and improvement flags

  var questionId = (_b = (_a = data.step) === null || _a === void 0 ? void 0 : _a.question) === null || _b === void 0 ? void 0 : _b.id;
  var tutorialId = data.id;
  var step = (_c = data.step) === null || _c === void 0 ? void 0 : _c.identifier;

  if (!questionId || !tutorialId || !step) {
    throw new Error("Tutorial found without expected id structure"); // should never happen
  }

  useEffect(function () {
    if (!state || !isLoaded || improvementStatus === lastImprovementStatus) {
      return;
    }

    if ((improvementStatus === null || improvementStatus === void 0 ? void 0 : improvementStatus.flag) !== undefined) {
      // send status to api
      evaluator.submitQuestionImprovementStatus(questionId, improvementStatus, tutorialId, step).catch(function (e) {
        dispatch(tutorialSystemError({
          type: TutorialSystemErrorType.ImprovementFeedbackError,
          detail: e.message
        })); // if status saves fails we need to clear status so user can retry

        dispatch(tutorialClearImprovementStatus());
      });
    }
  }, [dispatch, evaluator, improvementStatus, lastImprovementStatus, isLoaded, questionId, state, step, tutorialId]);
  useEffect(function () {
    if (!state || !isLoaded || params === lastParams) {
      return;
    }

    if (params) {
      // send params to api
      evaluator.saveParameters(questionId, params).catch(function () {
        /* todo handle save params error */
      });
    }
  }, [evaluator, lastParams, isLoaded, params, questionId, state]);
  useEffect(function () {
    defer(function () {
      if (systemError) {
        // clear after toast render
        dispatch(tutorialClearSystemError());
      }
    });
  });
  useEffect(function () {
    if (state.loaded && !isLoaded.current) {
      isLoaded.current = true;
    }
  }, [state.loaded]);
}

var TutorialNotifications = function (_a) {
  var systemError = _a.systemError;
  var intl = useIntl();
  var text = useMemo(function () {
    if ((systemError === null || systemError === void 0 ? void 0 : systemError.type) == TutorialSystemErrorType.ImprovementFeedbackError) {
      return {
        title: intl.formatMessage(messagesErrors.improvementFeedbackErrorTitle),
        description: intl.formatMessage(messagesErrors.improvementFeedbackErrorDescription),
        detail: systemError.detail
      };
    }

    return;
  }, [intl, systemError === null || systemError === void 0 ? void 0 : systemError.detail, systemError === null || systemError === void 0 ? void 0 : systemError.type]);
  return /*#__PURE__*/React.createElement(Notifications, {
    open: !!systemError,
    text: text
  });
};

var Styled$h = {
  Container: styled.div.withConfig({
    displayName: "Tutorial__Container"
  })(templateObject_1$h || (templateObject_1$h = __makeTemplateObject(["\n    display: ", ";\n    flex-direction: column;\n    height: 100%;\n  "], ["\n    display: ", ";\n    flex-direction: column;\n    height: 100%;\n  "])), function (_a) {
    var visible = _a.visible;
    return visible ? "flex" : "none";
  }),
  ScrollPane: styled.div.withConfig({
    displayName: "Tutorial__ScrollPane"
  })(templateObject_2$d || (templateObject_2$d = __makeTemplateObject(["\n    flex: 1;\n    overflow-y: auto;\n    overflow-x: hidden;\n  "], ["\n    flex: 1;\n    overflow-y: auto;\n    overflow-x: hidden;\n  "]))),
  ScrollContent: styled.div.withConfig({
    displayName: "Tutorial__ScrollContent"
  })(templateObject_3$b || (templateObject_3$b = __makeTemplateObject(["\n    margin: 0;\n    padding: 0;\n    padding-top: 16px;\n    box-sizing: border-box;\n  "], ["\n    margin: 0;\n    padding: 0;\n    padding-top: 16px;\n    box-sizing: border-box;\n  "]))),
  QuestionsContainer: styled.div.withConfig({
    displayName: "Tutorial__QuestionsContainer"
  })(templateObject_4$a || (templateObject_4$a = __makeTemplateObject(["\n    width: 100%;\n    box-sizing: border-box;\n    margin: 0;\n    padding: 0 0 0 0;\n  "], ["\n    width: 100%;\n    box-sizing: border-box;\n    margin: 0;\n    padding: 0 0 0 0;\n  "]))),
  InlineControlHolder: styled.div.withConfig({
    displayName: "Tutorial__InlineControlHolder"
  })(templateObject_5$9 || (templateObject_5$9 = __makeTemplateObject(["\n    margin: 0 24px;\n    padding-bottom: 16px;\n  "], ["\n    margin: 0 24px;\n    padding-bottom: 16px;\n  "])))
};

var Tutorial = function (_a) {
  var data = _a.data,
      visible = _a.visible,
      evaluator = _a.evaluator,
      evaluatedData = _a.evaluatedData,
      events = _a.events,
      questionIndex = _a.questionIndex,
      siblingQuestionCount = _a.siblingQuestionCount,
      hostApi = _a.hostApi;

  var getDefaultState = function () {
    return getDefaultTutorialState(data, evaluatedData);
  };

  var _b = useReducer(tutorialStateRootReducer, getDefaultState()),
      state = _b[0],
      dispatch = _b[1];

  var steps = state.steps,
      context = state.context,
      stepPending = state.stepPending,
      stepSubmitted = state.stepSubmitted,
      stepReset = state.stepReset,
      stepComplete = state.stepComplete,
      stepSubmitPrep = state.stepSubmitPrep,
      tutorialComplete = state.tutorialComplete,
      stepUserMustRetry = state.stepUserMustRetry,
      systemError = state.systemError,
      improvementStatus = state.improvementStatus,
      locked = state.locked;
  var lastRenderedStep = usePrevious(steps[steps.length - 1]);

  var _c = useState(improvementStatus !== undefined),
      animateQuestionImprovement = _c[0],
      setAnimateQuestionImprovement = _c[1];

  var _d = useState(false),
      doingStepAnimation = _d[0],
      setDoingStepAnimation = _d[1];

  var ctlsRef = React.useRef(null);
  var lastCtlHeight = useRef(undefined);
  useEffect(function () {
    var _a, _b, _c, _d, _e; // controls are out of flow so need to sync parent


    if (((_a = ctlsRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) !== lastCtlHeight.current) {
      lastCtlHeight.current = (_b = ctlsRef.current) === null || _b === void 0 ? void 0 : _b.offsetHeight;
      (_c = events === null || events === void 0 ? void 0 : events.onSetControlsSize) === null || _c === void 0 ? void 0 : _c.call(events, data.id, (_e = (_d = ctlsRef.current) === null || _d === void 0 ? void 0 : _d.offsetHeight) !== null && _e !== void 0 ? _e : 0);
    }
  });
  useEffect(function () {
    if (!state.loaded) {
      defer(function () {
        return dispatch(tutorialLoaded());
      });
    }
  }, [dispatch, evaluatedData, state.loaded]);
  useTutorial(data, evaluator, state, dispatch);
  useEffect(function () {
    // signals "controlled" reset in the current question step
    if (stepReset) {
      dispatch(tutorialStepReset(false));
    }
  }, [stepReset]);
  var handleStepAnimationEnd = useCallback(function () {
    if (doingStepAnimation) {
      setDoingStepAnimation(false);
    }

    if (tutorialComplete && !animateQuestionImprovement) {
      setAnimateQuestionImprovement(true);
    }
  }, [tutorialComplete, animateQuestionImprovement, doingStepAnimation]);

  if (!steps) {
    // only really applicable in dev but localize if it becomes a thing in prod
    return /*#__PURE__*/React.createElement("div", {
      style: {
        margin: "24px"
      }
    }, /*#__PURE__*/React.createElement("p", null, "Tutorial Display Error:"), /*#__PURE__*/React.createElement("p", null, "This tutorial has no question content"));
  }

  var questionEvents = {
    onSubmit: function (id, correct, inputVars, followupId) {
      // record the users answer to the current step
      // this also works out the next step (the pending step) (if any) based on that answer
      // the pending step only becomes visible after
      // "continue" is clicked and stepSubmitted turns to false
      dispatch(tutorialStepEvaluating(false));
      dispatch(tutorialUnLock());
      dispatch(tutorialRegisterAnswer(id, {
        correct: correct,
        inputVars: inputVars,
        followupId: followupId
      }));
      dispatch(tutorialStepComplete(true)); // this is for outer container (if any) e.g. formative group

      defer(function () {
        var _a;

        return (_a = events === null || events === void 0 ? void 0 : events.onSubmit) === null || _a === void 0 ? void 0 : _a.call(events, data.id, correct, inputVars, followupId);
      });
    },
    onValidate: function (id, passed) {
      if (!passed) {
        dispatch(tutorialStepSubmitted(false));
      }
    },
    onEvalStart: function () {
      dispatch(tutorialLock(TutorialLockedReason.Waiting));
      dispatch(tutorialStepEvaluating(true));
    },
    onEvalDeferred: function () {
      // question called evaluate but some further user interaction is required
      // e.g answer suggestions have been returned from eval
      dispatch(tutorialStepSubmitted(false));
      dispatch(tutorialUnLock());
      dispatch(tutorialStepEvaluating(false));
    },
    onSubmitPrep: function () {
      dispatch(tutorialStepSubmitPrep(true));
    }
  };

  var renderQuestion = function (step, mode, stepIndex) {
    var question = step.question;

    if (!question) {
      throw new Error("Expected tutorial step question but it was undefined");
    }

    var Question = questionLookup[question.question_type];

    if (!Question) {
      throw new Error("Tutorial cannot find implementation for question " + question.question_type);
    }

    var isLastStep = stepIndex === steps.length - 1;
    var isStepAppearing = isLastStep && doingStepAnimation;
    var evaluatedQuestion = evaluatedData === null || evaluatedData === void 0 ? void 0 : evaluatedData.questions[question.id];

    if (mode === QuestionMode.Tutorial) {
      return /*#__PURE__*/React.createElement(Question, {
        controlledSubmit: steps.length === 1 && stepSubmitted,
        controlledReset: steps.length === 1 && stepReset,
        mode: mode,
        visible: true,
        id: question.id,
        data: question,
        events: questionEvents,
        context: context,
        evaluator: evaluator,
        evaluatedData: evaluatedQuestion,
        tutorialId: data.id,
        tutorialStep: step.identifier
      });
    } else {
      return /*#__PURE__*/React.createElement(StepHolder, {
        key: question.id,
        stepIndex: stepIndex,
        animate: isStepAppearing,
        onAnimationEnd: handleStepAnimationEnd
      }, /*#__PURE__*/React.createElement(Question, {
        controlledSubmit: stepIndex === steps.length - 1 && stepSubmitted,
        controlledReset: steps.length - 1 === 1 && stepReset,
        mode: mode,
        visible: true,
        id: question.id,
        data: question,
        events: questionEvents,
        context: context,
        evaluator: evaluator,
        evaluatedData: evaluatedQuestion,
        tutorialId: data.id,
        tutorialStep: step.identifier
      }));
    }
  };

  var renderStep = function (step, stepIndex) {
    var _a, _b;

    var isStepAppearing = stepIndex === steps.length - 1 && step !== lastRenderedStep;

    if (!doingStepAnimation && isStepAppearing) {
      setDoingStepAnimation(isStepAppearing);
    }

    if (step.question) {
      return renderQuestion(step, QuestionMode.TutorialFollowup, stepIndex);
    } else if (step.notes) {
      var note = processText(step.notes.note_string, context, ProcessTextOption.Plain);
      return /*#__PURE__*/React.createElement(StepHolder, {
        key: step.identifier,
        stepIndex: stepIndex,
        animate: isStepAppearing || doingStepAnimation,
        onAnimationEnd: handleStepAnimationEnd
      }, /*#__PURE__*/React.createElement(CreateNoteWidget, {
        prompt: (_a = step.notes) === null || _a === void 0 ? void 0 : _a.prompt,
        note: note
      }));
    } else if (step.flashcard) {
      var flashcard = step.flashcard;
      return /*#__PURE__*/React.createElement(StepHolder, {
        key: step.identifier,
        stepIndex: stepIndex,
        animate: isStepAppearing,
        onAnimationEnd: handleStepAnimationEnd
      }, /*#__PURE__*/React.createElement(CreateFlashCardWidget, {
        prompt: flashcard.prompt,
        front: flashcard.flashcard_front,
        back: flashcard.flashcard_back,
        hostApi: hostApi,
        relatedId: data.id,
        isCreated: !!((_b = state.params) === null || _b === void 0 ? void 0 : _b.isFlashcardCreated),
        onNotifyCreate: function () {
          return dispatch(tutorialRememberFlashcardCreated());
        }
      }));
    }

    throw new Error("Tutorial unknown or missing step type");
  };

  var inGroup = questionIndex !== undefined && siblingQuestionCount !== undefined;
  var hasNextQuestion = inGroup && questionIndex < siblingQuestionCount - 1;
  var lastInGroup = inGroup && !hasNextQuestion;
  return /*#__PURE__*/React.createElement(Styled$h.Container, {
    visible: visible
  }, /*#__PURE__*/React.createElement(Styled$h.ScrollPane, null, /*#__PURE__*/React.createElement(Styled$h.ScrollContent, null, /*#__PURE__*/React.createElement(Styled$h.QuestionsContainer, null, renderQuestion(steps[0], QuestionMode.Tutorial, 0), steps.slice(1).map(function (step, i) {
    return renderStep(step, i + 1);
  })), stepComplete && stepPending && !stepUserMustRetry && /*#__PURE__*/React.createElement(Styled$h.InlineControlHolder, null, /*#__PURE__*/React.createElement(Button, {
    outline: true,
    onClick: function () {
      // this will move onto the next step
      dispatch(tutorialExecutePendingStep());
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesBase.continue)))), stepComplete && stepUserMustRetry && /*#__PURE__*/React.createElement(Styled$h.InlineControlHolder, null, /*#__PURE__*/React.createElement(Button, {
    outline: true,
    onClick: function () {
      // tutorial has no path out for when the user gets it wrong
      // this will give the user another chance at getting it correct
      dispatch(tutorialStepReset(true));
      defer(function () {
        var _a;

        return (_a = events === null || events === void 0 ? void 0 : events.onReset) === null || _a === void 0 ? void 0 : _a.call(events, data.id);
      });
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesQuestion.controlsRetry)))), tutorialComplete && !improvementStatus && !doingStepAnimation && /*#__PURE__*/React.createElement(QuestionImprovement, {
    status: improvementStatus,
    appearEffect: animateQuestionImprovement && tutorialComplete && !doingStepAnimation,
    onStatus: function (improvementStatus) {
      dispatch(tutorialSetImprovementStatus(improvementStatus));
    },
    onAppearStart: function (element) {
      if (element) {
        var scroller_1 = getScrollableParent(element);

        if (scroller_1) {
          defer(function () {
            scroller_1.scrollTo({
              left: 0,
              top: element.offsetTop - 16,
              behavior: "smooth"
            });
          });
        }
      }
    }
  }))), !stepComplete && !tutorialComplete && !stepSubmitPrep && /*#__PURE__*/React.createElement(TutorialControls, {
    ref: ctlsRef,
    type: TutorialControlsType.Submit,
    submitted: false,
    dockToBottom: inGroup,
    onSubmit: function () {
      dispatch(tutorialStepSubmitted(true));
    }
  }), tutorialComplete && inGroup && hasNextQuestion && /*#__PURE__*/React.createElement(TutorialControls, {
    ref: ctlsRef,
    type: TutorialControlsType.NextQuestion,
    dockToBottom: inGroup,
    onNextQuestion: function () {
      var _a;

      (_a = events === null || events === void 0 ? void 0 : events.onNextQuestion) === null || _a === void 0 ? void 0 : _a.call(events, data.id);
    }
  }), tutorialComplete && inGroup && lastInGroup && /*#__PURE__*/React.createElement(TutorialControls, {
    ref: ctlsRef,
    type: TutorialControlsType.Done,
    dockToBottom: inGroup,
    onDone: function () {
      var _a;

      (_a = events === null || events === void 0 ? void 0 : events.onDone) === null || _a === void 0 ? void 0 : _a.call(events, data.id);
    }
  }), /*#__PURE__*/React.createElement(TutorialNotifications, {
    systemError: systemError
  }), /*#__PURE__*/React.createElement(ModalBlock, {
    blocked: locked
  }));
};
var templateObject_1$h, templateObject_2$d, templateObject_3$b, templateObject_4$a, templateObject_5$9;

var QuestionGroupNotifications = function (_a) {
  var systemError = _a.systemError;
  var intl = useIntl();
  var text = useMemo(function () {
    if ((systemError === null || systemError === void 0 ? void 0 : systemError.type) == CoachmeAssessmentErrorType.SubmitAssessmentFailure) {
      return {
        title: intl.formatMessage(messagesErrors.submitAssessmentErrorTitle),
        description: intl.formatMessage(messagesErrors.submitAssessmentErrorDescription),
        detail: systemError.detail
      };
    }

    return;
  }, [intl, systemError]);
  return /*#__PURE__*/React.createElement(Notifications, {
    open: !!systemError,
    text: text
  });
};

var QuestionGroupMode;

(function (QuestionGroupMode) {
  /** questions form a whole like an assessment, one set of question controls operate across group */
  QuestionGroupMode["Amalgamated"] = "Amalgamated";
  /** each question has its own set of controls and operates independently */

  QuestionGroupMode["Autonomous"] = "Autonomous";
})(QuestionGroupMode || (QuestionGroupMode = {}));

var QuestionGroupLayout;

(function (QuestionGroupLayout) {
  /** questions form a stack all present from start */
  QuestionGroupLayout["VerticalAllTogether"] = "VerticalAllTogether";
  /** questions occupy same space with a nav bar to navigate between them */

  QuestionGroupLayout["OneAtAtime"] = "OneAtAtime";
})(QuestionGroupLayout || (QuestionGroupLayout = {}));

var QuestionGroupScrollTarget;

(function (QuestionGroupScrollTarget) {
  QuestionGroupScrollTarget[QuestionGroupScrollTarget["Top"] = 0] = "Top";
  QuestionGroupScrollTarget[QuestionGroupScrollTarget["TopOfQuestions"] = 1] = "TopOfQuestions";
  QuestionGroupScrollTarget[QuestionGroupScrollTarget["TopOfQuestion"] = 2] = "TopOfQuestion";
})(QuestionGroupScrollTarget || (QuestionGroupScrollTarget = {}));

var Styled$g = {
  Container: styled.div.withConfig({
    displayName: "QuestionGroup__Container"
  })(templateObject_1$g || (templateObject_1$g = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    height: 100%;\n    position: relative;\n  "], ["\n    display: flex;\n    flex-direction: column;\n    height: 100%;\n    position: relative;\n  "]))),
  ScrollPane: styled.div.withConfig({
    displayName: "QuestionGroup__ScrollPane"
  })(templateObject_3$a || (templateObject_3$a = __makeTemplateObject(["\n    flex: 1;\n    overflow-y: auto;\n    overflow-x: hidden;\n    ", "\n  "], ["\n    flex: 1;\n    overflow-y: auto;\n    overflow-x: hidden;\n    ", "\n  "])), function (_a) {
    var controlsHeight = _a.controlsHeight;
    return css(templateObject_2$c || (templateObject_2$c = __makeTemplateObject(["\n        margin-bottom: ", "px;\n      "], ["\n        margin-bottom: ", "px;\n      "])), isNumber(controlsHeight) ? controlsHeight : 0);
  }),
  ScrollContent: styled.div.withConfig({
    displayName: "QuestionGroup__ScrollContent"
  })(templateObject_4$9 || (templateObject_4$9 = __makeTemplateObject(["\n    margin: 0;\n    padding: 0;\n  "], ["\n    margin: 0;\n    padding: 0;\n  "]))),
  QuestionsContainer: styled.div.withConfig({
    displayName: "QuestionGroup__QuestionsContainer"
  })(templateObject_7$4 || (templateObject_7$4 = __makeTemplateObject(["\n    width: 100%;\n    box-sizing: border-box;\n    margin: 0;\n    ", "\n    ", "\n  "], ["\n    width: 100%;\n    box-sizing: border-box;\n    margin: 0;\n    ", "\n    ", "\n  "])), function (_a) {
    var vertMargin = _a.vertMargin;
    return vertMargin && css(templateObject_5$8 || (templateObject_5$8 = __makeTemplateObject(["\n        padding: 16px 0;\n      "], ["\n        padding: 16px 0;\n      "])));
  }, function (_a) {
    var fullHeight = _a.fullHeight;
    return fullHeight && css(templateObject_6$7 || (templateObject_6$7 = __makeTemplateObject(["\n        display: inline-block;\n      "], ["\n        display: inline-block;\n      "])));
  })
};

var QuestionGroup = function (_a) {
  var mode = _a.mode,
      header = _a.header,
      prologue = _a.prologue;
  var intl = useIntl();

  var _b = useQuestionGroupContext(),
      questions = _b.questions,
      evaluator = _b.evaluator,
      itemIndex = _b.itemIndex,
      setItemIndex = _b.setItemIndex,
      itemStates = _b.itemStates,
      submitItem = _b.submitItem,
      resetItem = _b.resetItem,
      submitRequested = _b.submitRequested,
      setSubmitRequested = _b.setSubmitRequested,
      submitted = _b.submitted,
      setSubmitted = _b.setSubmitted,
      result = _b.result,
      questionEvaluations = _b.questionEvaluations,
      setEvaluatedResult = _b.setEvaluatedResult,
      answerRevealed = _b.answerRevealed,
      setAnswerRevealed = _b.setAnswerRevealed,
      reset = _b.reset,
      setReset = _b.setReset,
      totalQuestionsUnanswered = _b.totalQuestionsUnanswered,
      setItemAnswerGiven = _b.setItemAnswerGiven,
      someQuestionsAreUnanswered = _b.someQuestionsAreUnanswered,
      evaluatedData = _b.evaluatedData,
      allQuestionsAreAnswered = _b.allQuestionsAreAnswered,
      incorrectlyAnsweredQuestions = _b.incorrectlyAnsweredQuestions,
      locked = _b.locked,
      setLocked = _b.setLocked,
      setUnlocked = _b.setUnlocked,
      systemError = _b.systemError,
      notifySystemError = _b.notifySystemError,
      hostApi = _b.hostApi,
      ariaLiveMessage = _b.ariaLiveMessage,
      ariaLiveMode = _b.ariaLiveMode,
      sendAriaLiveMessage = _b.sendAriaLiveMessage,
      clearAriaLiveMessage = _b.clearAriaLiveMessage;

  var scrollRef = useRef(null);
  var questionsContainerRef = useRef(null);

  var _c = useState(0),
      controlsHeight = _c[0],
      setControlsHeight = _c[1];

  useEffect(function () {
    if (reset) {
      sendAriaLiveMessage(intl.formatMessage(messagesQuestionGroup.assessmentResetA11y));
      evaluator.startAssessment();
      setReset(false);
    }
  });

  var _d = useState(false),
      answerWasRevealed = _d[0],
      setAnswerWasRevealed = _d[1];

  useEffect(function () {
    if (answerWasRevealed) {
      sendAriaLiveMessage(intl.formatMessage(messagesQuestionGroup.assessmentAnswersRevealedA11y));
      setAnswerWasRevealed(false);
    }
  }, [answerWasRevealed, intl, sendAriaLiveMessage]);
  var dataError = useMemo(function () {
    // some early live data was a little patchy so call this out probably
    // only applicable in dev but localize if it becomes a thing in prod
    var error = "";

    if (questions.some(function (question) {
      return !getQuestionLikeId(question);
    })) {
      error = "Question group has detected questions with missing ids";
    } else if (questions.some(function (question) {
      return !Object.keys(question).length;
    })) {
      error = "Question group has detected questions without data";
    } else {
      var questionIds = Array.from(new Set(questions.map(function (question) {
        return getQuestionLikeId(question);
      })));

      if (questionIds.length !== questions.length) {
        error = "Question group has detected duplicated question ids";
      }
    }

    return error;
  }, [questions]);
  var submittedUnansweredQuestionState = useMemo(function () {
    return {
      correct: false,
      answers: {}
    };
  }, []);
  var debouncedStoreQuestionAnswers = useMemo(function () {
    return debounce(function (questionId, userAnswer) {
      evaluator.storeAnswers(questionId, userAnswer);
    }, 1000);
  }, [evaluator]);

  if (dataError) {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        margin: "24px"
      }
    }, /*#__PURE__*/React.createElement("p", null, "Question Display Error:"), /*#__PURE__*/React.createElement("p", null, dataError));
  }

  var isAmalgamated = mode === QuestionGroupMode.Amalgamated;
  var isAutonomous = mode === QuestionGroupMode.Autonomous;

  var scrollToTop = function (target) {
    if (target === void 0) {
      target = QuestionGroupScrollTarget.Top;
    }

    setTimeout(function () {
      if (scrollRef.current && questionsContainerRef.current) {
        var yPos = target === QuestionGroupScrollTarget.TopOfQuestions || target === QuestionGroupScrollTarget.TopOfQuestion ? questionsContainerRef.current.offsetTop : 0;
        scrollRef.current.scroll({
          top: yPos,
          behavior: "smooth"
        });
      }
    }, 1);
  };

  if (submitted && !result && !locked && !systemError) {
    var userAnswers = Object.keys(itemStates).reduce(function (acc, questionId) {
      acc[questionId] = itemStates[questionId].userAnswer;
      return acc;
    }, {});
    sendAriaLiveMessage(intl.formatMessage(messagesBase.evaluating), AriaLiveMode.Assertive);
    setLocked(QuestionGroupLockedReason.Waiting);
    evaluator === null || evaluator === void 0 ? void 0 : evaluator.submitAssessment(userAnswers).then(function (result) {
      defer(function () {
        setEvaluatedResult(result);
        setUnlocked();
        scrollToTop();
      });
    }).catch(function (e) {
      defer(function () {
        setSubmitted(false);
        setUnlocked();
        notifySystemError({
          type: CoachmeAssessmentErrorType.SubmitAssessmentFailure,
          detail: e.toString()
        });
      });
    });
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styled$g.Container, null, /*#__PURE__*/React.createElement(Styled$g.ScrollPane, {
    ref: scrollRef,
    controlsHeight: controlsHeight
  }, /*#__PURE__*/React.createElement(Styled$g.ScrollContent, null, header, prologue, isAutonomous && questions.length > 1 && /*#__PURE__*/React.createElement(QuestionNavBar, {
    itemIndex: itemIndex,
    items: questions.map(function (question) {
      return questionCorrectnessToQuestionState(itemStates[getQuestionLikeId(question)].correct);
    }),
    onChange: function (newItemIndex) {
      setItemIndex(newItemIndex);
    }
  }), /*#__PURE__*/React.createElement(Styled$g.QuestionsContainer, {
    ref: questionsContainerRef,
    fullHeight: isAutonomous,
    vertMargin: isAmalgamated
  }, isAmalgamated && /*#__PURE__*/React.createElement(A11y, {
    focused: reset || answerWasRevealed
  }, intl.formatMessage(messagesQuestionGroup.assessmentStart)), questions.map(function (question, index) {
    var _a, _b;

    var isVisible = isAmalgamated || isAutonomous && index === itemIndex;

    if (isTutorial(question)) {
      return /*#__PURE__*/React.createElement(Tutorial, {
        key: question.id,
        visible: isVisible,
        evaluator: evaluator,
        evaluatedData: evaluatedData,
        questionIndex: index,
        siblingQuestionCount: questions.length,
        data: question,
        events: {
          onSubmit: function (id, correct, userAnswer) {
            delay(function () {
              return submitItem(id, correct, userAnswer);
            }, 20);
          },
          onReset: function (id) {
            resetItem(id);
          },
          onNextQuestion: function () {
            scrollToTop();
            defer(function () {
              return setItemIndex(Math.min(itemIndex + 1, questions.length - 1));
            });
          },
          onDone: function () {
            var _a;

            (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onRequestClose) === null || _a === void 0 ? void 0 : _a.call(hostApi);
          },
          onSetControlsSize: function (id, height) {
            setControlsHeight(height);
          }
        },
        hostApi: hostApi
      });
    } else {
      var QuestionComponent = questionLookup[question.question_type];

      if (!QuestionComponent) {
        throw "Question group unknown question type" + question.question_type;
      } // unanswered questions in a submitted summative wont have an evaluation record,
      // so feed them a memoized incorrect eval so they become submitted and incorrect


      var restoredQuestionData = ((_a = evaluatedData === null || evaluatedData === void 0 ? void 0 : evaluatedData.questions) === null || _a === void 0 ? void 0 : _a[question.id]) || (typeof ((_b = evaluatedData === null || evaluatedData === void 0 ? void 0 : evaluatedData.assessment) === null || _b === void 0 ? void 0 : _b.score) === "number" ? submittedUnansweredQuestionState : undefined);
      return /*#__PURE__*/React.createElement(QuestionComponent, {
        key: question.id,
        data: question,
        visible: isVisible,
        id: question.id,
        mode: isAmalgamated ? QuestionMode.Child : isAutonomous ? QuestionMode.StandaloneInGroup : QuestionMode.Standalone,
        questionIndex: index,
        siblingQuestionCount: questions.length,
        showQuestionIndex: isAmalgamated,
        controlledSubmit: submitted && isAutonomous,
        controlledReveal: answerRevealed,
        controlledReset: reset,
        dontValidate: isAmalgamated,
        evaluator: evaluator,
        evaluatedData: restoredQuestionData,
        parentEvaluatedData: submitted && questionEvaluations ? questionEvaluations[question.id] : null,
        events: {
          onAnswerGiven: function (id, answerGiven, userAnswer, flags) {
            if (isAmalgamated) {
              if (flags === null || flags === void 0 ? void 0 : flags.debounce) {
                debouncedStoreQuestionAnswers(id, userAnswer);
              } else {
                evaluator.storeAnswers(id, userAnswer);
              } // todo error handling

            }

            setItemAnswerGiven(id, answerGiven, userAnswer);
          },
          onSubmit: function (id, correct, userAnswer) {
            submitItem(id, correct, userAnswer);
          },
          onRevealAnswer: function (id) {
            scrollToTop(QuestionGroupScrollTarget.TopOfQuestion);
            defer(function () {
              var _a;

              return (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onRevealAnswer) === null || _a === void 0 ? void 0 : _a.call(hostApi, id);
            });
          },
          onReset: function (id) {
            scrollToTop();
            resetItem(id);
            defer(function () {
              var _a;

              return (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onRetryQuestion) === null || _a === void 0 ? void 0 : _a.call(hostApi, id);
            });
          },
          onNextQuestion: function () {
            scrollToTop();
            setItemIndex(Math.min(itemIndex + 1, questions.length - 1));
          },
          onDone: function () {
            var _a;

            (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onRequestClose) === null || _a === void 0 ? void 0 : _a.call(hostApi);
          },
          onSetControlsSize: function (id, height) {
            setControlsHeight(height);
          }
        }
      });
    }
  })))), isAmalgamated && /*#__PURE__*/React.createElement(QuestionGroupControls, {
    submitted: submitted && !!result,
    onSubmit: function () {
      if (allQuestionsAreAnswered) {
        setSubmitted(true);
      } else {
        setSubmitRequested(true);
      }
    },
    onReset: function () {
      setReset(true);
      setAnswerRevealed(false);
      setSubmitted(false);
      scrollToTop(QuestionGroupScrollTarget.TopOfQuestions);
      defer(function () {
        var _a;

        if (questions.length) {
          (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onRetryQuestion) === null || _a === void 0 ? void 0 : _a.call(hostApi, questions.map(function (question) {
            return question.id;
          }));
        }
      });
    },
    onReveal: function () {
      setAnswerRevealed(true);
      setAnswerWasRevealed(true);
      scrollToTop(QuestionGroupScrollTarget.TopOfQuestions);
      defer(function () {
        var _a;

        if (incorrectlyAnsweredQuestions.length) {
          (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onRevealAnswer) === null || _a === void 0 ? void 0 : _a.call(hostApi, incorrectlyAnsweredQuestions);
        }
      });
    },
    onDone: function () {
      var _a;

      (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onRequestClose) === null || _a === void 0 ? void 0 : _a.call(hostApi);
    }
  })), /*#__PURE__*/React.createElement(QuestionGroupNotifications, {
    systemError: systemError
  }), /*#__PURE__*/React.createElement(ModalBlock, {
    blocked: locked
  }), someQuestionsAreUnanswered && submitRequested && /*#__PURE__*/React.createElement(MessageBox, {
    type: MessagePresetType.ContinueCancel,
    open: someQuestionsAreUnanswered && submitRequested,
    title: intl.formatMessage(messagesAssessmentConfirmIncompleteDialog.title, {
      count: totalQuestionsUnanswered
    }),
    message: intl.formatMessage(messagesAssessmentConfirmIncompleteDialog.message),
    onRequestClose: function () {
      setSubmitRequested(false);
    },
    onResponse: function (response) {
      if (response === MessageResponse.Continue) {
        setSubmitted(true);
      }
    }
  }), /*#__PURE__*/React.createElement(AriaLiveRegion, {
    mode: ariaLiveMode,
    onClear: function () {
      clearAriaLiveMessage();
    }
  }, ariaLiveMessage));
};
var templateObject_1$g, templateObject_2$c, templateObject_3$a, templateObject_4$9, templateObject_5$8, templateObject_6$7, templateObject_7$4;

function getStatusTextColorCSS(questionStatus) {
  switch (questionStatus) {
    case QuestionStatus.Complete:
      return primaryShade(6);

    case QuestionStatus.Incomplete:
      return cloud.getShade(6);

    case QuestionStatus.Correct:
      return green.getShade(6);

    case QuestionStatus.Incorrect:
      return red.getShade(6);

    default:
      return cloud.getShade(10);
  }
}

var Styled$f = {
  StatusText: styled.div.withConfig({
    displayName: "StatusText"
  })(templateObject_1$f || (templateObject_1$f = __makeTemplateObject(["\n    font-weight: 500;\n    color: ", ";\n    display: flex;\n    flex-direction: row;\n    align-items: center;\n    svg {\n      margin-right: 12px;\n      min-width: 18px;\n    }\n  "], ["\n    font-weight: 500;\n    color: ", ";\n    display: flex;\n    flex-direction: row;\n    align-items: center;\n    svg {\n      margin-right: 12px;\n      min-width: 18px;\n    }\n  "])), function (p) {
    return getStatusTextColorCSS(p.status);
  })
};

var StatusText = function (_a) {
  var status = _a.status,
      className = _a.className,
      count = _a.count,
      withIcon = _a.withIcon;
  var msg = count === undefined ? messagesQuestion["status" + status] : messagesAssessment["score" + status + "Count"];
  return /*#__PURE__*/React.createElement(Styled$f.StatusText, {
    status: status,
    className: className
  }, withIcon && status === QuestionStatus.Correct ? /*#__PURE__*/React.createElement(Checkmark, null) : withIcon && status === QuestionStatus.Incorrect ? /*#__PURE__*/React.createElement(XSmall, null) : withIcon && status === QuestionStatus.Incomplete ? /*#__PURE__*/React.createElement(QuestionCircle, null) : null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, msg, {
    values: {
      count: count
    }
  })));
};
var templateObject_1$f;

var Styled$e = {
  Container: styled.div.withConfig({
    displayName: "CoachmeAssessmentResults__Container"
  })(templateObject_1$e || (templateObject_1$e = __makeTemplateObject(["\n    text-align: center;\n    margin: 16px 16px 0;\n    position: relative;\n  "], ["\n    text-align: center;\n    margin: 16px 16px 0;\n    position: relative;\n  "]))),
  SvgBackground: styled.div.withConfig({
    displayName: "CoachmeAssessmentResults__SvgBackground"
  })(templateObject_2$b || (templateObject_2$b = __makeTemplateObject(["\n    margin-bottom: 24px;\n  "], ["\n    margin-bottom: 24px;\n  "]))),
  SvgForeground: styled.div.withConfig({
    displayName: "CoachmeAssessmentResults__SvgForeground"
  })(templateObject_3$9 || (templateObject_3$9 = __makeTemplateObject(["\n    width: 387px;\n    position: absolute;\n    top: 0;\n  "], ["\n    width: 387px;\n    position: absolute;\n    top: 0;\n  "]))),
  TextOverlay: styled.div.withConfig({
    displayName: "CoachmeAssessmentResults__TextOverlay"
  })(templateObject_4$8 || (templateObject_4$8 = __makeTemplateObject(["\n    position: absolute;\n    top: 0;\n    width: 100%;\n    font-size: 1.8rem;\n  "], ["\n    position: absolute;\n    top: 0;\n    width: 100%;\n    font-size: 1.8rem;\n  "]))),
  TextOverlayTitle: styled.div.withConfig({
    displayName: "CoachmeAssessmentResults__TextOverlayTitle"
  })(templateObject_5$7 || (templateObject_5$7 = __makeTemplateObject(["\n    text-transform: uppercase;\n    text-align: center;\n  "], ["\n    text-transform: uppercase;\n    text-align: center;\n  "]))),
  TextOverlayTitleText: styled.h3.withConfig({
    displayName: "CoachmeAssessmentResults__TextOverlayTitleText"
  })(templateObject_6$6 || (templateObject_6$6 = __makeTemplateObject(["\n    display: inline-block;\n    margin-top: 13px;\n    margin-bottom: 13px;\n    font-weight: 800;\n    font-size: 1.8rem;\n    padding: 3px;\n    :focus {\n      border-radius: 4px;\n      outline: none;\n      box-shadow: 0 0 0 2px ", ";\n    }\n  "], ["\n    display: inline-block;\n    margin-top: 13px;\n    margin-bottom: 13px;\n    font-weight: 800;\n    font-size: 1.8rem;\n    padding: 3px;\n    :focus {\n      border-radius: 4px;\n      outline: none;\n      box-shadow: 0 0 0 2px ", ";\n    }\n  "])), primaryShade(6)),
  TextOverlayEntries: styled.div.withConfig({
    displayName: "CoachmeAssessmentResults__TextOverlayEntries"
  })(templateObject_7$3 || (templateObject_7$3 = __makeTemplateObject(["\n    display: inline-block;\n    text-align: left;\n    margin-left: 180px;\n    div {\n      margin-bottom: 16px;\n    }\n  "], ["\n    display: inline-block;\n    text-align: left;\n    margin-left: 180px;\n    div {\n      margin-bottom: 16px;\n    }\n  "])))
};

var CoachmeAssessmentResults = function (_a) {
  var id = _a.id,
      scorePercent = _a.scorePercent,
      totalCorrect = _a.totalCorrect,
      totalIncorrect = _a.totalIncorrect,
      totalIncomplete = _a.totalIncomplete,
      focusResults = _a.focusResults;
  var intl = useIntl();
  var resultsRef = useRef(null);
  useTakeFocus(resultsRef, {
    takeFocus: focusResults,
    temporary: true,
    smoothScrollIntoView: true
  });
  var lineItems = [{
    count: totalCorrect,
    type: QuestionStatus.Correct
  }, {
    count: totalIncorrect - totalIncomplete,
    type: QuestionStatus.Incorrect
  }, {
    count: totalIncomplete,
    type: QuestionStatus.Incomplete
  }];
  var a11yResults = intl.formatMessage(messagesAssessment.resultsA11y, {
    count: totalCorrect + totalIncorrect,
    score: scorePercent,
    breakdown: lineItems.reduce(function (acc, item) {
      return acc + (!item.count ? "" : intl.formatMessage(messagesAssessment.resultsA11yLineItem, {
        count: item.count,
        status: intl.formatMessage(messagesQuestion.status, {
          status: item.type
        })
      }) + ", ");
    }, "")
  });
  var resultsA11yDescriptionId = "results-title-a11y-" + id;
  return /*#__PURE__*/React.createElement(Styled$e.Container, null, /*#__PURE__*/React.createElement(Styled$e.SvgBackground, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("svg", {
    width: "100%",
    height: "216px",
    preserveAspectRatio: "none",
    viewBox: "0 0 387 216",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
    x1: "99.455%",
    y1: "35.521%",
    x2: "3.03%",
    y2: "65.492%",
    id: "prefix__a"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "#F6F8FC",
    offset: "0%"
  }), /*#__PURE__*/React.createElement("stop", {
    stopColor: "#CED2D9",
    offset: "100%"
  })), /*#__PURE__*/React.createElement("linearGradient", {
    x1: "99.238%",
    y1: "35.588%",
    x2: "2.499%",
    y2: "65.657%",
    id: "prefix__c"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "#F6F8FC",
    offset: "0%"
  }), /*#__PURE__*/React.createElement("stop", {
    stopColor: "#CED2D9",
    offset: "100%"
  })), /*#__PURE__*/React.createElement("path", {
    d: "M.538 4.004A4 4 0 014.548 0h377.98a4.003 4.003 0 014.01 4.004v207.992a4 4 0 01-4.01 4.004H4.548a4.003 4.003 0 01-4.01-4.004V4.004z",
    id: "prefix__b"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M.538 4.004A4 4 0 014.548 0h377.98a4.003 4.003 0 014.01 4.004v207.992a4 4 0 01-4.01 4.004H4.548a4.003 4.003 0 01-4.01-4.004V4.004z",
    fill: "url(#prefix__a)"
  }), /*#__PURE__*/React.createElement("mask", {
    id: "prefix__d",
    fill: "#fff"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#prefix__b"
  })), /*#__PURE__*/React.createElement("use", {
    fill: "url(#prefix__c)",
    xlinkHref: "#prefix__b"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "#FFF",
    mask: "url(#prefix__d)",
    d: "M-7 192l404.963-30v70H-7z"
  })))), /*#__PURE__*/React.createElement(Styled$e.SvgForeground, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 387 216",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
    x1: "99.455%",
    y1: "35.521%",
    x2: "3.03%",
    y2: "65.492%",
    id: "prefix__a"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "#F6F8FC",
    offset: "0%"
  }), /*#__PURE__*/React.createElement("stop", {
    stopColor: "#CED2D9",
    offset: "100%"
  })), /*#__PURE__*/React.createElement("linearGradient", {
    x1: "99.238%",
    y1: "35.588%",
    x2: "2.499%",
    y2: "65.657%",
    id: "prefix__c"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "#F6F8FC",
    offset: "0%"
  }), /*#__PURE__*/React.createElement("stop", {
    stopColor: "#CED2D9",
    offset: "100%"
  })), /*#__PURE__*/React.createElement("path", {
    d: "M.538 4.004A4 4 0 014.548 0h377.98a4.003 4.003 0 014.01 4.004v207.992a4 4 0 01-4.01 4.004H4.548a4.003 4.003 0 01-4.01-4.004V4.004z",
    id: "prefix__b"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("mask", {
    id: "prefix__d",
    fill: "#fff"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#prefix__b"
  })), /*#__PURE__*/React.createElement("path", {
    d: "M12.436 176.76h1.948c6.91 0 12.402 5.504 12.402 12.428v3.906c0 2.13 1.772 3.906 3.898 3.906h1.063c2.126 0 3.897-1.775 3.897-3.906v-3.906c0-6.924 5.492-12.427 12.402-12.427h1.594c2.126 0 3.898-1.775 3.898-3.906v-.71c0-2.13-1.772-3.906-3.898-3.906h-1.77c-6.91 0-12.402-5.503-12.402-12.427v-3.906c0-2.13-1.772-3.906-3.898-3.906h-1.064c-2.125 0-3.897 1.775-3.897 3.906v3.906c0 2.84-1.063 5.503-2.658 7.634h-.177v.355c-2.303 2.663-5.67 4.438-9.567 4.438h-1.771c-2.126 0-3.898 1.775-3.898 3.906v.71c0 2.308 1.772 3.906 3.898 3.906z",
    fill: "#FFF",
    opacity: 0.386,
    mask: "url(#prefix__d)"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M34.548 105c-3.213 3.217-3.213 8.365 0 11.675l4.675 5.148c1.948 2.114 2.727 4.872 2.24 7.722l-1.46 6.435c-.975 4.412 1.752 8.825 6.135 10.02m3-72l-3.807 1.212c-4.378 1.4-6.947 5.876-5.996 10.166L40.763 92m89.375-29.447l-.555-1.7c-1.386-4.112-5.822-6.527-10.074-5.632l-6.562 1.34c-2.773.716-5.638-.267-7.764-2.056l-5.176-4.292c-3.235-2.95-8.41-2.95-11.738 0l-5.176 4.292c-2.126 1.789-4.899 2.504-7.764 2.057l-6.47-1.341c-4.436-.895-8.872 1.61-10.074 5.633L58.138 63m86.204 82c4.095-1.367 6.498-5.742 5.608-9.935l-1.335-6.47c-.712-2.735.267-5.56 2.047-7.657l4.273-5.104c2.937-3.19 2.937-8.294 0-11.575l-4.273-5.104c-1.78-2.096-2.492-4.83-2.047-7.656l1.335-6.47c.89-4.376-1.602-8.75-5.608-9.935L141.138 74",
    stroke: "#91A3D4",
    strokeWidth: 3.818,
    strokeLinecap: "round",
    mask: "url(#prefix__d)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "#5773BF",
    fillRule: "nonzero",
    mask: "url(#prefix__d)",
    d: "M78.545 215.908V216l21.593-66.05L68.731 140l-21.593 65.959.093.092 20.378-9.673z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "#5773BF",
    fillRule: "nonzero",
    mask: "url(#prefix__d)",
    d: "M143.046 201.822h.092L117.06 138l-29.922 12.178L113.216 214h.092l9.15-20.236z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M93.823 148.503l-25.14-7.95-5.545 17.178c1.848 1.37 4.16 2.01 6.47 1.553l6.007-1.279c2.588-.64 5.084.183 7.025 1.919l4.713 4.02c2.958 2.741 7.671 2.741 10.629 0l4.714-4.02c1.94-1.645 4.436-2.559 7.024-1.919l6.007 1.28c3.42.73 6.84-.823 8.411-3.656L117.298 139l-23.475 9.503z",
    fill: "#334A88",
    fillRule: "nonzero",
    mask: "url(#prefix__d)"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M143.288 106.25l-3.563-4.203c-1.553-1.736-2.101-4.02-1.736-6.212l1.097-5.299c.73-3.563-1.37-7.217-4.751-8.13l-5.299-1.736c-2.1-.731-3.745-2.467-4.568-4.568l-1.735-5.299c-1.097-3.38-4.75-5.481-8.131-4.75l-5.299 1.096c-2.284.548-4.568-.183-6.212-1.736l-4.202-3.563c-2.65-2.467-6.852-2.467-9.502 0l-4.202 3.563c-1.736 1.553-4.02 2.101-6.212 1.736l-5.299-1.097c-3.563-.73-7.217 1.37-8.13 4.751l-1.737 5.299c-.73 2.1-2.466 3.745-4.567 4.568l-5.3 1.735c-3.38 1.097-5.48 4.75-4.75 8.131l1.097 5.299c.548 2.284-.183 4.568-1.736 6.212l-3.563 4.202c-2.467 2.65-2.467 6.852 0 9.502l3.563 4.202c1.553 1.736 2.101 4.02 1.736 6.212l-1.097 5.299c-.73 3.563 1.37 7.217 4.75 8.13l5.3 1.736c2.1.731 3.745 2.467 4.567 4.568l1.736 5.299c1.097 3.38 4.75 5.481 8.131 4.75l5.299-1.096c2.284-.548 4.568.183 6.212 1.736l4.202 3.563c2.65 2.467 6.852 2.467 9.502 0l4.202-3.563c1.736-1.553 4.02-2.284 6.212-1.736l5.299 1.097c3.563.73 7.217-1.37 8.13-4.751l1.736-5.299c.731-2.1 2.467-3.745 4.568-4.568l5.299-1.735c3.38-1.097 5.481-4.75 4.75-8.131l-1.096-5.299c-.548-2.284.183-4.568 1.736-6.212l3.563-4.202c2.467-2.65 2.467-6.852 0-9.502z",
    fill: "#B2BEE0",
    fillRule: "nonzero",
    mask: "url(#prefix__d)"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M90.379 61.828l-4.293 3.564c-1.773 1.554-4.105 2.103-6.345 1.737l-5.412-1.097c-3.64-.73-7.372 1.371-8.305 4.753l-1.773 5.301c-.747 2.102-2.52 3.747-4.666 4.57l-5.412 1.736c-3.453 1.097-5.6 4.753-4.853 8.135l1.12 5.301c.56 2.285-.187 4.57-1.773 6.215l-3.64 4.204c-2.519 2.65-2.519 6.855 0 9.506l3.64 4.204c1.586 1.737 2.146 4.021 1.773 6.215l-1.12 5.301c-.746 3.565 1.4 7.22 4.853 8.135l5.412 1.736c2.146.731 3.826 2.468 4.666 4.57l1.773 5.301c1.12 3.382 4.852 5.484 8.305 4.753l5.412-1.097c2.333-.548 4.666.183 6.345 1.737l4.293 3.564c1.306 1.188 3.08 1.828 4.759 1.828V60c-1.68 0-3.36.64-4.76 1.828z",
    fill: "#7188C7",
    fillRule: "nonzero",
    opacity: 0.39,
    mask: "url(#prefix__d)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "#FFF",
    mask: "url(#prefix__d)",
    cx: 95.138,
    cy: 111,
    r: 35
  }), /*#__PURE__*/React.createElement("path", {
    d: "M24.256 46.891h1.56c5.527 0 9.92 4.38 9.92 9.892v3.108c0 1.696 1.418 3.109 3.119 3.109h.85c1.7 0 3.118-1.413 3.118-3.109v-3.108c0-5.511 4.394-9.892 9.921-9.892h1.276c1.7 0 3.118-1.413 3.118-3.108v-.566c0-1.695-1.417-3.108-3.118-3.108h-1.417c-5.528 0-9.922-4.38-9.922-9.892V27.11c0-1.696-1.417-3.109-3.118-3.109h-.85c-1.701 0-3.118 1.413-3.118 3.109v3.108c0 2.261-.85 4.38-2.126 6.076h-.142v.283c-1.843 2.12-4.535 3.533-7.654 3.533h-1.417c-1.7 0-3.118 1.413-3.118 3.108v.566c0 1.837 1.417 3.108 3.118 3.108z",
    fill: "#FFF",
    fillRule: "nonzero",
    opacity: 0.448,
    mask: "url(#prefix__d)"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M139.784 56.326h.822c2.918 0 5.237 2.359 5.237 5.326v1.674c0 .913.748 1.674 1.645 1.674h.449c.898 0 1.646-.76 1.646-1.674v-1.674c0-2.967 2.319-5.326 5.236-5.326h.673c.898 0 1.646-.76 1.646-1.674v-.304c0-.913-.748-1.674-1.646-1.674h-.748c-2.917 0-5.236-2.359-5.236-5.326v-1.674c0-.913-.748-1.674-1.646-1.674h-.448c-.898 0-1.646.76-1.646 1.674v1.674c0 1.217-.449 2.359-1.122 3.272h-.075v.152c-.972 1.141-2.394 1.902-4.04 1.902h-.747c-.898 0-1.646.76-1.646 1.674v.304c0 .99.748 1.674 1.646 1.674z",
    fill: "#FFF",
    fillRule: "nonzero",
    opacity: 0.63,
    mask: "url(#prefix__d)"
  })), /*#__PURE__*/React.createElement("g", {
    textAnchor: "middle",
    dominantBaseline: "middle"
  }, /*#__PURE__*/React.createElement("text", {
    x: "96",
    y: "114",
    style: {
      fontWeight: 700,
      fontSize: "2.8rem"
    },
    fill: "black"
  }, scorePercent, "%")))), /*#__PURE__*/React.createElement(Styled$e.TextOverlay, null, /*#__PURE__*/React.createElement(Styled$e.TextOverlayTitle, null, /*#__PURE__*/React.createElement(Styled$e.TextOverlayTitleText, {
    ref: resultsRef,
    "aria-describedby": resultsA11yDescriptionId
  }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesAssessment.resultsTitle)))), /*#__PURE__*/React.createElement(A11y, {
    id: resultsA11yDescriptionId,
    "aria-hidden": true
  }, a11yResults), /*#__PURE__*/React.createElement(Styled$e.TextOverlayEntries, {
    "aria-hidden": true
  }, lineItems.map(function (item) {
    return /*#__PURE__*/React.createElement(StatusText, {
      key: item.type,
      withIcon: true,
      status: item.type,
      count: item.count
    });
  }))));
};
var templateObject_1$e, templateObject_2$b, templateObject_3$9, templateObject_4$8, templateObject_5$7, templateObject_6$6, templateObject_7$3;

var CloseBtn = function (_a) {
  var onClick = _a.onClick;
  var intl = useIntl();
  return /*#__PURE__*/React.createElement(IconButton, {
    label: intl.formatMessage(messagesBase.close),
    size: "small",
    rounded: true,
    tooltipPlacement: "left",
    icon: /*#__PURE__*/React.createElement(XSmall, {
      color: cloud.getShade(10)
    }),
    onClick: onClick
  });
};

var InfoMessageOrientationType;

(function (InfoMessageOrientationType) {
  InfoMessageOrientationType["Below"] = "below";
  InfoMessageOrientationType["Above"] = "above";
})(InfoMessageOrientationType || (InfoMessageOrientationType = {}));

var InfoMessageIconAlignType;

(function (InfoMessageIconAlignType) {
  InfoMessageIconAlignType["Normal"] = "normal";
  InfoMessageIconAlignType["Superscript"] = "superscript";
})(InfoMessageIconAlignType || (InfoMessageIconAlignType = {}));

var iconSuperscriptSize = 10;
var iconNormalSize = 16;
var margin = 2;
var arrowSize = 8;
var yOffset = 10;
var Styled$d = {
  Container: styled.div.withConfig({
    displayName: "InfoMessage__Container"
  })(templateObject_1$d || (templateObject_1$d = __makeTemplateObject(["\n    display: inline-block;\n    flex-grow: 1;\n    margin-left: ", "px;\n    ", "\n    align-self: ", ";\n    position: relative;\n    svg {\n      width: ", "px;\n      height: ", "px;\n    }\n    button {\n      cursor: pointer;\n      background-color: transparent;\n      padding: 3px;\n      border: none;\n      line-height: 1;\n      &:focus-visible {\n        outline: none;\n        box-shadow: 0 0 0 2px ", ";\n      }\n    }\n  "], ["\n    display: inline-block;\n    flex-grow: 1;\n    margin-left: ", "px;\n    ", "\n    align-self: ", ";\n    position: relative;\n    svg {\n      width: ", "px;\n      height: ", "px;\n    }\n    button {\n      cursor: pointer;\n      background-color: transparent;\n      padding: 3px;\n      border: none;\n      line-height: 1;\n      &:focus-visible {\n        outline: none;\n        box-shadow: 0 0 0 2px ", ";\n      }\n    }\n  "])), margin, function (_a) {
    var iconAlignment = _a.iconAlignment;
    return iconAlignment === InfoMessageIconAlignType.Superscript && "margin-top: -4px;";
  }, function (_a) {
    var iconAlignment = _a.iconAlignment;
    return iconAlignment === InfoMessageIconAlignType.Superscript ? "top" : "center";
  }, function (_a) {
    var iconSize = _a.iconSize;
    return iconSize;
  }, function (_a) {
    var iconSize = _a.iconSize;
    return iconSize;
  }, primaryShade(6)),
  MessageHolder: styled.aside.withConfig({
    displayName: "InfoMessage__MessageHolder"
  })(templateObject_4$7 || (templateObject_4$7 = __makeTemplateObject(["\n    margin-left: ", "px;\n    display: ", ";\n    font-size: 1.2rem;\n    background-color: ", ";\n    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);\n    color: #ffffff;\n    padding: 14px 16px;\n    border-radius: 4px;\n    width: ", "px;\n    line-height: 1.5;\n    position: absolute;\n    cursor: default;\n    :before {\n      content: \"\";\n      width: 0;\n      height: 0;\n      left: 50%;\n      position: absolute;\n      pointer-events: none;\n      border: 8px solid transparent;\n    }\n    ", ";\n  "], ["\n    margin-left: ", "px;\n    display: ", ";\n    font-size: 1.2rem;\n    background-color: ", ";\n    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);\n    color: #ffffff;\n    padding: 14px 16px;\n    border-radius: 4px;\n    width: ", "px;\n    line-height: 1.5;\n    position: absolute;\n    cursor: default;\n    :before {\n      content: \"\";\n      width: 0;\n      height: 0;\n      left: 50%;\n      position: absolute;\n      pointer-events: none;\n      border: 8px solid transparent;\n    }\n    ", ";\n  "])), function (_a) {
    var iconSize = _a.iconSize;
    return iconSize / 2 / 2 + margin;
  }, function (_a) {
    var isOpen = _a.isOpen;
    return isOpen ? "block" : "none";
  }, cloud.getShade(10), function (_a) {
    var maxWidth = _a.maxWidth;
    return maxWidth === undefined ? 212 : maxWidth;
  }, function (_a) {
    var popupOrientation = _a.popupOrientation,
        iconSize = _a.iconSize;
    return popupOrientation === InfoMessageOrientationType.Below ? css(templateObject_2$a || (templateObject_2$a = __makeTemplateObject(["\n            transform: translate(calc(-50% - ", "px), ", "px);\n            :before {\n              top: -", "px;\n              border-bottom: ", "px solid #1c1c1c;\n              border-top: none;\n            }\n          "], ["\n            transform: translate(calc(-50% - ", "px), ", "px);\n            :before {\n              top: -", "px;\n              border-bottom: ", "px solid #1c1c1c;\n              border-top: none;\n            }\n          "])), arrowSize / 2, yOffset, arrowSize, arrowSize) : css(templateObject_3$8 || (templateObject_3$8 = __makeTemplateObject(["\n            transform: translate(\n              calc(-50% - ", "px),\n              calc(-100% - ", "px - ", "px - ", "px)\n            );\n            :before {\n              bottom: -", "px;\n              border-top: ", "px solid #1c1c1c;\n              border-bottom: none;\n            }\n          "], ["\n            transform: translate(\n              calc(-50% - ", "px),\n              calc(-100% - ", "px - ", "px - ", "px)\n            );\n            :before {\n              bottom: -", "px;\n              border-top: ", "px solid #1c1c1c;\n              border-bottom: none;\n            }\n          "])), arrowSize / 2, arrowSize, iconSize, yOffset, arrowSize, arrowSize);
  })
};

var InfoMessage = function (_a) {
  var id = _a.id,
      message = _a.message,
      label = _a.label,
      maxWidth = _a.maxWidth,
      _b = _a.baseOrientation,
      baseOrientation = _b === void 0 ? InfoMessageOrientationType.Above : _b,
      _c = _a.iconAlignment,
      iconAlignment = _c === void 0 ? InfoMessageIconAlignType.Normal : _c;

  var _d = useState(false),
      isOpen = _d[0],
      setIsOpen = _d[1];

  var orientation = useState(baseOrientation)[0];
  var buttonRef = useRef(null);
  var messageRef = useRef(null);
  var baseId = useRef(id !== null && id !== void 0 ? id : v4());
  var tooltipId = "tooltip-" + baseId.current;
  useEffect(function () {
    var clickOutside = function () {
      setIsOpen(false);
    };

    if (isOpen) {
      defer(function () {
        return window.addEventListener("click", clickOutside);
      });
    } else {
      window.removeEventListener("click", clickOutside);
    }

    return function () {
      return window.removeEventListener("click", clickOutside);
    };
  }, [isOpen]);
  /*useEffect(() => {
    if (buttonRef.current && messageRef.current) {
      if (
        buttonRef.current.getBoundingClientRect().top < messageRef.current.offsetHeight &&
        orientation !== InfoOrientationType.Below
      ) {
        // setOrientation(InfoOrientationType.Below);
        // todo z-order in supernova means it can't go above as designed
        // vstui is adding a help control so replace this with that later
      }
    }
  }, [isOpen]);*/

  var iconSize = iconAlignment === InfoMessageIconAlignType.Normal ? iconNormalSize : iconSuperscriptSize;
  return /*#__PURE__*/React.createElement(Styled$d.Container, {
    iconAlignment: iconAlignment,
    iconSize: iconSize
  }, /*#__PURE__*/React.createElement("button", {
    ref: buttonRef,
    "aria-label": label,
    "aria-describedby": tooltipId,
    "aria-expanded": isOpen ? "true" : "false",
    onClick: function () {
      return setIsOpen(!isOpen);
    },
    onBlur: function () {
      return setIsOpen(false);
    },
    onKeyDown: function (event) {
      if (event.key === "Escape") {
        setIsOpen(false);
        event.stopPropagation();
      }
    }
  }, /*#__PURE__*/React.createElement(ICircleSmall, {
    color: cloud.getShade(5)
  })), /*#__PURE__*/React.createElement(Styled$d.MessageHolder, {
    id: tooltipId,
    ref: messageRef,
    isOpen: isOpen,
    maxWidth: maxWidth,
    popupOrientation: orientation,
    iconSize: iconSize
  }, isOpen && /*#__PURE__*/React.createElement("span", {
    role: "tooltip",
    "aria-live": "polite"
  }, message)));
};
var templateObject_1$d, templateObject_2$a, templateObject_3$8, templateObject_4$7;

var Styled$c = {
  Container: styled.div.withConfig({
    displayName: "MainHeader__Container"
  })(templateObject_1$c || (templateObject_1$c = __makeTemplateObject(["\n    position: relative;\n    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);\n    z-index: 1;\n  "], ["\n    position: relative;\n    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);\n    z-index: 1;\n  "]))),
  TopArea: styled.div.withConfig({
    displayName: "MainHeader__TopArea"
  })(templateObject_2$9 || (templateObject_2$9 = __makeTemplateObject(["\n    position: absolute;\n    right: 12px;\n    top: 12px;\n    display: inline-flex;\n    z-index: 1;\n    > * {\n      margin-left: 6px;\n    }\n  "], ["\n    position: absolute;\n    right: 12px;\n    top: 12px;\n    display: inline-flex;\n    z-index: 1;\n    > * {\n      margin-left: 6px;\n    }\n  "]))),
  HeadingContainer: styled.div.withConfig({
    displayName: "MainHeader__HeadingContainer"
  })(templateObject_3$7 || (templateObject_3$7 = __makeTemplateObject(["\n    padding: 16px 32px 12px 32px;\n  "], ["\n    padding: 16px 32px 12px 32px;\n  "]))),
  BrandTitle: styled.div.withConfig({
    displayName: "MainHeader__BrandTitle"
  })(templateObject_4$6 || (templateObject_4$6 = __makeTemplateObject(["\n    display: flex;\n    align-items: center;\n  "], ["\n    display: flex;\n    align-items: center;\n  "]))),
  BrandTitleText: styled.div.withConfig({
    displayName: "MainHeader__BrandTitleText"
  })(templateObject_5$6 || (templateObject_5$6 = __makeTemplateObject(["\n    color: ", ";\n    font-size: 1.6rem;\n    font-weight: 500;\n  "], ["\n    color: ", ";\n    font-size: 1.6rem;\n    font-weight: 500;\n  "])), cloud.getShade(6)),
  MainHeading: styled.h2.withConfig({
    displayName: "MainHeader__MainHeading"
  })(templateObject_6$5 || (templateObject_6$5 = __makeTemplateObject(["\n    display: inline-block;\n    font-size: 2.8rem;\n    font-weight: 400;\n    margin: 4px 0 0 0;\n    color: ", ";\n  "], ["\n    display: inline-block;\n    font-size: 2.8rem;\n    font-weight: 400;\n    margin: 4px 0 0 0;\n    color: ", ";\n  "])), cloud.getShade(10))
};

var MainHeader = function (_a) {
  var onClose = _a.onClose,
      title = _a.title,
      titleA11y = _a.titleA11y,
      baseId = _a.baseId,
      _b = _a.hasInfo,
      hasInfo = _b === void 0 ? false : _b,
      topSlot = _a.topSlot,
      noCloseBtn = _a.noCloseBtn,
      children = _a.children;
  var intl = useIntl();
  var titleRef = useRef(null);
  var titleA11yDescriptionId = "titleA11yDescription" + baseId;
  useTakeFocus(titleRef, {
    temporary: true,
    onceOnly: true
  });
  return /*#__PURE__*/React.createElement(Styled$c.Container, null, /*#__PURE__*/React.createElement(Styled$c.HeadingContainer, null, /*#__PURE__*/React.createElement(Styled$c.BrandTitle, null, /*#__PURE__*/React.createElement(Styled$c.BrandTitleText, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesBase.coachme))), hasInfo && /*#__PURE__*/React.createElement(InfoMessage, {
    id: "info-popup-" + baseId,
    maxWidth: 160,
    baseOrientation: InfoMessageOrientationType.Below,
    iconAlignment: InfoMessageIconAlignType.Superscript,
    message: intl.formatMessage(messagesBase.taglinePoweredBy),
    label: intl.formatMessage(messagesBase.moreInfo)
  })), /*#__PURE__*/React.createElement(Styled$c.MainHeading, {
    ref: titleRef,
    "aria-describedby": titleA11y ? titleA11yDescriptionId : undefined
  }, title), titleA11y && /*#__PURE__*/React.createElement(A11y, {
    "aria-hidden": true,
    id: titleA11yDescriptionId
  }, titleA11y), children, /*#__PURE__*/React.createElement(Styled$c.TopArea, null, topSlot, !noCloseBtn && /*#__PURE__*/React.createElement(CloseBtn, {
    onClick: onClose
  }))));
};
var templateObject_1$c, templateObject_2$9, templateObject_3$7, templateObject_4$6, templateObject_5$6, templateObject_6$5;

var Styled$b = {
  ProgressButton: styled(Button).withConfig({
    displayName: "CoachmeAssessmentHeader__ProgressButton"
  })(templateObject_1$b || (templateObject_1$b = __makeTemplateObject(["\n    padding: 4px;\n    min-height: auto;\n    margin-top: -1px;\n  "], ["\n    padding: 4px;\n    min-height: auto;\n    margin-top: -1px;\n  "]))),
  HeaderTitle: styled.div.withConfig({
    displayName: "CoachmeAssessmentHeader__HeaderTitle"
  })(templateObject_2$8 || (templateObject_2$8 = __makeTemplateObject(["\n    display: flex;\n    align-items: center;\n  "], ["\n    display: flex;\n    align-items: center;\n  "]))),
  HeaderSubTitle: styled.div.withConfig({
    displayName: "CoachmeAssessmentHeader__HeaderSubTitle"
  })(templateObject_3$6 || (templateObject_3$6 = __makeTemplateObject(["\n    margin-top: 8px;\n    margin-left: 48px;\n  "], ["\n    margin-top: 8px;\n    margin-left: 48px;\n  "]))),
  TitleText: styled.h2.withConfig({
    displayName: "CoachmeAssessmentHeader__TitleText"
  })(templateObject_4$5 || (templateObject_4$5 = __makeTemplateObject(["\n    margin: 0;\n    font-size: 2.8rem;\n    font-weight: 400;\n  "], ["\n    margin: 0;\n    font-size: 2.8rem;\n    font-weight: 400;\n  "]))),
  Content: styled.div.withConfig({
    displayName: "CoachmeAssessmentHeader__Content"
  })(templateObject_5$5 || (templateObject_5$5 = __makeTemplateObject(["\n    flex-grow: 1;\n    min-height: 0;\n    height: 100%;\n  "], ["\n    flex-grow: 1;\n    min-height: 0;\n    height: 100%;\n  "]))),
  PillHolder: styled.div.withConfig({
    displayName: "CoachmeAssessmentHeader__PillHolder"
  })(templateObject_6$4 || (templateObject_6$4 = __makeTemplateObject(["\n    margin: 8px 0;\n  "], ["\n    margin: 8px 0;\n  "]))),
  PillText: styled.span.withConfig({
    displayName: "CoachmeAssessmentHeader__PillText"
  })(templateObject_7$2 || (templateObject_7$2 = __makeTemplateObject(["\n    font-size: 1.2rem;\n    font-weight: ", ";\n    color: ", ";\n    border: 1px solid ", ";\n    border-radius: 10px;\n    padding: 4px 8px;\n  "], ["\n    font-size: 1.2rem;\n    font-weight: ", ";\n    color: ", ";\n    border: 1px solid ", ";\n    border-radius: 10px;\n    padding: 4px 8px;\n  "])), function (_a) {
    var submitted = _a.submitted;
    return submitted ? 700 : 500;
  }, function (_a) {
    var submitted = _a.submitted;
    return submitted ? primaryShade(6) : cloud.getShade(6);
  }, cloud.getShade(4))
};

var CoachmeAssessmentHeader = function (_a) {
  var type = _a.type,
      noCloseBtn = _a.noCloseBtn,
      noProgressBtn = _a.noProgressBtn;
  var intl = useIntl();

  var _b = useQuestionGroupContext(),
      groupId = _b.groupId,
      bookLocation = _b.bookLocation,
      itemIndex = _b.itemIndex,
      currentItemStatus = _b.currentItemStatus,
      questions = _b.questions,
      submitted = _b.submitted,
      scorePercent = _b.scorePercent,
      result = _b.result,
      hostApi = _b.hostApi;

  var isFormative = type === CoachmeAssessmentType.Formative;
  var isSummative = !isFormative;
  var title = isFormative ? messagesAssessment.formativeTitle : messagesAssessment.summativeTitle;
  var titleA11yMsg = isFormative ? messagesAssessment.formativeA11yTitle : messagesAssessment.summativeA11yTitle;
  var status = isSummative ? submitted && result ? QuestionStatus.Complete : QuestionStatus.Incomplete : currentItemStatus;
  var titleA11y = intl.formatMessage(titleA11yMsg, {
    count: questions.length,
    index: itemIndex + 1,
    status: intl.formatMessage(messagesQuestion.status, {
      status: status
    }),
    score: status === QuestionStatus.Complete ? intl.formatMessage(messagesAssessment.scoreCorrectPercent, {
      score: scorePercent
    }) : ""
  });
  var summativeExtraInfo = "";

  if (isSummative && submitted && result) {
    summativeExtraInfo = intl.formatMessage(messagesAssessment.scoreCorrectPercent, {
      score: scorePercent
    });
  } else if (isSummative && !submitted) {
    summativeExtraInfo = intl.formatMessage(messagesAssessment.questionCount, {
      count: questions.length
    });
  }

  return /*#__PURE__*/React.createElement(MainHeader, {
    title: intl.formatMessage(title, {
      count: questions.length
    }),
    titleA11y: titleA11y,
    onClose: function () {
      var _a;

      return (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onRequestClose) === null || _a === void 0 ? void 0 : _a.call(hostApi);
    },
    baseId: groupId,
    hasInfo: true,
    noCloseBtn: noCloseBtn,
    topSlot: isFormative && !noProgressBtn && /*#__PURE__*/React.createElement(Styled$b.ProgressButton, {
      naked: true,
      size: "small",
      onClick: function () {
        var _a;

        return (_a = hostApi === null || hostApi === void 0 ? void 0 : hostApi.onShowProgress) === null || _a === void 0 ? void 0 : _a.call(hostApi, groupId, bookLocation);
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesProgress.title)))
  }, summativeExtraInfo && /*#__PURE__*/React.createElement(Styled$b.PillHolder, null, /*#__PURE__*/React.createElement(Styled$b.PillText, {
    submitted: submitted
  }, summativeExtraInfo)));
};
var templateObject_1$b, templateObject_2$8, templateObject_3$6, templateObject_4$5, templateObject_5$5, templateObject_6$4, templateObject_7$2;

var Styled$a = {
  Container: styled.div.withConfig({
    displayName: "CoachmeAssessmentContent__Container"
  })(templateObject_1$a || (templateObject_1$a = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    height: 100%;\n  "], ["\n    display: flex;\n    flex-direction: column;\n    height: 100%;\n  "]))),
  Header: styled.div.withConfig({
    displayName: "CoachmeAssessmentContent__Header"
  })(templateObject_2$7 || (templateObject_2$7 = __makeTemplateObject(["\n    padding: 24px;\n    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);\n    z-index: 1;\n  "], ["\n    padding: 24px;\n    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);\n    z-index: 1;\n  "]))),
  HeaderTitle: styled.div.withConfig({
    displayName: "CoachmeAssessmentContent__HeaderTitle"
  })(templateObject_3$5 || (templateObject_3$5 = __makeTemplateObject(["\n    display: flex;\n    align-items: center;\n  "], ["\n    display: flex;\n    align-items: center;\n  "]))),
  HeaderSubTitle: styled.div.withConfig({
    displayName: "CoachmeAssessmentContent__HeaderSubTitle"
  })(templateObject_4$4 || (templateObject_4$4 = __makeTemplateObject(["\n    margin-top: 8px;\n    margin-left: 48px;\n  "], ["\n    margin-top: 8px;\n    margin-left: 48px;\n  "]))),
  TitleText: styled.h2.withConfig({
    displayName: "CoachmeAssessmentContent__TitleText"
  })(templateObject_5$4 || (templateObject_5$4 = __makeTemplateObject(["\n    margin: 0 0 0 16px;\n    font-size: 2.8rem;\n    font-weight: 400;\n  "], ["\n    margin: 0 0 0 16px;\n    font-size: 2.8rem;\n    font-weight: 400;\n  "]))),
  Content: styled.div.withConfig({
    displayName: "CoachmeAssessmentContent__Content"
  })(templateObject_6$3 || (templateObject_6$3 = __makeTemplateObject(["\n    flex-grow: 1;\n    min-height: 0;\n    z-index: 0;\n    height: 100%;\n  "], ["\n    flex-grow: 1;\n    min-height: 0;\n    z-index: 0;\n    height: 100%;\n  "])))
};

var CoachmeAssessmentContent = function (_a) {
  var type = _a.type,
      noCloseBtn = _a.noCloseBtn,
      noProgressBtn = _a.noProgressBtn;
  var intl = useIntl();

  var _b = useQuestionGroupContext(),
      groupId = _b.groupId,
      submitted = _b.submitted,
      scorePercent = _b.scorePercent,
      totalCorrect = _b.totalCorrect,
      totalIncorrect = _b.totalIncorrect,
      totalIncomplete = _b.totalIncomplete,
      result = _b.result,
      sendAriaLiveMessage = _b.sendAriaLiveMessage;

  var isFormative = type === CoachmeAssessmentType.Formative;
  var isSummative = !isFormative;
  var lastResult = usePrevious(result);

  var _c = useState(false),
      focusResults = _c[0],
      setFocusResults = _c[1];

  useEffect(function () {
    // don't focus results when loading from state, only when user clicks check
    if (!lastResult && result && isSummative) {
      setFocusResults(true); // tell screen reader user where focus has been moved to

      sendAriaLiveMessage(intl.formatMessage(messagesAssessment.resultsFocusA11y));
    }
  }, [intl, isSummative, lastResult, result, sendAriaLiveMessage, submitted]);
  return /*#__PURE__*/React.createElement(Styled$a.Container, null, /*#__PURE__*/React.createElement(Styled$a.Content, null, /*#__PURE__*/React.createElement(QuestionGroup, {
    mode: isFormative ? QuestionGroupMode.Autonomous : QuestionGroupMode.Amalgamated,
    layout: isFormative ? QuestionGroupLayout.OneAtAtime : QuestionGroupLayout.VerticalAllTogether,
    header: /*#__PURE__*/React.createElement(CoachmeAssessmentHeader, {
      type: type,
      noCloseBtn: noCloseBtn,
      noProgressBtn: noProgressBtn
    }),
    prologue: isSummative && submitted && result && /*#__PURE__*/React.createElement(CoachmeAssessmentResults, {
      id: "summative-results-" + groupId,
      focusResults: focusResults,
      scorePercent: scorePercent,
      totalCorrect: totalCorrect,
      totalIncorrect: totalIncorrect,
      totalIncomplete: totalIncomplete
    })
  })));
};
var templateObject_1$a, templateObject_2$7, templateObject_3$5, templateObject_4$4, templateObject_5$4, templateObject_6$3;

var isVaultData = function (data) {
  return data.type === "assessments";
};

var normalizeQuestionData = function (data) {
  var _a;

  if (!data) {
    return [];
  }

  if (isVaultData(data)) {
    // json api style
    return (_a = data.relationships) === null || _a === void 0 ? void 0 : _a.contents.data.filter(function (item) {
      return item.attributes.content_type === "Question" || item.attributes.content_type === "Tutorial";
    }).map(function (item) {
      var attributes = item.attributes;
      return __assign({}, attributes.data);
    });
  } // presume 'native' question format, checkpoint assessments further validate 'native' data


  return data;
};

var CoachmeAssessmentType;

(function (CoachmeAssessmentType) {
  CoachmeAssessmentType["Formative"] = "Formative";
  CoachmeAssessmentType["Summative"] = "Summative";
})(CoachmeAssessmentType || (CoachmeAssessmentType = {}));

var CoachmeAssessmentErrorType;

(function (CoachmeAssessmentErrorType) {
  CoachmeAssessmentErrorType["None"] = "None";
  CoachmeAssessmentErrorType["SubmitAssessmentFailure"] = "SubmitAssessmentFailure";
})(CoachmeAssessmentErrorType || (CoachmeAssessmentErrorType = {}));

var CoachmeAssessment = function (_a) {
  var groupId = _a.groupId,
      type = _a.type,
      questionData = _a.questionData,
      questionIndex = _a.questionIndex,
      evaluator = _a.evaluator,
      evaluatedData = _a.evaluatedData,
      onInit = _a.onInit,
      onRequestClose = _a.onRequestClose,
      onShowProgress = _a.onShowProgress,
      onRevealAnswer = _a.onRevealAnswer,
      onRetryQuestion = _a.onRetryQuestion,
      createFlashcard = _a.createFlashcard,
      getFlashcardDecks = _a.getFlashcardDecks,
      reviewFlashcards = _a.reviewFlashcards,
      noCloseBtn = _a.noCloseBtn,
      noProgressBtn = _a.noProgressBtn,
      bookLocation = _a.bookLocation; // coachme components use 'native' question data types
  // normalize data into that format if it isn't already

  var questions = useMemo(function () {
    return normalizeQuestionData(questionData);
  }, [questionData]);
  useEffect(function () {
    // when this component renders, all its upfront assets have already been loaded
    // and it renders immediately, inform host if they are interested
    onInit === null || onInit === void 0 ? void 0 : onInit();
  }, [evaluatedData, onInit]);
  return /*#__PURE__*/React.createElement(QuestionGroupContextProvider, {
    groupId: groupId,
    bookLocation: bookLocation,
    questions: questions,
    questionIndex: questionIndex,
    evaluator: evaluator,
    evaluatedData: evaluatedData,
    hostApi: {
      onRequestClose: onRequestClose,
      onShowProgress: onShowProgress,
      onRevealAnswer: onRevealAnswer,
      onRetryQuestion: onRetryQuestion,
      getFlashcardDecks: getFlashcardDecks,
      createFlashcard: createFlashcard,
      reviewFlashcards: reviewFlashcards
    }
  }, /*#__PURE__*/React.createElement(CoachmeAssessmentContent, {
    type: type,
    noCloseBtn: noCloseBtn,
    noProgressBtn: noProgressBtn
  }));
};

var Styled$9 = {
  ErrorContainer: styled.div.withConfig({
    displayName: "InlineError__ErrorContainer"
  })(templateObject_1$9 || (templateObject_1$9 = __makeTemplateObject(["\n    display: flex;\n    width: 100%;\n    justify-content: center;\n    align-items: ", ";\n    height: 100%;\n  "], ["\n    display: flex;\n    width: 100%;\n    justify-content: center;\n    align-items: ", ";\n    height: 100%;\n  "])), function (_a) {
    var vertCenter = _a.vertCenter;
    return vertCenter ? "center" : "flex-start";
  }),
  ErrorIconWrapper: styled.div.withConfig({
    displayName: "InlineError__ErrorIconWrapper"
  })(templateObject_2$6 || (templateObject_2$6 = __makeTemplateObject(["\n    margin-bottom: 12px;\n    display: flex;\n    justify-content: center;\n  "], ["\n    margin-bottom: 12px;\n    display: flex;\n    justify-content: center;\n  "]))),
  ErrorContent: styled.div.withConfig({
    displayName: "InlineError__ErrorContent"
  })(templateObject_4$3 || (templateObject_4$3 = __makeTemplateObject(["\n    padding: 16px;\n    display: flex;\n    flex-direction: column;\n    line-height: 1.5;\n    justify-content: center;\n    width: 75%;\n    ", "\n  "], ["\n    padding: 16px;\n    display: flex;\n    flex-direction: column;\n    line-height: 1.5;\n    justify-content: center;\n    width: 75%;\n    ", "\n  "])), function (_a) {
    var vertCenter = _a.vertCenter;
    return !vertCenter && css(templateObject_3$4 || (templateObject_3$4 = __makeTemplateObject(["\n        margin-top: 72px;\n      "], ["\n        margin-top: 72px;\n      "])));
  }),
  ErrorTitle: styled.div.withConfig({
    displayName: "InlineError__ErrorTitle"
  })(templateObject_5$3 || (templateObject_5$3 = __makeTemplateObject(["\n    margin-bottom: 0;\n    font-weight: 700;\n    text-align: center;\n  "], ["\n    margin-bottom: 0;\n    font-weight: 700;\n    text-align: center;\n  "]))),
  ErrorMessage: styled.div.withConfig({
    displayName: "InlineError__ErrorMessage"
  })(templateObject_6$2 || (templateObject_6$2 = __makeTemplateObject(["\n    margin: 12px 0 0 0;\n    font-weight: 400;\n    text-align: center;\n  "], ["\n    margin: 12px 0 0 0;\n    font-weight: 400;\n    text-align: center;\n  "])))
};

var InlineError = function (_a) {
  var error = _a.error,
      _b = _a.vertCenter,
      vertCenter = _b === void 0 ? true : _b;
  return /*#__PURE__*/React.createElement(Styled$9.ErrorContainer, {
    vertCenter: vertCenter
  }, /*#__PURE__*/React.createElement(Styled$9.ErrorContent, {
    vertCenter: vertCenter
  }, /*#__PURE__*/React.createElement(Styled$9.ErrorIconWrapper, null, /*#__PURE__*/React.createElement(ExclamationCircle, {
    color: red.getShade(6)
  })), /*#__PURE__*/React.createElement(Styled$9.ErrorTitle, null, error.title), /*#__PURE__*/React.createElement(Styled$9.ErrorMessage, null, error.description)));
};
var templateObject_1$9, templateObject_2$6, templateObject_3$4, templateObject_4$3, templateObject_5$3, templateObject_6$2;

var BookEnrichment = function (_a) {
  var _b, _c, _d, _e;

  var id = _a.id,
      enrichment = _a.enrichment,
      evaluator = _a.evaluator,
      evaluatedData = _a.evaluatedData,
      errorData = _a.errorData,
      noCloseBtn = _a.noCloseBtn,
      props = __rest(_a, ["id", "enrichment", "evaluator", "evaluatedData", "errorData", "noCloseBtn"]);

  var intl = useIntl();

  var _f = useState(errorData),
      error = _f[0],
      setError = _f[1];

  useEffect(function () {
    setError(errorData);
  }, [errorData]);
  var errorNotification = useMemo(function () {
    if (error && error.type != EnrichmentErrorType.None) {
      return {
        title: intl.formatMessage(messagesErrors.enrichmentLoadErrorTitle),
        description: intl.formatMessage(messagesErrors.enrichmentLoadErrorDescription)
      };
    }

    return;
  }, [intl, error]);

  if (errorNotification) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InlineError, {
      error: errorNotification
    }), !noCloseBtn && /*#__PURE__*/React.createElement(CloseBtn, {
      onClick: function () {
        var _a;

        return (_a = props.onRequestClose) === null || _a === void 0 ? void 0 : _a.call(props);
      }
    }));
  } else if (enrichment && enrichment.id === id && evaluatedData) {
    var assessment = (_b = enrichment.relationships) === null || _b === void 0 ? void 0 : _b.content.data[0];

    if (enrichment.type != "book_enrichments" || ((_c = enrichment.relationships) === null || _c === void 0 ? void 0 : _c.content.data[0].type) != "assessments") {
      setError({
        type: EnrichmentErrorType.UnknownEnrichmentType
      });
    }

    var bookLocation = (_e = (_d = enrichment.relationships) === null || _d === void 0 ? void 0 : _d.content.data[0].attributes.data) === null || _e === void 0 ? void 0 : _e.book_location;
    return /*#__PURE__*/React.createElement(CoachmeAssessment, __assign({
      type: assessment.attributes.assessment_type === AssessmentType.FORMATIVE ? CoachmeAssessmentType.Formative : CoachmeAssessmentType.Summative,
      groupId: enrichment.id,
      questionData: assessment,
      evaluator: evaluator,
      evaluatedData: evaluatedData,
      noCloseBtn: noCloseBtn,
      bookLocation: bookLocation
    }, props));
  } else {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CenteredLoader, {
      vertCenter: true
    }), !noCloseBtn && /*#__PURE__*/React.createElement(CloseBtn, {
      onClick: function () {
        var _a;

        return (_a = props.onRequestClose) === null || _a === void 0 ? void 0 : _a.call(props);
      }
    }));
  }
};

var en = {
	"coachme.base.ok": "OK",
	"coachme.base.cancel": "Cancel",
	"coachme.base.continue": "Continue",
	"coachme.base.submit": "Submit",
	"coachme.base.loading": "Loading",
	"coachme.base.close": "Close",
	"coachme.base.back": "Back",
	"coachme.base.moreInfo": "More information",
	"coachme.base.taglinePoweredBy": "Powered by VitalSource’s artificial intelligence technology.",
	"coachme.base.evaluating": "Evaluating - standby",
	"coachme.base.questions": "Questions",
	"coachme.base.coachme": "CoachMe",
	"coachme.question.status": "{status, select, Incomplete {Incomplete} Complete {Complete} Correct {Correct} Incorrect {Incorrect}}",
	"coachme.question.pip": "question {index} status {status}",
	"coachme.question.type.multipleChoice": "Multiple Choice Question",
	"coachme.question.type.gapFill": "Cloze Question",
	"coachme.question.type.submitCompare": "Submit Compare Question",
	"coachme.question.notification.attention": "Attention.",
	"coachme.question.feedback.answerCorrect": "Your answer is correct.",
	"coachme.question.feedback.answerIncorrect": "Your answer is incorrect.",
	"coachme.question.feedback.submissionThanks": "Thank you for your submission.",
	"coachme.question.feedback.wrongAnswersSpecific": "You have {wrongCount} answers that are incorrect.",
	"coachme.question.feedback.wrongAnswersGeneric": "One or more answers is incorrect.",
	"coachme.question.feedback.wrongAnswersPrompt": "See question and answers above to see which ones you got wrong.",
	"coachme.question.feedback.wrongAnswersPromptNotIndicated": "Information about which blanks are incorrect is not available in order to avoid revealing the correct answers before you've had the opportunity to try again.",
	"coachme.question.feedback.itemCorrectAnswer": "Correct answer.",
	"coachme.question.feedback.itemIncorrectAnswer": "Incorrect answer.",
	"coachme.question.feedback.itemRevealedCorrectAnswer": "Revealed as the correct answer.",
	"coachme.question.feedback.correctAnswerIs": "The correct answer is {correctAnswer}",
	"coachme.question.feedback.revealAnnouncement": "Question solution has been revealed. You have moved back to the top of question area. Navigate the question area to find the answers.",
	"coachme.question.feedback.resetAnnouncement": "Question has reset. You have moved back to the top of the question area.",
	"coachme.question.feedback.tightropeResponse": "{feedback}",
	"coachme.question.controls.checkAnswer": "Check Answer",
	"coachme.question.controls.revealAnswer": "Reveal Answer",
	"coachme.question.controls.submit": "Submit",
	"coachme.question.controls.retry": "Retry",
	"coachme.question.controls.nextQuestion": "Next Question",
	"coachme.question.controls.done": "Done",
	"coachme.question.controls.typedPlaceHolder": "Type your answer...",
	"coachme.question.controls.selectPlaceHolder": "- Select -",
	"coachme.question.validation.pleaseSelectAnswer": "Please select an answer.",
	"coachme.question.validation.typeAnAnswer": "Please type an answer.",
	"coachme.question.status.incomplete": "Incomplete",
	"coachme.question.status.complete": "Complete",
	"coachme.question.status.incorrect": "Incorrect",
	"coachme.question.status.correct": "Correct",
	"coachme.question.suggestions.prompt": "You may have misspelled your answer. {count, plural, one {Does this one look right} other {Do any of these look right}}?",
	"coachme.question.suggestions.close": "Close Suggestions",
	"coachme.submitCompare.inputFieldLabel": "Your answer.",
	"coachme.submitCompare.answerSaved": "Your answer has been saved.",
	"coachme.submitCompare.compareWithExpert": "Compare your response to an expert’s answer:",
	"coachme.submitCompare.selfAssessmentAnnouncement": "A self assessment panel has appeared beneath the expert's answer.",
	"coachme.gapFill.gapNumberLabel": "{blankIndex, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} blank.",
	"coachme.gapFill.fillInTheBlankPrompt": "Fill in the {blankIndex, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} blank.",
	"coachme.gapFill.validation.notFilledIn": "Please select an answer for each blank.",
	"coachme.gapFill.validation.sameAnswerRepeated": "Each answer can only be used once.",
	"coachme.gapFill.wordBank.headerLabel": "Word bank. Each word in the bank can only be used once.",
	"coachme.gapFill.wordBank.wordSpent": "This word has been used.",
	"coachme.gapFill.wordBank.footerLabel": "End of word bank.",
	"coachme.widgets.addToFlashcard": "Add to Flashcard",
	"coachme.widgets.createANote": "Create a Note",
	"coachme.widgets.noteCreated": "A note has been created.",
	"coachme.widgets.createAFlashcard": "Create a Flashcard",
	"coachme.widgets.reviewFlashcard": "Review Flashcard",
	"coachme.widgets.flashcardCreated": "Flashcard created. Go back and review.",
	"coachme.questionGroup.previousQuestion": "Previous question",
	"coachme.questionGroup.nextQuestion": "Next question",
	"coachme.questionGroup.assessmentStart": "Start of assessment area",
	"coachme.questionGroup.assessmentResetA11y": "The assessment has been reset",
	"coachme.questionGroup.assessmentAnswersRevealedA11y": "Answers have been revealed see questions below for details",
	"coachme.questionGroup.checkAllAnswers": "Check All Answers",
	"coachme.questionGroup.retryQuestions": "Retry Questions",
	"coachme.questionGroup.revealAnswers": "Reveal Answers",
	"coachme.assessment.formativeTitle": "Practice {count, plural, one {Question} other {Questions}}",
	"coachme.assessment.formativeA11yTitle": "{count, plural, one {, status {status}} other {, current question {index}, status {status}}}, {count, plural, other {contains # questions}}",
	"coachme.assessment.summativeTitle": "Knowledge Check",
	"coachme.assessment.summativeA11yTitle": "status {status}, {score}, contains {count, plural, one {one question} other {# questions}}",
	"coachme.assessment.questionCount": "{count, plural, one {# Question} other {# Questions}}",
	"coachme.assessment.scoreCorrectPercent": "{score}% Correct",
	"coachme.assessment.scoreCorrectCount": "{count} Correct",
	"coachme.assessment.scoreIncorrectCount": "{count} Incorrect",
	"coachme.assessment.scoreIncompleteCount": "{count} Incomplete",
	"coachme.assessment.confirmIncompleteDialog.title": "You have {count} incomplete {count, plural, one {question} other {questions}}",
	"coachme.assessment.confirmIncompleteDialog.message": "All incomplete questions will be marked as ‘Incorrect’. Are you sure you want to continue?",
	"coachme.assessment.resultsTitle": "Your results",
	"coachme.assessment.resultsA11yLineItem": "{count, plural, one {# question} other {# questions}} {status}",
	"coachme.assessment.resultsA11y": "Score {score}%, {breakdown}. See {count, plural, one {question} other {questions}} below for more details.",
	"coachme.assessment.resultsFocusA11y": "A results panel has appeared above the question content.",
	"coachme.questionImprovement.prompt": "Was this question helpful?",
	"coachme.questionImprovement.thumbsUp": "Yes",
	"coachme.questionImprovement.thumbsDown": "No",
	"coachme.questionImprovement.dialog.title": "Tell Us Why",
	"coachme.questionImprovement.dialog.information": "Bookshelf CoachMe practice questions are powered by VitalSource’s artificial intelligence technology. Your feedback will help us continue to improve the learner experience.",
	"coachme.questionImprovement.dialog.issue.wrongAnswer": "I think the correct answer is wrong",
	"coachme.questionImprovement.dialog.issue.mineAlsoCorrect": "I think my answer is also correct",
	"coachme.questionImprovement.dialog.issue.questionUnclear": "The question is unclear to me",
	"coachme.questionImprovement.dialog.issue.questionNotRelevant": "I don't think the question is relevant to the material",
	"coachme.questionImprovement.dialog.otherFeedback": "Other/Additional Feedback",
	"coachme.questionImprovement.dialog.feedbackPlaceholder": "Leave a comment",
	"coachme.errors.evaluateErrorTitle": "An error occurred",
	"coachme.errors.evaluateErrorDescription": "We are unable to submit your answer. Try again in a few minutes.",
	"coachme.errors.revealAnswerErrorTitle": "An error occurred",
	"coachme.errors.revealAnswerErrorDescription": "We are unable to reveal your answer. Try again in a few minutes.",
	"coachme.errors.submitAssessmentErrorTitle": "An error occurred",
	"coachme.errors.submitAssessmentErrorDescription": "We are unable to submit your assessment for evaluation. Try again in a few minutes.",
	"coachme.errors.improvementFeedbackErrorTitle": "An error occurred",
	"coachme.errors.improvementFeedbackErrorDescription": "We are unable to register your feedback. Try again in a few minutes.",
	"coachme.errors.enrichmentLoadErrorTitle": "An error occurred loading questions.",
	"coachme.errors.enrichmentLoadErrorDescription": "We are working on getting it back up. Sorry for the inconvenience.",
	"coachme.errors.progressLoadErrorTitle": "An error occurred loading question progress.",
	"coachme.errors.progressLoadErrorDescription": "We are working on getting it back up. Sorry for the inconvenience.",
	"coachme.progress.title": "Question Progress",
	"coachme.progress.countComplete": "{completionCount} complete",
	"coachme.progress.countIncomplete": "{countIncomplete} incomplete",
	"coachme.progress.countBreakdownA11y": "{completionCount, plural, one {# question} other {# questions}} complete, {countIncomplete, plural, one {# question} other {# questions}} incomplete",
	"coachme.progress.indicatorA11y": "{percentComplete}% or {completionCount} out of {completionTotal, plural, one {# question} other {# questions}} complete",
	"coachme.progress.questionStatusA11y": "Question {status}",
	"coachme.progress.detailListItemA11y": "Activity on page {page} contains {questionCount, plural, one {# question} other {# questions}}",
	"coachme.progress.backToAll": "Back to All Chapters"
};

var en$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    'default': en
});

var supported = [
	[
		"es-MX",
		"es-MX"
	],
	[
		"pt-BR",
		"pt-BR"
	]
];
var locales = {
	supported: supported
};

function __variableDynamicImportRuntime0__(path) {
  switch (path) {
    case '../../locale/en.json': return Promise.resolve().then(function () { return en$1; });
    case '../../locale/es-MX.json': return import('./locale/es-MX-c3b11e1d.js');
    case '../../locale/pt-BR.json': return import('./locale/pt-BR-79a894d7.js');
    default: return new Promise(function(resolve, reject) {
      (typeof queueMicrotask === 'function' ? queueMicrotask : setTimeout)(
        reject.bind(null, new Error("Unknown variable dynamic import: " + path))
      );
    })
   }
 }

var isSupportedLocale = function (locale) {
  return !!locales.supported.find(function (_a) {
    var fullLocale = _a[0];
    return fullLocale === locale;
  });
};

var LocaleProvider = function (_a) {
  var locale = _a.locale,
      children = _a.children;
  var localeToUse = locale || navigator.language;

  var _b = useState(),
      messages = _b[0],
      setMessages = _b[1];

  var updateMessages = useCallback(function (messages) {
    var fixedMessages = __assign({}, messages); // remove translation metadata


    delete fixedMessages.smartling;
    setMessages(fixedMessages);
  }, []);
  var initEnglish = useCallback(function () {
    updateMessages(en);
  }, [updateMessages]);
  useEffect(function () {
    if (isSupportedLocale(localeToUse)) {
      __variableDynamicImportRuntime0__("../../locale/" + localeToUse + ".json").then(function (response) {
        updateMessages(__assign(__assign({}, en), response.default));
      }).catch(function () {
        // fallback
        initEnglish();
      });
    } else {
      initEnglish();
    }
  }, [updateMessages, initEnglish, localeToUse]);

  if (!messages) {
    return null;
  }

  return /*#__PURE__*/React.createElement(IntlProvider, {
    locale: localeToUse,
    messages: messages
  }, children);
};

var defaultTheme = createTheme({
  color: {
    primary: ocean
  },
  typography: {
    htmlFontSize: 10 // for 0.1 rem = 1px

  }
}); // currently the theme of the host cannot be used as there are color
// clashes with green and red themes and correct/ incorrect colors
// so a local theme is used instead, so purposely the host does not pass a theme

var DefaultTheme = function (_a) {
  var theme = _a.theme,
      children = _a.children;

  if (!theme) {
    // parent has not supplied a theme so fallback to default
    return /*#__PURE__*/React.createElement(ThemeProvider, {
      theme: defaultTheme
    }, children);
  } else {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
};

var EnrichmentComponent = function (_a) {
  var enrichmentId = _a.enrichmentId,
      theme = _a.theme,
      locale = _a.locale,
      props = __rest(_a, ["enrichmentId", "theme", "locale"]);

  var _b = useEnrichment(enrichmentId),
      data = _b.data,
      restoredData = _b.restoredData,
      error = _b.error;

  var evaluatorService = AssessmentInstance;
  var enrichmentEvaluatorService = evaluatorService.getEnrichmentService(enrichmentId);

  var renderEnrichment = function () {
    return /*#__PURE__*/React.createElement(BookEnrichment, __assign({
      id: enrichmentId,
      enrichment: data,
      evaluator: enrichmentEvaluatorService,
      evaluatedData: restoredData,
      errorData: error
    }, props));
  };

  return /*#__PURE__*/React.createElement(LocaleProvider, {
    locale: locale
  }, /*#__PURE__*/React.createElement(DefaultTheme, {
    theme: theme
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      height: "100%"
    }
  }, renderEnrichment())));
};

var ProgressPanelHeader = function (props) {
  var intl = useIntl();
  return /*#__PURE__*/React.createElement(MainHeader, __assign({}, props, {
    title: intl.formatMessage(messagesProgress.title)
  }));
};

var ProgressIndicatorSize;

(function (ProgressIndicatorSize) {
  ProgressIndicatorSize[ProgressIndicatorSize["Normal"] = 0] = "Normal";
  ProgressIndicatorSize[ProgressIndicatorSize["Large"] = 1] = "Large";
})(ProgressIndicatorSize || (ProgressIndicatorSize = {}));

var percentCaptionClass = "percentCaption";
var totalCaptionClass = "totalCaption";

var strokeFrames = function (pos) {
  return keyframes(templateObject_1$8 || (templateObject_1$8 = __makeTemplateObject(["\n  0% {\n    stroke-dashoffset: 100\n  }\n  100% {\n    stroke-dashoffset: ", "\n  }\n"], ["\n  0% {\n    stroke-dashoffset: 100\n  }\n  100% {\n    stroke-dashoffset: ", "\n  }\n"])), pos);
};

var Styled$8 = {
  Container: styled.span.withConfig({
    displayName: "ProgressIndicator__Container"
  })(templateObject_3$3 || (templateObject_3$3 = __makeTemplateObject(["\n    position: relative;\n    display: inline-flex;\n    justify-content: center;\n    align-items: center;\n    ", ";\n    & .", " {\n      display: none;\n    }\n    &:hover {\n      .", " {\n        display: block;\n      }\n      .", " {\n        display: none;\n      }\n    }\n  "], ["\n    position: relative;\n    display: inline-flex;\n    justify-content: center;\n    align-items: center;\n    ", ";\n    & .", " {\n      display: none;\n    }\n    &:hover {\n      .", " {\n        display: block;\n      }\n      .", " {\n        display: none;\n      }\n    }\n  "])), function (_a) {
    var diameter = _a.diameter;
    return css(templateObject_2$5 || (templateObject_2$5 = __makeTemplateObject(["\n        width: ", "px;\n        height: ", "px;\n      "], ["\n        width: ", "px;\n        height: ", "px;\n      "])), diameter, diameter);
  }, percentCaptionClass, percentCaptionClass, totalCaptionClass),
  Caption: styled.span.withConfig({
    displayName: "ProgressIndicator__Caption"
  })(templateObject_4$2 || (templateObject_4$2 = __makeTemplateObject(["\n    display: inline-flex;\n    flex-direction: column;\n    align-items: center;\n    font-size: ", ";\n    z-index: 1;\n    user-select: none;\n  "], ["\n    display: inline-flex;\n    flex-direction: column;\n    align-items: center;\n    font-size: ", ";\n    z-index: 1;\n    user-select: none;\n  "])), function (_a) {
    var size = _a.size;
    return size === ProgressIndicatorSize.Normal ? "1.2rem" : "2.0rem";
  }),
  MainText: styled.span.withConfig({
    displayName: "ProgressIndicator__MainText"
  })(templateObject_5$2 || (templateObject_5$2 = __makeTemplateObject(["\n    font-weight: 500;\n  "], ["\n    font-weight: 500;\n  "]))),
  Annotation: styled.span.withConfig({
    displayName: "ProgressIndicator__Annotation"
  })(templateObject_6$1 || (templateObject_6$1 = __makeTemplateObject(["\n    font-size: 1.2rem;\n    color: ", ";\n    margin-top: 2px;\n  "], ["\n    font-size: 1.2rem;\n    color: ", ";\n    margin-top: 2px;\n  "])), cloud.getShade(6)),
  ProgressCircle: styled.svg.withConfig({
    displayName: "ProgressIndicator__ProgressCircle"
  })(templateObject_8$1 || (templateObject_8$1 = __makeTemplateObject(["\n    ", ";\n  "], ["\n    ", ";\n  "])), function (_a) {
    var pos = _a.pos;
    return pos !== undefined && css(templateObject_7$1 || (templateObject_7$1 = __makeTemplateObject(["\n        animation: ", " 0.6s linear forwards;\n      "], ["\n        animation: ", " 0.6s linear forwards;\n      "])), strokeFrames(pos));
  })
};

var ProgressIndicator = function (_a) {
  var _b = _a.size,
      size = _b === void 0 ? ProgressIndicatorSize.Normal : _b,
      completionCount = _a.completionCount,
      completionTotal = _a.completionTotal,
      percentComplete = _a.percentComplete,
      className = _a.className,
      _c = _a.animate,
      animate = _c === void 0 ? false : _c;
  var theme = useContext(ThemeContext);
  var baseSize = size === ProgressIndicatorSize.Normal ? 60 : 150;
  var strokeWidth = size === ProgressIndicatorSize.Normal ? 8 : 20;
  var strokeOutlineWidth = size === ProgressIndicatorSize.Normal ? 1 : 2;
  var innerSize = baseSize - strokeOutlineWidth * 2; // prevent stroke clipping

  var center = baseSize / 2;
  var innerRadius = innerSize / 2 - strokeWidth / 2;
  var innerCircumference = 2 * Math.PI * innerRadius;
  return /*#__PURE__*/React.createElement(Styled$8.Container, {
    className: className,
    diameter: baseSize
  }, /*#__PURE__*/React.createElement(A11y, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesProgress.indicatorA11y, {
    values: {
      completionCount: completionCount,
      completionTotal: completionTotal,
      percentComplete: percentComplete
    }
  }))), /*#__PURE__*/React.createElement(Styled$8.ProgressCircle, {
    pos: animate ? 100 - percentComplete : undefined,
    focusable: false,
    "aria-hidden": true,
    width: baseSize,
    height: baseSize,
    viewBox: "0 0 " + baseSize + " " + baseSize,
    style: {
      transform: "rotate(-90deg)",
      position: "absolute"
    }
  }, /*#__PURE__*/React.createElement("circle", {
    cx: center,
    cy: center,
    r: innerSize / 2,
    fill: frost.getShade(1),
    stroke: frost.getShade(5),
    strokeWidth: strokeOutlineWidth
  }), /*#__PURE__*/React.createElement("circle", {
    cx: center,
    cy: center,
    r: innerSize / 2 - strokeWidth,
    fill: "#ffffff",
    stroke: frost.getShade(5),
    strokeWidth: strokeOutlineWidth
  }), /*#__PURE__*/React.createElement("circle", {
    cx: center,
    cy: center,
    r: innerRadius,
    fill: "none",
    stroke: percentComplete > 0 ? theme.vstui.color.primary.getShade(6) : "none",
    strokeWidth: strokeWidth,
    strokeLinecap: "round",
    pathLength: innerCircumference,
    strokeDasharray: innerCircumference,
    strokeDashoffset: animate ? undefined : innerCircumference - innerCircumference * (percentComplete / 100)
  })), /*#__PURE__*/React.createElement(Styled$8.Caption, {
    size: size,
    className: percentCaptionClass,
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Styled$8.MainText, null, percentComplete + "%")), /*#__PURE__*/React.createElement(Styled$8.Caption, {
    size: size,
    className: totalCaptionClass,
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Styled$8.MainText, null, completionCount), "/", completionTotal), size === ProgressIndicatorSize.Large && /*#__PURE__*/React.createElement(Styled$8.Annotation, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesBase.questions)))));
};
var templateObject_1$8, templateObject_2$5, templateObject_3$3, templateObject_4$2, templateObject_5$2, templateObject_6$1, templateObject_7$1, templateObject_8$1;

var Styled$7 = {
  ListItem: styled.li.withConfig({
    displayName: "ProgressListItem__ListItem"
  })(templateObject_1$7 || (templateObject_1$7 = __makeTemplateObject(["\n    list-style: none;\n  "], ["\n    list-style: none;\n  "]))),
  Button: styled.button.withConfig({
    displayName: "ProgressListItem__Button"
  })(templateObject_3$2 || (templateObject_3$2 = __makeTemplateObject(["\n    background: none;\n    font-size: inherit;\n    font-family: inherit;\n    text-align: left;\n    display: flex;\n    width: 100%;\n    align-items: center;\n    padding: 8px 16px;\n    margin-bottom: 8px;\n    border: 1px solid ", ";\n    border-radius: 4px;\n    cursor: pointer;\n    &:hover {\n      box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);\n    }\n    ", ";\n    &:focus-visible {\n      outline: none;\n      border-color: ", ";\n      box-shadow: 0 0 2px 2px ", ";\n    }\n  "], ["\n    background: none;\n    font-size: inherit;\n    font-family: inherit;\n    text-align: left;\n    display: flex;\n    width: 100%;\n    align-items: center;\n    padding: 8px 16px;\n    margin-bottom: 8px;\n    border: 1px solid ", ";\n    border-radius: 4px;\n    cursor: pointer;\n    &:hover {\n      box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);\n    }\n    ", ";\n    &:focus-visible {\n      outline: none;\n      border-color: ", ";\n      box-shadow: 0 0 2px 2px ", ";\n    }\n  "])), cloud.getShade(3), function (_a) {
    var started = _a.started;
    return started && css(templateObject_2$4 || (templateObject_2$4 = __makeTemplateObject(["\n        font-weight: 500;\n      "], ["\n        font-weight: 500;\n      "])));
  }, primaryShade(6), primaryShade(6)),
  Title: styled.span.withConfig({
    displayName: "ProgressListItem__Title"
  })(templateObject_4$1 || (templateObject_4$1 = __makeTemplateObject(["\n    flex-grow: 1;\n    margin-right: 8px;\n    text-overflow: ellipsis;\n    display: -webkit-box;\n    -webkit-box-orient: vertical;\n    -webkit-line-clamp: 2;\n    overflow: hidden;\n  "], ["\n    flex-grow: 1;\n    margin-right: 8px;\n    text-overflow: ellipsis;\n    display: -webkit-box;\n    -webkit-box-orient: vertical;\n    -webkit-line-clamp: 2;\n    overflow: hidden;\n  "]))),
  ProgressContainer: styled.span.withConfig({
    displayName: "ProgressListItem__ProgressContainer"
  })(templateObject_5$1 || (templateObject_5$1 = __makeTemplateObject(["\n    flex-shrink: 0;\n  "], ["\n    flex-shrink: 0;\n  "])))
};

var ProgressListItem = function (_a) {
  var data = _a.data,
      onSelect = _a.onSelect;
  var intl = useIntl();
  var title = data.title,
      completionCount = data.completionCount,
      completionTotal = data.completionTotal,
      percentComplete = data.percentComplete,
      id = data.id;
  var a11yExtraTitle = intl.formatMessage(messagesProgress.indicatorA11y, {
    completionCount: completionCount,
    completionTotal: completionTotal,
    percentComplete: percentComplete
  });
  return /*#__PURE__*/React.createElement(Styled$7.ListItem, null, /*#__PURE__*/React.createElement(Styled$7.Button, {
    "aria-label": title + ". " + a11yExtraTitle,
    started: completionCount > 0,
    onClick: function () {
      onSelect(id);
    }
  }, /*#__PURE__*/React.createElement(Styled$7.Title, null, title), /*#__PURE__*/React.createElement(Styled$7.ProgressContainer, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(ProgressIndicator, __assign({}, data)))));
};
var templateObject_1$7, templateObject_2$4, templateObject_3$2, templateObject_4$1, templateObject_5$1;

var Styled$6 = {
  Container: styled.div.withConfig({
    displayName: "ProgressList__Container"
  })(templateObject_1$6 || (templateObject_1$6 = __makeTemplateObject(["\n    ul {\n      margin: 0;\n      padding: 0;\n    }\n  "], ["\n    ul {\n      margin: 0;\n      padding: 0;\n    }\n  "])))
};

var ProgressList = function (_a) {
  var items = _a.items,
      onSelect = _a.onSelect;
  return /*#__PURE__*/React.createElement(Styled$6.Container, null, /*#__PURE__*/React.createElement("ul", null, items.map(function (item) {
    return /*#__PURE__*/React.createElement(ProgressListItem, {
      onSelect: onSelect,
      key: item.id,
      data: item
    });
  })));
};
var templateObject_1$6;

var StatusCorrectIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "16",
    height: "16",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
    focusable: false
  }, /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "8",
    cy: "8",
    r: "7.25",
    fill: "#EDF7ED",
    stroke: "#048A04",
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.985 10.31a.647.647 0 0 1-.915 0L5.28 8.52a.647.647 0 0 1 .914-.914L7.528 8.94l2.93-2.931a.646.646 0 1 1 .915.915L7.985 10.31Z",
    fill: "#048A04"
  })));
};

var StatusIncorrectIcon = function () {
  return /*#__PURE__*/React.createElement("svg", {
    width: "16",
    height: "16",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
    focusable: false
  }, /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "8",
    cy: "8",
    r: "7.25",
    fill: "#FFF2F4",
    stroke: "#E71D3A",
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8.8 8 1.863-1.863a.565.565 0 1 0-.8-.8L8 7.2 6.137 5.338a.565.565 0 1 0-.8.8L7.2 8 5.338 9.863a.565.565 0 1 0 .8.8L8 8.8l1.863 1.863a.564.564 0 0 0 .8 0 .565.565 0 0 0 0-.8L8.8 8Z",
    fill: "#E71D3A"
  })));
};

var progressStatusPipSize = 24;
var Styled$5 = {
  MainCircle: styled.button.withConfig({
    displayName: "ProgressStatusPip__MainCircle"
  })(templateObject_2$3 || (templateObject_2$3 = __makeTemplateObject(["\n    position: relative;\n    background-color: ", ";\n    border: 2px solid\n      ", ";\n    border-radius: 100%;\n    width: ", "px;\n    height: ", "px;\n    margin-right: 12px;\n    margin-bottom: 16px;\n    box-sizing: border-box;\n    outline: none;\n    ", "\n  "], ["\n    position: relative;\n    background-color: ", ";\n    border: 2px solid\n      ", ";\n    border-radius: 100%;\n    width: ", "px;\n    height: ", "px;\n    margin-right: 12px;\n    margin-bottom: 16px;\n    box-sizing: border-box;\n    outline: none;\n    ", "\n  "])), function (_a) {
    var status = _a.status;
    return status === QuestionStatus.Incomplete ? frost.getShade(1) : primaryShade(6);
  }, function (_a) {
    var status = _a.status;
    return status === QuestionStatus.Incomplete ? frost.getShade(5) : primaryShade(6);
  }, progressStatusPipSize, progressStatusPipSize, function (_a) {
    var isStatic = _a.isStatic;
    return !isStatic && css(templateObject_1$5 || (templateObject_1$5 = __makeTemplateObject(["\n        cursor: pointer;\n        &:hover,\n        &:focus {\n          box-shadow: 0 0 0 2px white, 0 0 0 4px ", ";\n        }\n      "], ["\n        cursor: pointer;\n        &:hover,\n        &:focus {\n          box-shadow: 0 0 0 2px white, 0 0 0 4px ", ";\n        }\n      "])), primaryShade(6));
  }),
  StatusCircle: styled.div.withConfig({
    displayName: "ProgressStatusPip__StatusCircle"
  })(templateObject_3$1 || (templateObject_3$1 = __makeTemplateObject(["\n    position: absolute;\n    left: 50%;\n    top: 50%;\n    width: 16px;\n    height: 16px;\n    border-radius: 100%;\n    box-shadow: 0 0 0 2px white;\n  "], ["\n    position: absolute;\n    left: 50%;\n    top: 50%;\n    width: 16px;\n    height: 16px;\n    border-radius: 100%;\n    box-shadow: 0 0 0 2px white;\n  "])))
};

var ProgressStatusPip = function (_a) {
  var className = _a.className,
      status = _a.status,
      _b = _a.isStatic,
      isStatic = _b === void 0 ? false : _b,
      onClick = _a.onClick;
  var intl = useIntl();
  var isMarked = status === QuestionStatus.Correct || status === QuestionStatus.Incorrect;
  var pip = /*#__PURE__*/React.createElement(Styled$5.MainCircle, {
    as: isStatic ? "div" : "button",
    className: className,
    status: status,
    isStatic: isStatic,
    tabIndex: isStatic ? undefined : 0,
    onClick: function (event) {
      onClick === null || onClick === void 0 ? void 0 : onClick(); // don't want list item to also see event, been dealt with here

      event.stopPropagation();
    },
    "aria-label": intl.formatMessage(messagesProgress.questionStatusA11y, {
      status: intl.formatMessage(messagesQuestion.status, {
        status: status
      })
    })
  }, isMarked ? /*#__PURE__*/React.createElement(Styled$5.StatusCircle, null, status === QuestionStatus.Correct ? /*#__PURE__*/React.createElement(StatusCorrectIcon, null) : /*#__PURE__*/React.createElement(StatusIncorrectIcon, null)) : /*#__PURE__*/React.createElement(Styled$5.StatusCircle, {
    style: {
      opacity: 0
    }
  })); // note 0 opacity placeholder is due to issue with vstui tooltip, this keeps things level

  return isStatic ? pip : /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    title: intl.formatMessage(messagesQuestion.status, {
      status: status
    })
  }, pip);
};
var templateObject_1$5, templateObject_2$3, templateObject_3$1;

var Styled$4 = {
  Container: styled.div.withConfig({
    displayName: "ProgressStatusPips__Container"
  })(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["\n    display: flex;\n    align-items: center;\n    flex-wrap: wrap;\n  "], ["\n    display: flex;\n    align-items: center;\n    flex-wrap: wrap;\n  "])))
};

var ProgressStatusPips = function (_a) {
  var items = _a.items,
      onClick = _a.onClick;
  return /*#__PURE__*/React.createElement(Styled$4.Container, null, items.map(function (item, index) {
    var _a;

    return /*#__PURE__*/React.createElement(ProgressStatusPip, {
      key: (_a = item.id) !== null && _a !== void 0 ? _a : index,
      id: item.id,
      status: item.status,
      onClick: function () {
        return onClick(index);
      }
    });
  }));
};
var templateObject_1$4;

var Styled$3 = {
  ActivityListItem: styled.li.withConfig({
    displayName: "ProgressActivityListItem__ActivityListItem"
  })(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["\n    display: flex;\n    align-items: baseline;\n    list-style: none;\n    padding: 16px 16px 0;\n    margin-bottom: 8px;\n    border: 1px solid ", ";\n    border-radius: 4px;\n    cursor: pointer;\n    &:hover {\n      box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);\n    }\n    &:focus-visible {\n      outline: none;\n      border-color: ", ";\n      box-shadow: 0 0 2px 2px ", ";\n    }\n  "], ["\n    display: flex;\n    align-items: baseline;\n    list-style: none;\n    padding: 16px 16px 0;\n    margin-bottom: 8px;\n    border: 1px solid ", ";\n    border-radius: 4px;\n    cursor: pointer;\n    &:hover {\n      box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);\n    }\n    &:focus-visible {\n      outline: none;\n      border-color: ", ";\n      box-shadow: 0 0 2px 2px ", ";\n    }\n  "])), cloud.getShade(3), primaryShade(6), primaryShade(6)),
  Page: styled.div.withConfig({
    displayName: "ProgressActivityListItem__Page"
  })(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\n    flex-grow: 1;\n    margin-left: 32px;\n    text-align: right;\n    font-size: 1.2rem;\n    color: ", ";\n    align-self: flex-start;\n    line-height: ", "px;\n  "], ["\n    flex-grow: 1;\n    margin-left: 32px;\n    text-align: right;\n    font-size: 1.2rem;\n    color: ", ";\n    align-self: flex-start;\n    line-height: ", "px;\n  "])), cloud.getShade(7), progressStatusPipSize)
};

var ProgressActivityListItem = function (_a) {
  var questions = _a.questions,
      page = _a.page,
      onClick = _a.onClick;
  var intl = useIntl();
  var questionPips = questions.map(function (question) {
    return {
      status: question.status,
      id: question.id
    };
  });
  return /*#__PURE__*/React.createElement(Styled$3.ActivityListItem, {
    "aria-label": intl.formatMessage(messagesProgress.detailListItemA11y, {
      questionCount: questions.length,
      page: page
    }),
    onClick: function () {
      onClick(questions.findIndex(function (question) {
        return question.status === QuestionStatus.Incomplete;
      }));
    }
  }, /*#__PURE__*/React.createElement(ProgressStatusPips, {
    items: questionPips,
    onClick: function (index) {
      onClick(index);
    }
  }), /*#__PURE__*/React.createElement(Styled$3.Page, {
    "aria-hidden": true
  }, page));
};
var templateObject_1$3, templateObject_2$2;

var indicatorYGap = "12px";
var Styled$2 = {
  Container: styled.div.withConfig({
    displayName: "ProgressChapterDetailPanel__Container"
  })(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n  "], ["\n    display: flex;\n    flex-direction: column;\n  "]))),
  Title: styled.div.withConfig({
    displayName: "ProgressChapterDetailPanel__Title"
  })(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: row;\n    align-items: center;\n    background-color: ", ";\n    padding: 16px;\n  "], ["\n    display: flex;\n    flex-direction: row;\n    align-items: center;\n    background-color: ", ";\n    padding: 16px;\n  "])), frost.getShade(2)),
  TitleTxt: styled.div.withConfig({
    displayName: "ProgressChapterDetailPanel__TitleTxt"
  })(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n    margin-left: 8px;\n    font-size: 1.6rem;\n    font-weight: 500;\n    flex-grow: 1;\n  "], ["\n    margin-left: 8px;\n    font-size: 1.6rem;\n    font-weight: 500;\n    flex-grow: 1;\n  "]))),
  ProgressIndicator: styled(ProgressIndicator).withConfig({
    displayName: "ProgressChapterDetailPanel__ProgressIndicator"
  })(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    align-self: center;\n    margin-top: 20px;\n    margin-bottom: calc(", "px + ", ");\n  "], ["\n    align-self: center;\n    margin-top: 20px;\n    margin-bottom: calc(", "px + ", ");\n  "])), progressStatusPipSize, indicatorYGap),
  ProgressStatusPip: styled(ProgressStatusPip).withConfig({
    displayName: "ProgressChapterDetailPanel__ProgressStatusPip"
  })(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n    margin: 0;\n    position: absolute;\n    left: 50%;\n    transform: translate(-50%, calc(-100% - ", "));\n  "], ["\n    margin: 0;\n    position: absolute;\n    left: 50%;\n    transform: translate(-50%, calc(-100% - ", "));\n  "])), indicatorYGap),
  CompleteIncomplete: styled.div.withConfig({
    displayName: "ProgressChapterDetailPanel__CompleteIncomplete"
  })(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    display: flex;\n    margin-top: 16px;\n    justify-content: center;\n    margin-bottom: 22px;\n  "], ["\n    display: flex;\n    margin-top: 16px;\n    justify-content: center;\n    margin-bottom: 22px;\n  "]))),
  CompleteIncompleteCaption: styled.div.withConfig({
    displayName: "ProgressChapterDetailPanel__CompleteIncompleteCaption"
  })(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    position: relative;\n    font-size: 1.4rem;\n    color: ", ";\n  "], ["\n    position: relative;\n    font-size: 1.4rem;\n    color: ", ";\n  "])), cloud.getShade(10)),
  CompleteIncompleteDivider: styled.div.withConfig({
    displayName: "ProgressChapterDetailPanel__CompleteIncompleteDivider"
  })(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n    display: inline-block;\n    width: 2px;\n    margin: 0 16px;\n    background-color: ", ";\n  "], ["\n    display: inline-block;\n    width: 2px;\n    margin: 0 16px;\n    background-color: ", ";\n  "])), cloud.getShade(5)),
  ActivitiesList: styled.ul.withConfig({
    displayName: "ProgressChapterDetailPanel__ActivitiesList"
  })(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n    margin: 0 24px;\n    padding: 0;\n  "], ["\n    margin: 0 24px;\n    padding: 0;\n  "])))
};

var ProgressChapterDetailPanel = function (_a) {
  var title = _a.title,
      completionCount = _a.completionCount,
      completionTotal = _a.completionTotal,
      percentComplete = _a.percentComplete,
      activities = _a.activities,
      onBack = _a.onBack,
      onSelect = _a.onSelect;
  var intl = useIntl();
  var countIncomplete = completionTotal - completionCount;
  return /*#__PURE__*/React.createElement(Styled$2.Container, null, /*#__PURE__*/React.createElement(Styled$2.Title, null, /*#__PURE__*/React.createElement(IconButton, {
    rounded: true,
    label: intl.formatMessage(messagesProgress.backToAll),
    icon: /*#__PURE__*/React.createElement(CaretLeftLarge, {
      color: frost.getShade(10)
    }),
    onClick: function () {
      onBack();
    }
  }), /*#__PURE__*/React.createElement(Styled$2.TitleTxt, null, title)), /*#__PURE__*/React.createElement(Styled$2.ProgressIndicator, __assign({
    animate: false,
    size: ProgressIndicatorSize.Large
  }, {
    completionCount: completionCount,
    completionTotal: completionTotal,
    percentComplete: percentComplete
  })), /*#__PURE__*/React.createElement(Styled$2.CompleteIncomplete, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Styled$2.CompleteIncompleteCaption, null, /*#__PURE__*/React.createElement(Styled$2.ProgressStatusPip, {
    status: QuestionStatus.Complete,
    isStatic: true
  }), /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesProgress.countComplete, {
    values: {
      completionCount: completionCount
    }
  }))), /*#__PURE__*/React.createElement(Styled$2.CompleteIncompleteDivider, null), /*#__PURE__*/React.createElement(Styled$2.CompleteIncompleteCaption, null, /*#__PURE__*/React.createElement(Styled$2.ProgressStatusPip, {
    status: QuestionStatus.Incomplete,
    isStatic: true
  }), /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesProgress.countIncomplete, {
    values: {
      countIncomplete: countIncomplete
    }
  })))), /*#__PURE__*/React.createElement(A11y, null, /*#__PURE__*/React.createElement(FormattedMessage, __assign({}, messagesProgress.countBreakdownA11y, {
    values: {
      completionCount: completionCount,
      countIncomplete: countIncomplete
    }
  }))), /*#__PURE__*/React.createElement(Styled$2.ActivitiesList, null, activities.map(function (activity, i) {
    return /*#__PURE__*/React.createElement(ProgressActivityListItem, __assign({
      key: i
    }, activity, {
      onClick: function (index) {
        onSelect(activity.id, activity.cfi, index);
      }
    }));
  })));
};
var templateObject_1$2, templateObject_2$1, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9;

var Styled$1 = {
  Container: styled.div.withConfig({
    displayName: "ProgressPanel__Container"
  })(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n    height: 100%;\n    overflow-y: auto;\n    overflow-x: hidden;\n  "], ["\n    height: 100%;\n    overflow-y: auto;\n    overflow-x: hidden;\n  "]))),
  ListContent: styled.div.withConfig({
    displayName: "ProgressPanel__ListContent"
  })(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    padding: 16px;\n  "], ["\n    padding: 16px;\n  "])))
};

var ProgressPanel = function (_a) {
  var onClose = _a.onClose,
      onSelect = _a.onSelect,
      onDetailSelect = _a.onDetailSelect,
      onBack = _a.onBack,
      items = _a.items,
      detail = _a.detail,
      noCloseBtn = _a.noCloseBtn;
  var containerRef = useRef(null);
  return /*#__PURE__*/React.createElement(Styled$1.Container, {
    ref: containerRef
  }, /*#__PURE__*/React.createElement(ProgressPanelHeader, {
    noCloseBtn: noCloseBtn,
    onClose: onClose
  }), detail ? /*#__PURE__*/React.createElement(ProgressChapterDetailPanel, __assign({}, detail, {
    onSelect: function (activityId, cfi, index) {
      onDetailSelect(activityId, cfi, index);
    },
    onBack: function () {
      onBack();
    }
  })) : /*#__PURE__*/React.createElement(Styled$1.ListContent, null, /*#__PURE__*/React.createElement(ProgressList, {
    items: items,
    onSelect: function (id) {
      var item = items.find(function (item) {
        return item.id === id;
      });

      if (item) {
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }

        onSelect(item.detailId);
      }
    }
  })));
};
var templateObject_1$1, templateObject_2;

var Styled = {
  Container: styled.div.withConfig({
    displayName: "ProgressContent__Container"
  })(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    height: 100%;\n    display: flex;\n    flex-direction: column;\n  "], ["\n    height: 100%;\n    display: flex;\n    flex-direction: column;\n  "])))
};

var ProgressContent = function (_a) {
  var getChapterProgress = _a.getChapterProgress,
      getChapterProgressDetail = _a.getChapterProgressDetail,
      cfiToPage = _a.cfiToPage,
      onClose = _a.onClose,
      onSelectActivity = _a.onSelectActivity,
      initialized = _a.initialized,
      hasError = _a.hasError,
      bookLocation = _a.bookLocation,
      noCloseBtn = _a.noCloseBtn;
  var intl = useIntl();

  var _b = useState(),
      progressError = _b[0],
      setProgressError = _b[1];

  var _c = useState(),
      progressData = _c[0],
      setProgressData = _c[1];

  var _d = useState(),
      detailId = _d[0],
      setDetailId = _d[1];

  var _e = useState(),
      progressChapterDetail = _e[0],
      setProgressChapterDetail = _e[1];

  var shouldGotoDetail = useState(!!bookLocation)[0];
  var loadProgress = useCallback(function () {
    getChapterProgress().then(function (progress) {
      setProgressData(progress);

      if (shouldGotoDetail && bookLocation) {
        var index = location ? progress.map(function (item) {
          return item.title;
        }).indexOf(bookLocation) : -1;

        if (index !== -1) {
          setDetailId(progress[index].detailId);
        }
      }
    }).catch(function (error) {
      setProgressError(error);
    });
  }, [bookLocation, getChapterProgress, shouldGotoDetail]);
  useEffect(function () {
    if (!progressData && initialized !== false) {
      loadProgress();
    }
  }, [initialized, loadProgress, progressData]);
  useEffect(function () {
    if (detailId && progressData && !progressChapterDetail && !progressError) {
      getChapterProgressDetail(detailId).then(function (detail) {
        var item = progressData.find(function (item) {
          return item.detailId === detailId;
        });

        if (!item) {
          throw new Error("progress data not found");
        }

        if (cfiToPage) {
          for (var _i = 0, _a = detail.activities; _i < _a.length; _i++) {
            var activity = _a[_i];
            activity.page = cfiToPage(activity.cfi);
          }
        }

        setProgressChapterDetail(__assign(__assign({}, item), detail));
      }).catch(function (error) {
        setProgressError(error);
      });
    }
  }, [cfiToPage, detailId, getChapterProgressDetail, progressChapterDetail, progressData, progressError]);

  if (progressError || hasError) {
    return /*#__PURE__*/React.createElement(Styled.Container, null, /*#__PURE__*/React.createElement(ProgressPanelHeader, {
      noCloseBtn: noCloseBtn,
      onClose: onClose
    }), /*#__PURE__*/React.createElement(InlineError, {
      vertCenter: false,
      error: {
        title: intl.formatMessage(messagesErrors.progressLoadErrorTitle),
        description: intl.formatMessage(messagesErrors.enrichmentLoadErrorDescription)
      }
    }));
  } else if (!progressData || detailId && !progressChapterDetail) {
    return /*#__PURE__*/React.createElement(Styled.Container, null, /*#__PURE__*/React.createElement(ProgressPanelHeader, {
      noCloseBtn: noCloseBtn,
      onClose: onClose
    }), /*#__PURE__*/React.createElement(CenteredLoader, {
      vertCenter: true
    }), ";");
  } else {
    return /*#__PURE__*/React.createElement(Styled.Container, null, /*#__PURE__*/React.createElement(ProgressPanel, {
      items: progressData,
      detail: progressChapterDetail,
      onClose: onClose,
      noCloseBtn: noCloseBtn,
      onBack: function () {
        setDetailId(undefined);
        setProgressChapterDetail(undefined);
      },
      onSelect: function (detailId) {
        setDetailId(detailId);
        setProgressChapterDetail(undefined);
      },
      onDetailSelect: function (id, cfi, index) {
        onSelectActivity(id, cfi, index);
      }
    }));
  }
};
var templateObject_1;

var ProgressComponent = function (_a) {
  var locale = _a.locale,
      evaluator = _a.evaluator,
      localeProvided = _a.localeProvided,
      props = __rest(_a, ["locale", "evaluator", "localeProvided"]);

  var evaluatorService = evaluator !== null && evaluator !== void 0 ? evaluator : AssessmentInstance;
  var content = /*#__PURE__*/React.createElement(DefaultTheme, null, /*#__PURE__*/React.createElement(ProgressContent, __assign({
    getChapterProgress: evaluatorService.practiceCompleted,
    getChapterProgressDetail: evaluatorService.questionStatus,
    onClose: function () {
      var _a;

      return (_a = props.onClose) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    onSelectActivity: function (activityId, cfi, index) {
      var _a;

      return (_a = props.onSelectActivity) === null || _a === void 0 ? void 0 : _a.call(props, activityId, cfi, index);
    }
  }, props)));
  return localeProvided ? content : /*#__PURE__*/React.createElement(LocaleProvider, {
    locale: locale
  }, content);
};

export { EnrichmentComponent as Component, InfoMessage, ProgressComponent, ServicesInstance as default };
